import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Heart, MessageCircle, Share2, MapPin, Sparkles, Flame, PlusCircle, 
  User, Compass, Award, Shield, Search, ArrowUpRight, X, Play, Pause, 
  Send, Compass as CompassIcon, Navigation, Check, LogOut, Lock, Mail, 
  Globe, Camera, Bookmark, Users, ChevronDown, ChevronUp, Filter, Info, Phone,
  Instagram, Facebook, Youtube, Maximize2, Shuffle
} from 'lucide-react';
import { CITIES_OF_INDIA, CITY_COVERS } from './constants/cities';
import { motion, useScroll, useTransform } from 'framer-motion';
import Lenis from 'lenis';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Contact/social links — swap these with the real Maujiyo handles when ready
const CONTACT = {
  whatsapp: "919999999999",
  instagram: "https://instagram.com/maujiyo",
  facebook: "https://facebook.com/maujiyo",
  youtube: "https://youtube.com/@maujiyo",
};

// Canonical DJ genre list (must match backend DJ_GENRES)
const DJ_GENRES = ["Bollywood", "Punjabi", "Commercial", "EDM", "House", "Deep House", "Tech House", "Techno", "Trance", "Psytrance", "Progressive House", "Drum & Bass", "Dubstep", "Trap", "Hip-Hop", "R&B", "Reggaeton", "Afro House", "Disco", "Retro", "Lounge"];

// Google Maps directions link (no API key needed)
const mapsUrl = (query) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

// Display names for account types — everyone who isn't a Club Owner or Party Host is a Party Animal
const ROLE_LABELS = { party_goer: "Party Animal", social: "Party Animal", club_owner: "Club Owner", party_host: "Party Host", artist: "Artist", admin: "Admin" };
const roleLabel = (role) => ROLE_LABELS[role] || role;

// Masked line-by-line reveal for the kinetic hero headline
const heroLineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({ y: "0%", transition: { delay: 0.2 + 0.14 * i, duration: 0.9, ease: [0.16, 1, 0.3, 1] } })
};

const apiFetch = (path, options = {}) => fetch(`${API_BASE}${path}`, {
  ...options,
  credentials: "include",
  headers: { ...(typeof options.body === "string" ? { "Content-Type": "application/json" } : {}), ...(options.headers || {}) },
});

export default function App() {
  // Navigation & State
  const [currentCity, setCurrentCity] = useState(() => {
    const c = new URLSearchParams(window.location.search).get('city');
    return c && CITIES_OF_INDIA.includes(c) ? c : "Mumbai";
  });
  const [citySelectorOpen, setCitySelectorOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("discovery"); // discovery, shorts, clubs, profile, chat
  const [user, setUser] = useState(null); // logged in user object
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login"); // login, register, role_select
  
  // Data States
  const [clubsData, setClubsData] = useState({ hottest: [], trending: [], featured: [], night_spots: [] });
  const [clubCategoryTab, setClubCategoryTab] = useState("hottest");
  const [clubSort, setClubSort] = useState("default"); // hottest, trending, featured, night_spots
  const [shortsList, setShortsList] = useState([]);
  const [shortsCity, setShortsCity] = useState(null); // null = follow global city
  const [shortsClub, setShortsClub] = useState(null);
  const [shortsTag, setShortsTag] = useState(null);
  const [trendingTags, setTrendingTags] = useState([]);
  const [activeShortIndex, setActiveShortIndex] = useState(0);
  const [eventsList, setEventsList] = useState([]);
  const [tonightOnly, setTonightOnly] = useState(false);
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [storyViewerId, setStoryViewerId] = useState(null);
  const [clubProfileId, setClubProfileId] = useState(null);
  const [clubSetupClub, setClubSetupClub] = useState(null);
  const clubSetupShown = useRef(false);
  const [unread, setUnread] = useState({ messages: 0, likes: 0 });
  const [myFollows, setMyFollows] = useState({ users: [], clubs: [] });
  const [myRsvps, setMyRsvps] = useState([]);
  const [myWaitlist, setMyWaitlist] = useState([]);
  const [followersOpen, setFollowersOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [commentsShortId, setCommentsShortId] = useState(null);
  const [commentsPostId, setCommentsPostId] = useState(null);
  const [guestsEventId, setGuestsEventId] = useState(null);
  const [profileUserId, setProfileUserId] = useState(null);
  const [partyLightbox, setPartyLightbox] = useState(null); // { photos, index, name }
  const partiesRef = useRef(null); // parties carousel scroller
  const [reelsStartId, setReelsStartId] = useState(null); // full-screen reels mode
  const [reelsShorts, setReelsShorts] = useState(null); // override list when opened from the main-page segment
  const [shortsTick, setShortsTick] = useState(0); // bump to refetch the main-page shorts segment
  // Palette shuffle: rotate every section's crazy-gradient palette
  const PALETTES = ["font-crazy", "font-crazy-aqua", "font-crazy-sunset", "font-crazy-candy"];
  const [paletteSeed, setPaletteSeed] = useState(0);
  const pal = (i) => PALETTES[(i + paletteSeed) % PALETTES.length];
  
  // Upload & Onboarding Modals
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [newPostOpen, setNewPostOpen] = useState(false);
  const [postsTick, setPostsTick] = useState(0);
  const [tableBookingTarget, setTableBookingTarget] = useState(null); // { club } or { event }, optional offerId
  const [clubTick, setClubTick] = useState(0); // bump to refetch open club profile + club list after bookings
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState("party_goer");

  // Auth Inputs
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [selectedRegRole, setSelectedRegRole] = useState("party_animal");
  // Phone OTP auth
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [devOtp, setDevOtp] = useState(null);
  const [loginViaOtp, setLoginViaOtp] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [legacyEmail, setLegacyEmail] = useState(false);
  const [regCity, setRegCity] = useState("Mumbai");
  const [newPassword, setNewPassword] = useState("");
  const [otpPurpose, setOtpPurpose] = useState(null);
  const [resendIn, setResendIn] = useState(0);

  // OTP resend countdown (30s)
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  // Fetch initial data
  useEffect(() => {
    fetchClubs(currentCity);
    fetchShorts(shortsCity || currentCity);
    fetchEvents(currentCity, tonightOnly);
    fetchTrending();
    apiFetch("/auth/me").then((res) => res.ok ? res.json() : null).then((profile) => profile && setUser(profile)).catch(() => {});
    // Auto geolocation detection demo
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          // Default demo geolocation mapping to Mumbai/Goa if coordinates match
          console.log("Location detected", pos.coords);
        },
        (err) => console.log("Geolocation permission skipped", err)
      );
    }
  }, [currentCity, shortsCity, tonightOnly]);

  // Emergent Google OAuth callback: process session_id from URL fragment once
  const oauthProcessed = useRef(false);
  const [oauthProcessing, setOauthProcessing] = useState(() => window.location.hash.includes('session_id='));
  useEffect(() => {
    if (!window.location.hash.includes('session_id=')) return;
    if (oauthProcessed.current) return;
    oauthProcessed.current = true;
    const sid = window.location.hash.split('session_id=')[1].split('&')[0];
    apiFetch('/auth/session', {
      method: 'POST',
      body: JSON.stringify({ session_id: sid })
    }).then(r => r.ok ? r.json() : Promise.reject(new Error('oauth failed')))
      .then(u => setUser(u))
      .catch(() => alert('Google sign-in failed, please try again'))
      .finally(() => {
        window.history.replaceState(null, '', window.location.pathname);
        setOauthProcessing(false);
      });
  }, []);

  // First sign-in club registration prompt: club owners with an incomplete club profile
  useEffect(() => {
    if (!user) { clubSetupShown.current = false; return; }
    if (user.role !== "club_owner" || clubSetupShown.current) return;
    clubSetupShown.current = true;
    apiFetch("/clubs/mine").then(r => r.ok ? r.json() : []).then(mine => {
      const pending = (Array.isArray(mine) ? mine : []).find(c => !c.profile_completed);
      if (pending) setClubSetupClub(pending);
    }).catch(() => {});
  }, [user]);

  // Browser/hardware Back button closes the full-screen club page
  const clubPushed = useRef(false);
  useEffect(() => {
    if (clubProfileId && !clubPushed.current) {
      window.history.pushState({ clubPage: true }, "");
      clubPushed.current = true;
    }
    if (!clubProfileId) clubPushed.current = false;
  }, [clubProfileId]);
  useEffect(() => {
    const onPop = () => { if (clubPushed.current) { clubPushed.current = false; setClubProfileId(null); } };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const closeClubProfile = () => {
    if (clubPushed.current) { clubPushed.current = false; window.history.back(); }
    setClubProfileId(null);
  };

  const fetchClubs = async (city) => {
    try {
      const res = await apiFetch(`/clubs?city=${encodeURIComponent(city)}`);
      const data = await res.json();
      // Group by category
      const grouped = {
        hottest: data.filter(c => c.category === 'hottest'),
        trending: data.filter(c => c.category === 'trending'),
        featured: data.filter(c => c.category === 'featured'),
        night_spots: data.filter(c => c.category === 'night_spot'),
      };
      setClubsData(grouped);
    } catch (e) {
      console.error("Failed to fetch clubs", e);
    }
  };

  const fetchShorts = async (city) => {
    try {
      const res = await apiFetch(`/shorts?city=${encodeURIComponent(city)}`);
      const data = await res.json();
      setShortsList(data);
    } catch (e) {
      console.error("Failed to fetch shorts", e);
    }
  };

  const fetchEvents = async (city, tonight = false) => {
    try {
      const res = await apiFetch(`/events?city=${encodeURIComponent(city)}${tonight ? '&tonight=true' : ''}`);
      const data = await res.json();
      setEventsList(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to fetch events", e);
    }
  };

  const fetchTrending = async () => {
    try {
      const res = await apiFetch("/hashtags/trending");
      const data = await res.json();
      setTrendingTags(Array.isArray(data) ? data : []);
    } catch (e) {}
  };

  // Poll unread message & like notification badges
  useEffect(() => {
    if (!user) { setUnread({ messages: 0, likes: 0 }); return; }
    const load = () => apiFetch("/notifications/unread").then(r => r.ok ? r.json() : null).then(d => d && setUnread(d)).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [user]);

  // Load my follows and RSVPs when signed in
  useEffect(() => {
    if (!user) { setMyFollows({ users: [], clubs: [] }); setMyRsvps([]); setMyWaitlist([]); return; }
    apiFetch("/follows/mine").then(r => r.ok ? r.json() : null).then(d => d && setMyFollows(d)).catch(() => {});
    apiFetch("/rsvps/mine").then(r => r.ok ? r.json() : null).then(d => d && setMyRsvps(d)).catch(() => {});
    apiFetch("/waitlist/mine").then(r => r.ok ? r.json() : null).then(d => d && setMyWaitlist(d)).catch(() => {});
  }, [user]);

  // Parties refresh in the background every 60s (fresh lineup without reloading)
  useEffect(() => {
    const t = setInterval(() => fetchEvents(currentCity, tonightOnly), 60000);
    return () => clearInterval(t);
  }, [currentCity, tonightOnly]);

  // Deep links from shared URLs: ?short=<id> or ?club=<id>; ticket return: ?ticket=success&session_id=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cid = params.get('club');
    const sid = params.get('short');
    const uid = params.get('user');
    if (uid) setProfileUserId(uid);
    const ticket = params.get('ticket');
    const sessionId = params.get('session_id');
    if (ticket === 'success' && sessionId) {
      const check = (attempt) => {
        apiFetch(`/payments/status/${sessionId}`).then(r => r.ok ? r.json() : null).then(d => {
          if (d && d.payment_status === 'paid') {
            alert("🎟️ Ticket confirmed! You're on the guest list.");
            window.history.replaceState(null, '', window.location.pathname);
            fetchEvents(currentCity, tonightOnly);
          } else if (attempt < 5) {
            setTimeout(() => check(attempt + 1), 2000);
          } else {
            alert("Payment is being processed — your ticket will appear shortly.");
            window.history.replaceState(null, '', window.location.pathname);
          }
        }).catch(() => {});
      };
      check(0);
    } else if (ticket === 'cancelled') {
      alert("Ticket purchase cancelled — no charge was made.");
      window.history.replaceState(null, '', window.location.pathname);
    }
    const gig = params.get('gig');
    if (gig === 'success' && sessionId) {
      const check = (attempt) => {
        apiFetch(`/payments/status/${sessionId}`).then(r => r.ok ? r.json() : null).then(d => {
          if (d && d.payment_status === 'paid') {
            alert("💰 Gig fee paid and held safely — release it after the set from your Gig Offers.");
            window.history.replaceState(null, '', window.location.pathname);
            setActiveTab("profile");
          } else if (attempt < 5) {
            setTimeout(() => check(attempt + 1), 2000);
          } else {
            alert("Payment is being processed — your booking will update shortly.");
            window.history.replaceState(null, '', window.location.pathname);
          }
        }).catch(() => {});
      };
      check(0);
    } else if (gig === 'cancelled') {
      alert("Gig payment cancelled — no charge was made.");
      window.history.replaceState(null, '', window.location.pathname);
    }
    if (cid) setClubProfileId(cid);
    if (sid) {
      setActiveTab("shorts");
      setTimeout(() => {
        document.querySelector(`[data-testid="short-item-${sid}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 1200);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await apiFetch("/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
        setAuthModalOpen(false);
      } else {
        const err = await res.json();
        alert(err.detail || "Login failed");
      }
    } catch (e) {
      alert("Network error during login");
    }
  };

  const resetAuthModal = () => {
    setLegacyEmail(false); setLoginViaOtp(false); setForgotMode(false);
    setOtpSent(false); setDevOtp(null); setOtp(""); setPassword(""); setNewPassword("");
    setOtpPurpose(null); setResendIn(0); setAuthMode('login');
  };

  const handleDeactivate = async () => {
    if (!window.confirm("Deactivate your account? Your profile and content stay hidden until you log in again — logging in reactivates it instantly.")) return;
    const res = await apiFetch("/users/me/deactivate", { method: "POST" });
    if (res.ok) {
      setUser(null);
      alert("Account deactivated. See you soon — log back in anytime to reactivate.");
    } else {
      const err = await res.json();
      alert(err.detail || "Could not deactivate");
    }
  };

  const sendOtp = async (purpose) => {
    const res = await apiFetch("/auth/otp/send", { method: "POST", body: JSON.stringify({ phone, purpose }) });
    const d = await res.json();
    if (!res.ok) { alert(d.detail || "Could not send OTP"); return false; }
    setDevOtp(d.dev_otp || null);
    setOtpPurpose(purpose);
    setResendIn(30);
    setOtpSent(true);
    setOtp("");
    return true;
  };

  const handleRegisterPhone = async (e) => {
    e.preventDefault();
    if (!otpSent) {
      if (!name.trim()) { alert("Enter your name"); return; }
      if (password.length < 6) { alert("Password must be at least 6 characters"); return; }
      await sendOtp("register");
      return;
    }
    try {
      const res = await apiFetch("/auth/register-phone", {
        method: "POST",
        body: JSON.stringify({ name, phone, password, role: selectedRegRole, city: regCity, otp })
      });
      if (res.ok) {
        setUser(await res.json());
        setAuthModalOpen(false);
        setOnboardingOpen(true);
      } else {
        const err = await res.json();
        alert(err.detail || "Registration failed");
      }
    } catch (err) {
      alert("Network error during registration");
    }
  };

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await apiFetch("/auth/login-phone", { method: "POST", body: JSON.stringify({ phone, password }) });
      if (res.ok) {
        setUser(await res.json());
        setAuthModalOpen(false);
      } else {
        const err = await res.json();
        alert(err.detail || "Login failed");
      }
    } catch (err) {
      alert("Network error during login");
    }
  };

  const handleOtpLogin = async (e) => {
    e.preventDefault();
    if (!otpSent) { await sendOtp("login"); return; }
    try {
      const res = await apiFetch("/auth/otp-login", { method: "POST", body: JSON.stringify({ phone, otp }) });
      if (res.ok) {
        setUser(await res.json());
        setAuthModalOpen(false);
      } else {
        const err = await res.json();
        alert(err.detail || "OTP login failed");
      }
    } catch (err) {
      alert("Network error during login");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!otpSent) { await sendOtp("reset"); return; }
    try {
      const res = await apiFetch("/auth/forgot-password", { method: "POST", body: JSON.stringify({ phone, otp, new_password: newPassword }) });
      if (res.ok) {
        alert("Password updated — sign in with your new password.");
        setForgotMode(false); setOtpSent(false); setOtp(""); setDevOtp(null); setLoginViaOtp(false);
      } else {
        const err = await res.json();
        alert(err.detail || "Reset failed");
      }
    } catch (err) {
      alert("Network error");
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLikeShort = async (shortId) => {
    try {
      const res = await apiFetch(`/shorts/${shortId}/like`, { method: "POST" });
      if (res.ok) {
        const updated = await res.json();
        setShortsList(shortsList.map(s => s.id === shortId ? updated : s));
      } else if (res.status === 401) {
        setAuthModalOpen(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleFollowUser = async (userId) => {
    try {
      const res = await apiFetch(`/users/${userId}/follow`, { method: "POST" });
      if (res.ok) {
        const updated = await res.json();
        setMyFollows(f => ({ ...f, users: updated.is_following ? [...f.users, userId] : f.users.filter(id => id !== userId) }));
      } else if (res.status === 401) {
        setAuthModalOpen(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteShort = async (shortId) => {
    const res = await apiFetch(`/shorts/${shortId}`, { method: "DELETE" });
    if (res.ok) setShortsList(shortsList.filter(s => s.id !== shortId));
  };

  const handleShare = (type, id) => {
    const url = `${window.location.origin}/?${type}=${id}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => alert("Link copied! Share it anywhere.")).catch(() => prompt("Copy this link:", url));
    } else {
      prompt("Copy this link:", url);
    }
  };

  const handleBookTicket = async (eventId) => {
    try {
      const res = await apiFetch("/payments/checkout", {
        method: "POST",
        body: JSON.stringify({ event_id: eventId, origin_url: window.location.origin })
      });
      if (res.ok) {
        const d = await res.json();
        window.location.href = d.checkout_url;
      } else if (res.status === 401) {
        setAuthModalOpen(true);
      } else {
        const err = await res.json();
        alert(err.detail || "Could not start checkout");
      }
    } catch (e) {
      alert("Network error starting checkout");
    }
  };

  const handleRemind = async (eventId) => {
    const res = await apiFetch(`/events/${eventId}/remind`, { method: "POST" });
    if (res.ok) {
      const d = await res.json();
      alert(d.sent > 0 ? `Reminder sent to ${d.sent} guest${d.sent === 1 ? '' : 's'}!` : "No guests to remind yet.");
    } else {
      const err = await res.json();
      alert(err.detail || "Failed to send reminders");
    }
  };

  const handleWaitlist = async (eventId) => {
    const res = await apiFetch(`/events/${eventId}/waitlist`, { method: "POST" });
    if (res.ok) {
      const d = await res.json();
      setMyWaitlist(w => d.waitlisted ? [...w, eventId] : w.filter(id => id !== eventId));
      alert(d.waitlisted ? `You're #${d.position} on the waitlist — we'll notify you if a spot opens!` : "Removed from the waitlist.");
    } else if (res.status === 401) {
      setAuthModalOpen(true);
    } else {
      const err = await res.json();
      alert(err.detail || "Failed");
    }
  };

  // Derived shorts filters
  const shortsClubOptions = [...new Set(shortsList.map(s => s.club_tag).filter(Boolean))];
  const shortsTagOptions = [...new Set(shortsList.flatMap(s => (s.caption.match(/#\w+/g) || [])))];
  const filteredShorts = shortsList.filter(s =>
    (!shortsClub || s.club_tag === shortsClub) &&
    (!shortsTag || (s.caption || "").includes(shortsTag))
  );

  // Track party views for host analytics (once per session per party)
  useEffect(() => {
    if (!eventsList.length) return;
    const seen = JSON.parse(sessionStorage.getItem("gn_viewed") || "[]");
    eventsList.forEach(ev => {
      if (!seen.includes(ev.id)) {
        apiFetch(`/events/${ev.id}/view`, { method: "POST" }).catch(() => {});
        seen.push(ev.id);
      }
    });
    sessionStorage.setItem("gn_viewed", JSON.stringify(seen));
  }, [eventsList]);

  // Lenis smooth momentum scrolling
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => { lenis.raf(time); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);

  // Hero cover parallax
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const coverY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  const handleRsvp = async (eventId) => {
    try {
      const res = await apiFetch(`/events/${eventId}/rsvp`, { method: "POST" });
      if (res.ok) {
        const updated = await res.json();
        setEventsList(eventsList.map(e => e.id === eventId ? { ...e, going_count: updated.going_count } : e));
        setMyRsvps(r => updated.going ? [...r, eventId] : r.filter(id => id !== eventId));
      } else if (res.status === 401) {
        setAuthModalOpen(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (oauthProcessing) {
    return (
      <div className="min-h-screen bg-[#090A0F] flex items-center justify-center text-slate-400 font-mono text-sm" data-testid="oauth-processing">
        Signing you in with Google…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090A0F] text-slate-100 font-sans selection:bg-rose-500 selection:text-white pb-24">
      <CursorGlow />
      <AmbientAudio />
      
      {/* Top Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#090A0F]/80 backdrop-blur-2xl border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <button 
            onClick={() => setActiveTab("discovery")} 
            className="flex items-center gap-2 text-2xl font-black tracking-tighter text-white"
            data-testid="logo-button"
          >
            MAUJIYO
            <span className="text-[10px] bg-rose-500/20 text-rose-400 px-2 py-0.5 rounded-full border border-rose-500/30 uppercase tracking-widest font-mono">SOCIAL</span>
          </button>

          {/* Palette Shuffle — randomize every section's colours */}
          <button
            onClick={() => setPaletteSeed(s => s + 1 + Math.floor(Math.random() * (PALETTES.length - 1)))}
            className="hidden md:flex items-center justify-center w-9 h-9 rounded-xl bg-slate-900/80 border border-purple-500/40 text-purple-300 hover:text-white btn-neon-purple transition"
            title="Shuffle the vibe — randomize all section colours"
            data-testid="palette-shuffle-btn"
          >
            <Shuffle size={15} />
          </button>

          {/* City Selector Button */}
          <button 
            onClick={() => setCitySelectorOpen(true)}
            className="hidden md:flex items-center gap-2 bg-slate-900/80 hover:bg-slate-800 border border-cyan-500/40 px-3.5 py-2 rounded-xl text-xs font-mono text-cyan-400 btn-neon-cyan transition"
            data-testid="city-selector-trigger"
          >
            <MapPin size={14} className="text-rose-500 animate-pulse" />
            <span>CITY: <strong className="font-neon-cyan tracking-wide">{currentCity}</strong></span>
            <ChevronDown size={14} />
          </button>

          {/* Search */}
          <SearchBox 
            onSelectClub={(id) => setClubProfileId(id)} 
            onSelectUser={(id) => setProfileUserId(id)} 
            onSelectEvent={(id) => { setActiveTab("discovery"); setTimeout(() => document.querySelector(`[data-testid="event-card-${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 600); }}
          />
        </div>

        {/* Center Nav tabs */}
        <div className="hidden lg:flex items-center gap-1 bg-slate-900/60 p-1.5 rounded-2xl border border-white/10 text-xs font-mono">
          <button 
            onClick={() => setActiveTab("discovery")}
            className={`px-4 py-2 rounded-xl transition ${activeTab === 'discovery' ? 'bg-rose-600 text-white shadow-lg shadow-rose-600/30 font-bold' : 'text-slate-400 hover:text-white'}`}
            data-testid="nav-tab-discovery"
          >
            Discovery
          </button>
          {user && (
            <button 
              onClick={() => setActiveTab("following")}
              className={`px-4 py-2 rounded-xl transition ${activeTab === 'following' ? 'bg-rose-600 text-white shadow-lg shadow-rose-600/30 font-bold' : 'text-slate-400 hover:text-white'}`}
              data-testid="nav-tab-following"
            >
              Following
            </button>
          )}
          {user && (
            <button
              onClick={() => setActiveTab("feed")}
              className={`px-4 py-2 rounded-xl transition ${activeTab === 'feed' ? 'bg-rose-600 text-white shadow-lg shadow-rose-600/30 font-bold' : 'text-slate-400 hover:text-white'}`}
              data-testid="nav-tab-feed"
            >
              Feed
            </button>
          )}
          <button 
            onClick={() => setActiveTab("shorts")}
            className={`px-4 py-2 rounded-xl transition ${activeTab === 'shorts' ? 'bg-rose-600 text-white shadow-lg shadow-rose-600/30 font-bold' : 'text-slate-400 hover:text-white'}`}
            data-testid="nav-tab-shorts"
          >
            ⚡ Shorts
          </button>
          <button 
            onClick={() => setActiveTab("clubs")}
            className={`px-4 py-2 rounded-xl transition ${activeTab === 'clubs' ? 'bg-rose-600 text-white shadow-lg shadow-rose-600/30 font-bold' : 'text-slate-400 hover:text-white'}`}
            data-testid="nav-tab-clubs"
          >
            Clubs ({currentCity})
          </button>
          <button 
            onClick={() => setActiveTab("chat")}
            className={`relative px-4 py-2 rounded-xl transition ${activeTab === 'chat' ? 'bg-rose-600 text-white shadow-lg shadow-rose-600/30 font-bold' : 'text-slate-400 hover:text-white'}`}
            data-testid="nav-tab-chat"
          >
            Live Chat
            {unread.messages > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-cyan-500 text-white text-[9px] font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center" data-testid="chat-unread-badge">
                {unread.messages}
              </span>
            )}
          </button>
          {user && (user.role === "club_owner" || user.role === "admin") && (
            <button 
              onClick={() => setActiveTab("dashboard")}
              className={`px-4 py-2 rounded-xl transition ${activeTab === 'dashboard' ? 'bg-rose-600 text-white shadow-lg shadow-rose-600/30 font-bold' : 'text-slate-400 hover:text-white'}`}
              data-testid="nav-tab-dashboard"
            >
              My Venues
            </button>
          )}
        </div>

        {/* User profile / Auth */}
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setCitySelectorOpen(true)}
            className="md:hidden flex items-center gap-1 bg-slate-900 border border-cyan-500/40 px-3 py-1.5 rounded-xl text-xs font-mono text-cyan-400 btn-neon-cyan"
            data-testid="mobile-city-trigger"
          >
            <MapPin size={12} />
            <span className="font-neon-cyan">{currentCity}</span>
          </button>

          {user ? (
            <div className="flex items-center gap-3">
              <button 
                onClick={() => { setActiveTab("profile"); apiFetch("/notifications/read", { method: "POST" }); setUnread({ ...unread, likes: 0 }); }}
                className="relative flex items-center gap-2 bg-slate-900 hover:bg-slate-800 border border-white/10 px-3 py-1.5 rounded-2xl transition"
                data-testid="user-profile-button"
              >
                {unread.likes > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white text-[9px] font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center" data-testid="likes-unread-badge">
                    {unread.likes}
                  </span>
                )}
                <img src={user.avatar} alt={user.name} className="w-7 h-7 rounded-full object-cover border border-rose-500" />
                <span className="text-xs font-semibold hidden sm:inline">{user.name}</span>
                <span className="text-[10px] bg-cyan-500/20 text-cyan-300 px-1.5 py-0.5 rounded uppercase font-mono">{roleLabel(user.role)}</span>
              </button>
              <button 
                onClick={async () => { await apiFetch("/auth/logout", { method: "POST" }); setUser(null); }}
                className="text-slate-400 hover:text-rose-500 p-2 transition"
                title="Logout"
                data-testid="logout-button"
              >
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <button 
              onClick={() => { resetAuthModal(); setAuthModalOpen(true); }}
              className="bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-xl btn-neon transition flex items-center gap-2"
              data-testid="auth-login-trigger"
            >
              <User size={15} />
              <span>Sign In / Join</span>
            </button>
          )}
        </div>
      </nav>

      {/* Main Content Area */}
      <div className="pt-24">
        {/* Mobile search */}
        <div className="lg:hidden px-4 mb-6">
          <SearchBox 
            mobile 
            onSelectClub={(id) => setClubProfileId(id)} 
            onSelectUser={(id) => setProfileUserId(id)} 
            onSelectEvent={(id) => { setActiveTab("discovery"); setTimeout(() => document.querySelector(`[data-testid="event-card-${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 600); }}
          />
        </div>

        {/* 1. DISCOVERY TAB (Main Nightlife Page with India-wide city focus) */}
        {activeTab === "discovery" && (
          <div className="max-w-7xl mx-auto px-6 py-8">
            
            {/* Hero Section */}
            <div ref={heroRef} className="relative rounded-3xl overflow-hidden bg-[#0B0C12] border border-white/10 p-8 md:p-14 mb-16 shadow-2xl">
              {CITY_COVERS[currentCity] && (
                <motion.div className="absolute inset-0" style={{ y: coverY, scale: 1.18 }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2 }}>
                  <img src={CITY_COVERS[currentCity]} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#090A0F]/95 via-[#090A0F]/85 to-[#090A0F]/60" />
                </motion.div>
              )}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(225,29,72,0.15),transparent_50%)] pointer-events-none" />
              <div className="relative z-10 max-w-2xl" key={currentCity}>
                <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest bg-rose-500/20 text-rose-400 px-3 py-1 rounded-full border border-rose-500/30 mb-6">
                  <Flame size={14} className="animate-bounce" /> India's Ultimate Nightlife Platform
                </span>
                <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-6 leading-none">
                  {["Where's the", "Party Tonight", `in ${currentCity}?`].map((line, i) => (
                    <span key={i} className="block overflow-hidden pb-1">
                      <motion.span className="block" custom={i} variants={heroLineVariants} initial="hidden" animate="show">
                        {i === 1 ? <span className="font-neon">{line}</span> : <span className={pal(0)}>{line}</span>}
                      </motion.span>
                    </span>
                  ))}
                </h1>
                <p className="text-slate-400 text-sm md:text-base mb-8 leading-relaxed">
                  Explore hottest clubs, trending lounges, connect with verified party hosts & club owners, and watch viral party shorts from across India.
                </p>
                <div className="flex flex-wrap gap-4">
                  <button 
                    onClick={() => setActiveTab("clubs")} 
                    className="bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs uppercase tracking-wider px-6 py-3.5 rounded-2xl btn-neon transition flex items-center gap-2"
                    data-testid="explore-clubs-btn"
                  >
                    <Compass size={16} /> Browse Clubs in {currentCity}
                  </button>
                  <button 
                    onClick={() => setActiveTab("shorts")} 
                    className="bg-slate-900 hover:bg-slate-800 border border-white/10 text-white font-bold text-xs uppercase tracking-wider px-6 py-3.5 rounded-2xl transition flex items-center gap-2"
                    data-testid="explore-shorts-btn"
                  >
                    <Play size={16} className="text-cyan-400" /> Watch Party Shorts
                  </button>
                  {user && ["party_host", "club_owner", "admin"].includes(user.role) && (
                    <button 
                      onClick={() => setAddEventOpen(true)} 
                      className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-xs uppercase tracking-wider px-6 py-3.5 rounded-2xl btn-neon-cyan transition flex items-center gap-2"
                      data-testid="host-event-btn"
                    >
                      <Sparkles size={16} /> Host an Event
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Editorial marquee */}
            <div className="marquee-wrap border-y border-white/10 py-5 mb-14 overflow-hidden" data-testid="editorial-marquee">
              <div className="marquee-track">
                {[0, 1].map(k => (
                  <span key={k} className="marquee-chunk">
                    FEEL THE BEAT · MAUJIYO · WHERE&apos;S THE PARTY TONIGHT · INDIA&apos;S ULTIMATE NIGHTLIFE PLATFORM · OWN THE NIGHT ·&nbsp;
                  </span>
                ))}
              </div>
            </div>

            {/* Trending Hashtags — jump into filtered Shorts */}
            {trendingTags.length > 0 && (
              <div className="mb-12" data-testid="trending-tags-strip">
                <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-3">Trending Now</p>
                <div className="flex flex-wrap gap-2">
                  {trendingTags.map(t => (
                    <button
                      key={t.tag}
                      onClick={() => { setActiveTab("shorts"); setShortsCity("All India"); setShortsClub(null); setShortsTag(t.tag); }}
                      className="text-xs font-mono px-3.5 py-2 rounded-full bg-slate-900 border border-white/10 text-cyan-300 hover:border-rose-500 hover:text-white transition"
                      data-testid={`trending-${t.tag.slice(1)}`}
                    >
                      {t.tag} <span className="text-slate-500">×{t.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Tonight's Parties (events) */}
            <div className="mb-14">
              <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <div>
                  <h2 className={`text-2xl md:text-3xl font-black tracking-tight ${pal(1)}`}>Parties in {currentCity}</h2>
                  <div className="mt-2 h-px w-32 bg-gradient-to-r from-rose-500 to-transparent" />
                </div>
                <div className="flex items-center gap-2">
                  {eventsList.length > 2 && (
                    <>
                      <button onClick={() => partiesRef.current?.scrollBy({ left: -340, behavior: 'smooth' })} className="w-9 h-9 rounded-full bg-slate-900 border border-white/10 text-slate-300 hover:border-rose-500 hover:text-white transition flex items-center justify-center text-lg" data-testid="parties-scroll-prev">‹</button>
                      <button onClick={() => partiesRef.current?.scrollBy({ left: 340, behavior: 'smooth' })} className="w-9 h-9 rounded-full bg-slate-900 border border-white/10 text-slate-300 hover:border-rose-500 hover:text-white transition flex items-center justify-center text-lg" data-testid="parties-scroll-next">›</button>
                    </>
                  )}
                  <button
                    onClick={() => setTonightOnly(!tonightOnly)}
                    className={`text-[11px] font-mono font-bold px-4 py-2 rounded-full border transition ${tonightOnly ? 'bg-emerald-600 border-emerald-500 text-white shadow-lg shadow-emerald-600/30' : 'bg-slate-900 border-white/10 text-slate-300 hover:border-emerald-500/50'}`}
                    data-testid="tonight-filter-toggle"
                  >
                    ⚡ Happening Tonight {tonightOnly ? "ON" : "OFF"}
                  </button>
                </div>
              </div>
              {eventsList.length === 0 ? (
                <p className="text-xs font-mono text-slate-500" data-testid="events-empty">No parties listed yet — hosts, add tonight's plan!</p>
              ) : (
                <div ref={partiesRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-3" data-testid="parties-carousel">
                  {eventsList.map((ev) => (
                    <div key={ev.id} className={`relative overflow-hidden bg-slate-900/70 border border-white/10 rounded-2xl hover:border-rose-500/50 transition w-[320px] shrink-0 snap-start ${((ev.photos && ev.photos.length > 0) || ev.photo) ? '' : 'p-5'}`} data-testid={`event-card-${ev.id}`}>
                      <EventGallery photos={(ev.photos && ev.photos.length > 0) ? ev.photos : (ev.photo ? [ev.photo] : [])} eventId={ev.id} name={ev.name} onOpen={(photos, i) => setPartyLightbox({ photos, index: i, name: ev.name })} />
                      {((ev.photos && ev.photos.length > 0) || ev.photo) && <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/10 pointer-events-none" />}
                      <div className={((ev.photos && ev.photos.length > 0) || ev.photo) ? 'relative p-5 pt-32 cursor-zoom-in' : ''} onClick={((ev.photos && ev.photos.length > 0) || ev.photo) ? (e) => { if (e.target === e.currentTarget) setPartyLightbox({ photos: (ev.photos && ev.photos.length > 0) ? ev.photos : [ev.photo], index: 0, name: ev.name }); } : undefined}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-bold font-crazy">{ev.name}</h3>
                        <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                          {ev.starts_at && <Countdown startsAt={ev.starts_at} />}
                          {ev.recurring && <span className="text-[9px] font-mono bg-purple-600/20 text-purple-300 px-2 py-1 rounded-lg" title="Repeats every week">🔁 Weekly</span>}
                          <span className="text-[10px] font-mono bg-rose-600/20 text-rose-400 px-2 py-1 rounded-lg whitespace-nowrap">{ev.price}</span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 font-mono mb-1">📅 {ev.date}</p>
                      <p className="text-xs text-slate-400 flex items-center gap-1 mb-2">
                        <MapPin size={12} className="text-rose-500" />
                        <a href={mapsUrl(`${ev.venue}, ${ev.city}`)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:text-emerald-300 underline decoration-dotted decoration-white/20 hover:decoration-emerald-400 transition" data-testid={`event-directions-${ev.id}`} title="Open in Google Maps">{ev.venue}</a>
                      </p>
                      {(ev.lineup_artists || []).length > 0 ? (
                        <div className="flex flex-wrap items-center gap-1.5 mb-2">
                          <span className="text-xs text-purple-300 font-mono">🎧</span>
                          {ev.lineup_artists.map((la, i) => (
                            <button
                              key={i}
                              onClick={() => la.user_id && setProfileUserId(la.user_id)}
                              disabled={!la.user_id}
                              className={`text-[10px] font-mono px-2 py-1 rounded-lg border transition ${la.user_id ? 'bg-purple-600/20 border-purple-500/30 text-purple-200 hover:border-purple-400' : 'bg-slate-900 border-white/10 text-slate-400 cursor-default'}`}
                              data-testid={`lineup-artist-${ev.id}-${i}`}
                            >
                              {la.name}{la.genres.length > 0 && <span className="text-slate-500"> · {la.genres.slice(0, 2).join(", ")}</span>}
                            </button>
                          ))}
                        </div>
                      ) : ev.lineup && <p className="text-xs text-purple-300 font-mono mb-2">🎧 {ev.lineup}</p>}
                      {ev.description && <p className="text-xs text-slate-300">{ev.description}</p>}
                      {["single", "stag", "couple"].some(k => (ev.entry_prices || {})[k] != null) && (
                        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`event-entry-prices-${ev.id}`}>
                          {["single", "stag", "couple"].filter(k => (ev.entry_prices || {})[k] != null).map(k => (
                            <span key={k} className="text-[9px] font-mono font-bold px-2 py-1 rounded-lg bg-cyan-600/15 border border-cyan-500/30 text-cyan-300" data-testid={`event-entry-${k}-${ev.id}`}>
                              🎟 {k.charAt(0).toUpperCase() + k.slice(1)} ₹{ev.entry_prices[k]}
                            </span>
                          ))}
                        </div>
                      )}
                      {(ev.tables || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`event-tables-${ev.id}`}>
                          {ev.tables.map((t, i) => (
                            <span key={i} className="text-[9px] font-mono font-bold px-2 py-1 rounded-lg bg-amber-600/15 border border-amber-500/30 text-amber-300" data-testid={`event-table-${i}-${ev.id}`}>
                              🪑 {t.name} · ₹{t.price} · {t.seats} seats
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-3 gap-2">
                        <HostBadge hostId={ev.host_id} hostName={ev.host_name} onOpen={(id) => setProfileUserId(id)} />
                        <div className="flex items-center gap-2">
                          {user && (user.role === "admin" || ev.host_id === user.id) && (
                            <>
                              <button 
                                onClick={() => setGuestsEventId(ev.id)} 
                                className="text-[10px] font-mono font-bold px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-cyan-600 text-cyan-300 hover:text-white transition"
                                data-testid={`guests-btn-${ev.id}`}
                              >
                                Guest List
                              </button>
                              <button 
                                onClick={() => handleRemind(ev.id)} 
                                className="text-[10px] font-mono font-bold px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-amber-600 text-amber-300 hover:text-white transition"
                                data-testid={`remind-btn-${ev.id}`}
                              >
                                🔔 Remind
                              </button>
                            </>
                          )}
                          {ev.capacity ? (
                            <span className="text-[9px] font-mono text-slate-500" data-testid={`capacity-${ev.id}`}>{ev.going_count || 0}/{ev.capacity} spots</span>
                          ) : null}
                          {(((ev.entry_prices && Object.keys(ev.entry_prices).length > 0) || (ev.tables || []).length > 0 || (ev.offers || []).length > 0)) && (
                            <button
                              onClick={() => setTableBookingTarget({ event: ev })}
                              className="text-[10px] font-mono font-bold px-3 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white transition btn-neon-purple"
                              data-testid={`book-table-event-${ev.id}`}
                            >
                              🪑 Table / Entry
                            </button>
                          )}
                          {ev.capacity && (ev.going_count || 0) >= ev.capacity && !myRsvps.includes(ev.id) ? (
                            <button 
                              onClick={() => handleWaitlist(ev.id)} 
                              className={`text-[10px] font-mono font-bold px-3 py-1.5 rounded-xl transition ${myWaitlist.includes(ev.id) ? 'bg-amber-600/80 text-white' : 'bg-slate-800 hover:bg-amber-600 text-amber-300 hover:text-white'}`}
                              data-testid={`waitlist-btn-${ev.id}`}
                            >
                              {myWaitlist.includes(ev.id) ? "Waitlisted ✓" : "Full — Join Waitlist"}
                            </button>
                          ) : ev.ticket_price > 0 ? (
                            <button 
                              onClick={() => handleBookTicket(ev.id)} 
                              className="text-[10px] font-mono font-bold px-3 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white transition btn-neon"
                              data-testid={`book-ticket-btn-${ev.id}`}
                            >
                              🎟️ Book Ticket ₹{ev.ticket_price}
                            </button>
                          ) : (
                            <button 
                              onClick={() => handleRsvp(ev.id)} 
                              className={`text-[10px] font-mono font-bold px-3 py-1.5 rounded-xl transition ${myRsvps.includes(ev.id) ? 'bg-emerald-600 text-white' : 'bg-slate-800 hover:bg-rose-600 text-slate-300 hover:text-white'}`}
                              data-testid={`rsvp-btn-${ev.id}`}
                            >
                              {myRsvps.includes(ev.id) ? `Going ✓ (${ev.going_count || 0})` : `I'm Going (${ev.going_count || 0})`}
                            </button>
                          )}
                        </div>
                      </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Party Shorts segment (main page reels, All India) */}
            <MainShorts key={shortsTick} crazyCls={pal(13)} onOpen={(id, list) => { setReelsShorts(list); setReelsStartId(id); }} />

            {/* Party Stories (Instagram-style) */}
            {shortsList.length > 0 && (
              <div className="mb-14" data-testid="stories-bar">
                <div className="flex gap-5 overflow-x-auto pb-3">
                  {[...new Map(shortsList.map(s => [s.user_id, s])).values()].map((s) => (
                    <button key={s.user_id} onClick={() => setStoryViewerId(s.user_id)} className="flex flex-col items-center gap-2 shrink-0 group" data-testid={`story-${s.user_id}`}>
                      <div className="w-16 h-16 rounded-full p-[3px] bg-gradient-to-tr from-rose-500 via-pink-500 to-cyan-400 group-hover:scale-105 transition">
                        <img src={s.user_avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"} alt={s.user_name} className="w-full h-full rounded-full object-cover border-2 border-[#090A0F]" />
                      </div>
                      <span className="text-[10px] font-mono text-slate-300 max-w-[64px] truncate">{s.user_name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Hottest Clubs */}
            <ClubSection tag="Hottest Right Now" title={`Hottest Clubs in ${currentCity}`} clubs={clubsData.hottest} emptyText={`No hottest clubs listed in ${currentCity} yet — try another city.`} onSelect={setClubProfileId} index={1} crazyCls={pal(2)} />

            {/* Trending Clubs */}
            <ClubSection tag="Trending" title={`Trending Clubs in ${currentCity}`} clubs={clubsData.trending} emptyText={`No trending clubs listed in ${currentCity} yet — try another city.`} onSelect={setClubProfileId} index={2} crazyCls={pal(3)} />

            {/* Featured Clubs */}
            <ClubSection tag="Featured" title={`Featured Clubs in ${currentCity}`} clubs={clubsData.featured} emptyText={`No featured clubs listed in ${currentCity} yet — try another city.`} onSelect={setClubProfileId} index={3} crazyCls={pal(4)} />

            {/* Night Spots */}
            <ClubSection tag="Late Night" title={`Night Spots in ${currentCity}`} clubs={clubsData.night_spots} emptyText={`No night spots listed in ${currentCity} yet — try another city.`} onSelect={setClubProfileId} index={4} crazyCls={pal(5)} />

            {/* Latest Reviews wall (All India) */}
            <LatestReviews onSelectClub={setClubProfileId} crazyCls={pal(6)} />

            {/* Artist directory with genre filter */}
            <ArtistDirectory onSelectArtist={setProfileUserId} crazyCls={pal(7)} />

          </div>
        )}

        {/* 1.5 FOLLOWING FEED TAB */}
        {activeTab === "following" && user && (
          <FollowingFeed user={user} onShare={handleShare} crazyCls={pal(10)} />
        )}

        {activeTab === "feed" && (
          <FeedTab user={user} crazyCls={pal(14)} onNeedAuth={() => setAuthModalOpen(true)} onOpenUser={setProfileUserId} onOpenComments={setCommentsPostId} />
        )}

        {/* 2. SHORTS TAB (TikTok-style Vertical Feed with Upload & Likes) */}
        {activeTab === "shorts" && (
          <div className="max-w-sm mx-auto px-4 py-4">
            <div className="mb-6">
              <h2 className={`text-2xl font-black ${pal(8)}`}>Party Shorts</h2>
              <div className="mt-2 h-px w-28 bg-gradient-to-r from-rose-500 to-transparent" />
              <div className="flex items-center gap-2 flex-wrap mt-4">
                <select
                  value={shortsCity || currentCity}
                  onChange={(e) => setShortsCity(e.target.value === currentCity ? null : e.target.value)}
                  className="bg-slate-900 border border-white/10 text-xs font-mono px-3 py-2 rounded-xl text-cyan-300 focus:outline-none focus:border-rose-500"
                  data-testid="shorts-city-filter"
                >
                  <option value="All India">⚡ All India</option>
                  {CITIES_OF_INDIA.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {user ? (
                  <button 
                    onClick={() => setUploadModalOpen(true)}
                    className="bg-rose-600 hover:bg-rose-500 text-white px-4 py-2 rounded-xl text-xs font-mono font-bold flex items-center gap-2 btn-neon transition"
                    data-testid="upload-short-btn"
                  >
                    <PlusCircle size={16} /> Upload Short
                  </button>
                ) : (
                  <button 
                    onClick={() => setAuthModalOpen(true)}
                    className="bg-slate-900 border border-white/10 text-xs font-mono px-4 py-2 rounded-xl hover:bg-slate-800"
                    data-testid="login-to-upload-btn"
                  >
                    Sign in to Upload
                  </button>
                )}
              </div>
            </div>

            {/* Club & hashtag filters */}
            {(shortsClubOptions.length > 0 || shortsTagOptions.length > 0) && (
              <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="shorts-filters">
                <select
                  value={shortsClub || ""}
                  onChange={(e) => setShortsClub(e.target.value || null)}
                  className="bg-slate-900 border border-white/10 text-[11px] font-mono px-3 py-1.5 rounded-xl text-slate-300 focus:outline-none focus:border-rose-500"
                  data-testid="shorts-club-filter"
                >
                  <option value="">All Clubs</option>
                  {shortsClubOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {shortsTagOptions.slice(0, 8).map(tag => (
                  <button
                    key={tag}
                    onClick={() => setShortsTag(shortsTag === tag ? null : tag)}
                    className={`text-[11px] font-mono px-2.5 py-1.5 rounded-full border transition ${shortsTag === tag ? 'bg-rose-600 border-rose-500 text-white' : 'bg-slate-900 border-white/10 text-cyan-300 hover:border-cyan-500/50'}`}
                    data-testid={`shorts-tag-${tag.slice(1)}`}
                  >
                    {tag}
                  </button>
                ))}
                {(shortsClub || shortsTag) && (
                  <button onClick={() => { setShortsClub(null); setShortsTag(null); }} className="text-[10px] font-mono text-slate-500 hover:text-white underline" data-testid="shorts-clear-filters">
                    Clear
                  </button>
                )}
              </div>
            )}

            {/* Shorts Feed List */}
            <div className="space-y-6">
              {filteredShorts.map((short) => (
                <div key={short.id} className="relative bg-slate-900 border border-white/10 rounded-3xl overflow-hidden shadow-2xl" data-testid={`short-item-${short.id}`}>
                  {/* Video Container */}
                  <div className="relative aspect-[9/16] bg-black">
                    {short.media_type === "image" ? (
                      <img src={short.video_url} alt={short.caption} className="w-full h-full object-cover" />
                    ) : (
                      <video 
                        src={short.video_url} 
                        className="w-full h-full object-cover" 
                        controls 
                        loop 
                        autoPlay 
                        muted 
                        playsInline 
                      />
                    )}
                    
                    {/* Overlay Details */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 pointer-events-none p-6 flex flex-col justify-between">
                      {/* Top User Info */}
                      <div className="flex items-center justify-between pointer-events-auto">
                        <div className="flex items-center gap-3 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10">
                          <button onClick={() => setProfileUserId(short.user_id)} className="flex items-center gap-3 hover:opacity-80 transition" data-testid={`short-author-${short.id}`}>
                            <img src={short.user_avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"} alt="" className="w-7 h-7 rounded-full object-cover" />
                            <span className="text-xs font-bold text-white">{short.user_name}</span>
                            {short.user_verified && (
                              <span className="bg-cyan-500 rounded-full p-0.5" title="Verified"><Check size={9} className="text-white" /></span>
                            )}
                          </button>
                          {(!user || user.id !== short.user_id) && (
                            <button
                              onClick={() => handleFollowUser(short.user_id)}
                              className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full transition ${myFollows.users.includes(short.user_id) ? 'bg-slate-700 text-cyan-300' : 'bg-rose-600 hover:bg-rose-500 text-white'}`}
                              data-testid={`follow-user-btn-${short.id}`}
                            >
                              {myFollows.users.includes(short.user_id) ? "Following ✓" : "+ Follow"}
                            </button>
                          )}
                        </div>
                        {short.club_tag && (
                          <span className="text-[10px] font-mono bg-rose-600/80 backdrop-blur text-white px-2.5 py-1 rounded-full">
                            📍 {short.club_tag}
                          </span>
                        )}
                      </div>

                      {/* Bottom Caption & Actions */}
                      <div className="pointer-events-auto flex items-end justify-between">
                        <div>
                          <p className="text-xs font-mono text-cyan-400 mb-1">#{short.city} Nightlife</p>
                          <p className="text-sm font-medium text-slate-100 max-w-xs">{short.caption}</p>
                        </div>
                        
                        <div className="flex flex-col items-center gap-4">
                          {user && (user.role === "admin" || user.id === short.user_id) && (
                            <button 
                              onClick={() => handleDeleteShort(short.id)}
                              className="flex flex-col items-center gap-1 bg-black/50 hover:bg-red-600/80 backdrop-blur p-2.5 rounded-2xl border border-white/10 transition"
                              title="Remove short"
                              data-testid={`delete-short-btn-${short.id}`}
                            >
                              <X size={16} className="text-red-400" />
                            </button>
                          )}
                          <button 
                            onClick={() => handleLikeShort(short.id)}
                            className="flex flex-col items-center gap-1 bg-black/50 hover:bg-rose-600/80 backdrop-blur p-3 rounded-2xl border border-white/10 transition group"
                            data-testid={`like-short-btn-${short.id}`}
                          >
                            <Heart size={20} className="text-rose-500 group-hover:text-white fill-rose-500 group-hover:fill-white" />
                            <span className="text-[10px] font-mono font-bold">{short.likes}</span>
                          </button>

                          <button 
                            onClick={() => setCommentsShortId(short.id)}
                            className="flex flex-col items-center gap-1 bg-black/50 hover:bg-cyan-600/60 backdrop-blur p-3 rounded-2xl border border-white/10 transition"
                            data-testid={`comments-short-btn-${short.id}`}
                          >
                            <MessageCircle size={20} className="text-cyan-400" />
                            <span className="text-[10px] font-mono font-bold">{short.comments}</span>
                          </button>

                          <button 
                            onClick={() => handleShare("short", short.id)}
                            className="flex flex-col items-center gap-1 bg-black/50 hover:bg-emerald-600/60 backdrop-blur p-3 rounded-2xl border border-white/10 transition"
                            title="Copy link to this short"
                            data-testid={`share-short-btn-${short.id}`}
                          >
                            <Share2 size={20} className="text-emerald-400" />
                          </button>

                          <button
                            onClick={() => { setReelsShorts(null); setReelsStartId(short.id); }}
                            className="flex flex-col items-center gap-1 bg-black/50 hover:bg-purple-600/60 backdrop-blur p-3 rounded-2xl border border-white/10 transition"
                            title="Watch full-screen"
                            data-testid={`reels-open-${short.id}`}
                          >
                            <Maximize2 size={20} className="text-purple-300" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 3. CLUBS TAB (5 Hottest, 7 Trending, 15 Featured, 25 Night Spots inventories) */}
        {activeTab === "clubs" && (
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
              <div className="flex items-end gap-4 flex-wrap">
                <div>
                  <span className="text-xs font-mono text-cyan-400 uppercase tracking-widest">Nightlife Directory</span>
                  <h2 className={`text-3xl font-black ${pal(9)}`}>Clubs to Visit in {currentCity}</h2>
                </div>
                <button
                  onClick={() => setClubSort(clubSort === 'rating' ? 'default' : 'rating')}
                  className={`text-[11px] font-mono font-bold px-4 py-2 rounded-full border transition mb-1 ${clubSort === 'rating' ? 'bg-amber-600 border-amber-500 text-white shadow-lg shadow-amber-600/30' : 'bg-slate-900 border-white/10 text-slate-300 hover:border-amber-500/50'}`}
                  data-testid="top-rated-sort"
                >
                  ⭐ Top Rated {clubSort === 'rating' ? "ON" : ""}
                </button>
              </div>
              
              {/* Category Sub-tabs */}
              <div className="flex flex-wrap gap-2 bg-slate-900/80 p-1.5 rounded-2xl border border-white/10 text-xs font-mono">
                <button 
                  onClick={() => setClubCategoryTab("hottest")}
                  className={`px-4 py-2 rounded-xl transition ${clubCategoryTab === 'hottest' ? 'bg-rose-600 text-white font-bold' : 'text-slate-400 hover:text-white'}`}
                  data-testid="club-tab-hottest"
                >
                  🔥 Hottest Clubs (5)
                </button>
                <button 
                  onClick={() => setClubCategoryTab("trending")}
                  className={`px-4 py-2 rounded-xl transition ${clubCategoryTab === 'trending' ? 'bg-rose-600 text-white font-bold' : 'text-slate-400 hover:text-white'}`}
                  data-testid="club-tab-trending"
                >
                  ⚡ Trending (7)
                </button>
                <button 
                  onClick={() => setClubCategoryTab("featured")}
                  className={`px-4 py-2 rounded-xl transition ${clubCategoryTab === 'featured' ? 'bg-rose-600 text-white font-bold' : 'text-slate-400 hover:text-white'}`}
                  data-testid="club-tab-featured"
                >
                  ✨ Featured (15)
                </button>
                <button 
                  onClick={() => setClubCategoryTab("night_spots")}
                  className={`px-4 py-2 rounded-xl transition ${clubCategoryTab === 'night_spots' ? 'bg-rose-600 text-white font-bold' : 'text-slate-400 hover:text-white'}`}
                  data-testid="club-tab-nightspots"
                >
                  🍸 Night Spots (25)
                </button>
              </div>
            </div>

            {/* Clubs Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(clubSort === 'rating' ? [...(clubsData[clubCategoryTab] || [])].sort((a, b) => (b.rating || 0) - (a.rating || 0)) : (clubsData[clubCategoryTab] || [])).map((club) => (
                <div key={club.id} onClick={() => setClubProfileId(club.id)} className="bg-slate-900/60 border border-white/10 rounded-3xl overflow-hidden hover:border-cyan-500/50 transition flex flex-col justify-between group cursor-pointer" data-testid={`club-inventory-card-${club.id}`}>
                  <div>
                    <div className="relative h-56 overflow-hidden">
                      <img src={club.image} alt={club.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
                      <span className="absolute top-4 left-4 bg-black/60 backdrop-blur-md text-cyan-400 text-xs font-mono px-3 py-1 rounded-full border border-white/10">
                        {club.city}
                      </span>
                      <span className="absolute top-4 right-4 bg-rose-600 text-white text-xs font-mono font-bold px-2.5 py-1 rounded-xl">
                        ⭐ {club.rating}
                      </span>
                    </div>
                    <div className="p-6">
                      <p className="text-xs font-mono text-rose-400 uppercase tracking-widest mb-1">{club.vibe}</p>
                      <h3 className="text-xl font-bold mb-2 font-crazy">{club.name}</h3>
                      <p className="text-xs text-slate-400 flex items-center gap-1.5 mb-3">
                        <MapPin size={14} className="text-cyan-400" /> {club.address}
                      </p>
                      <p className="text-xs text-slate-300 leading-relaxed">{club.description}</p>
                    </div>
                  </div>
                  <div className="p-6 pt-0">
                    <button 
                      onClick={() => alert(`Reserving VIP table at ${club.name}, ${club.city}! Our nightlife concierge will contact you shortly.`)}
                      className="w-full py-3 bg-slate-800 hover:bg-rose-600 text-white text-xs font-mono font-bold uppercase tracking-wider rounded-2xl transition shadow-lg"
                      data-testid={`reserve-club-${club.id}`}
                    >
                      Reserve Table / VIP Access
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 3.5 OWNER DASHBOARD TAB (Club owners manage their venues) */}
        {activeTab === "dashboard" && user && (
          <OwnerDashboard user={user} onChanged={() => fetchClubs(currentCity)} />
        )}

        {/* 4. CHAT TAB (Global lounge + private DMs) */}
        {activeTab === "chat" && (
          <ChatCenter user={user} onNeedAuth={() => setAuthModalOpen(true)} onOpenUser={(id) => setProfileUserId(id)} crazyCls={pal(12)} />
        )}

        {/* 5. PROFILE TAB */}
        {activeTab === "profile" && user && (
          <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="bg-slate-900/80 border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />
              {(user.cover || (user.role === "artist" && (user.genres || []).length > 0)) && (
                <div className="relative h-36 -mx-8 -mt-8 mb-6 overflow-hidden bg-gradient-to-r from-purple-700/60 via-rose-600/50 to-cyan-600/60" data-testid="profile-cover">
                  {user.cover && <img src={user.cover} alt="" className="w-full h-full object-cover" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
                  {user.role === "artist" && (user.genres || []).length > 0 && (
                    <div className="absolute bottom-3 left-8 right-8 flex flex-wrap gap-1.5" data-testid="profile-banner-genres">
                      {user.genres.map(g => (
                        <span key={g} className="text-[9px] font-mono font-bold px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-purple-400/40 text-purple-200">{g}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row items-center gap-6 mb-8 relative z-10">
                <img src={user.avatar} alt={user.name} className="w-24 h-24 rounded-full object-cover border-2 border-rose-500 shadow-xl" />
                <div className="text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-3 mb-2">
                    <h2 className={`text-2xl font-black ${pal(11)}`}>{user.name}</h2>
                    <span className="text-xs font-mono uppercase bg-cyan-500/20 text-cyan-300 px-2.5 py-1 rounded-lg border border-cyan-500/30">{roleLabel(user.role)}</span>
                  </div>
                  <p className="text-xs text-slate-400 mb-4">{user.email} • 📍 {user.city}</p>
                  <p className="text-sm text-slate-300">{user.bio || "Nightlife enthusiast & party explorer."}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 border-t border-b border-white/10 py-6 mb-8 text-center font-mono">
                <div>
                  <p className="text-2xl font-black text-rose-500">{shortsList.filter(s => s.user_id === user.id).length}</p>
                  <p className="text-xs text-slate-400 uppercase">Shorts Here</p>
                </div>
                <button onClick={() => setFollowersOpen(true)} className="hover:bg-slate-800/60 rounded-2xl py-1 transition" data-testid="followers-count-btn">
                  <p className="text-2xl font-black text-cyan-400">{user.followers_count ?? 0}</p>
                  <p className="text-xs text-slate-400 uppercase">Followers</p>
                </button>
                <div>
                  <p className="text-2xl font-black text-purple-400">{user.following_count ?? 0}</p>
                  <p className="text-xs text-slate-400 uppercase">Following</p>
                </div>
              </div>

              {user.role === "party_goer" && (
                <div className="mb-8 bg-gradient-to-r from-purple-900/40 via-rose-900/30 to-cyan-900/40 border border-white/10 rounded-3xl p-5" data-testid="become-banner">
                  <p className="text-xs font-mono text-rose-400 uppercase tracking-widest mb-1">Level Up Your Nightlife</p>
                  <p className="text-sm text-slate-300 mb-4">Become a Party Host to throw your own parties, or a Club Owner to list and manage your venue.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={async () => {
                        const res = await apiFetch("/users/me", { method: "PATCH", body: JSON.stringify({ role: "party_host" }) });
                        if (res.ok) { setUser(await res.json()); setAddEventOpen(true); }
                      }}
                      className="py-3 bg-purple-600/80 hover:bg-purple-500 text-white text-xs font-mono font-bold rounded-2xl transition"
                      data-testid="become-host-btn"
                    >
                      🎪 Become a Party Host
                    </button>
                    <button
                      onClick={async () => {
                        const res = await apiFetch("/users/me", { method: "PATCH", body: JSON.stringify({ role: "club_owner" }) });
                        if (res.ok) { setUser(await res.json()); setActiveTab("dashboard"); }
                      }}
                      className="py-3 bg-cyan-600/80 hover:bg-cyan-500 text-white text-xs font-mono font-bold rounded-2xl transition"
                      data-testid="become-owner-btn"
                    >
                      🏢 Become a Club Owner
                    </button>
                  </div>
                </div>
              )}

              <MyTableBookings />
              {["club_owner", "party_host"].includes(user.role) && <TableBookingsManager />}

              {/* Account actions */}
              <div className="flex gap-3 mb-8">
                <button
                  onClick={async () => { await apiFetch("/auth/logout", { method: "POST" }); setUser(null); }}
                  className="flex-1 bg-slate-800 hover:bg-slate-700 border border-white/10 text-slate-200 text-[11px] font-mono font-bold py-3 rounded-2xl transition flex items-center justify-center gap-2"
                  data-testid="profile-logout-btn"
                >
                  <LogOut size={14} /> Log Out
                </button>
                <button
                  onClick={handleDeactivate}
                  className="flex-1 bg-red-600/10 hover:bg-red-600/30 border border-red-500/30 text-red-300 text-[11px] font-mono font-bold py-3 rounded-2xl transition"
                  data-testid="deactivate-account-btn"
                >
                  Deactivate Account
                </button>
              </div>

              {/* My Posts (Instagram-style feed) */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-mono text-rose-400 uppercase tracking-widest">📸 My Posts</p>
                  <button onClick={() => setNewPostOpen(true)} className="text-[10px] font-mono font-bold bg-rose-600 hover:bg-rose-500 px-3.5 py-2 rounded-xl btn-neon transition" data-testid="new-post-btn">+ New Post</button>
                </div>
                <PostsGrid userId={user.id} viewer={user} refreshKey={postsTick} />
              </div>

              <NotificationsFeed />
              <LinkPhone user={user} onSaved={setUser} />
              {user.role === "artist" && <ArtistGenres user={user} onSaved={setUser} />}
              {user.role === "artist" && <ArtistSpotify user={user} onSaved={setUser} />}
              {user.role === "artist" && <ArtistMixes user={user} />}
              {["artist", "club_owner", "admin"].includes(user.role) && <BookingRequests user={user} />}
              {["party_host", "club_owner", "admin"].includes(user.role) && <HostAnalytics />}
              <MyTickets />
              {["party_host", "club_owner", "admin"].includes(user.role) && <HostEarnings />}

              <button 
                onClick={() => setEditProfileOpen(true)}
                className="w-full mb-4 bg-slate-800 hover:bg-slate-700 border border-white/10 text-slate-200 font-bold text-xs uppercase tracking-wider px-4 py-3 rounded-2xl transition flex items-center justify-center gap-2"
                data-testid="edit-profile-btn"
              >
                ✏️ Edit Profile
              </button>

              <div className="flex gap-4">
                <button 
                  onClick={() => setUploadModalOpen(true)}
                  className="flex-1 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs uppercase tracking-wider py-3.5 rounded-2xl shadow-lg shadow-rose-600/30 transition flex items-center justify-center gap-2"
                  data-testid="profile-upload-short-btn"
                >
                  <Camera size={16} /> Upload New Short
                </button>
                <button 
                  onClick={() => setActiveTab("shorts")}
                  className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs uppercase tracking-wider py-3.5 rounded-2xl transition flex items-center justify-center gap-2"
                >
                  <Play size={16} /> View Shorts Feed
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Contact Footer */}
      <footer className="border-t border-white/10 mt-10" data-testid="contact-footer">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-xl font-black tracking-tight">MAUJIYO</div>
          <div className="flex items-center gap-3">
            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-mono font-bold px-4 py-2.5 rounded-2xl transition" data-testid="contact-whatsapp">
              <MessageCircle size={15} /> WhatsApp Us
            </a>
            <a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="p-2.5 bg-slate-900 hover:bg-slate-800 border border-white/10 rounded-2xl text-slate-300 hover:text-rose-400 transition" title="Instagram" data-testid="contact-instagram">
              <Instagram size={16} />
            </a>
            <a href={CONTACT.facebook} target="_blank" rel="noreferrer" className="p-2.5 bg-slate-900 hover:bg-slate-800 border border-white/10 rounded-2xl text-slate-300 hover:text-cyan-400 transition" title="Facebook" data-testid="contact-facebook">
              <Facebook size={16} />
            </a>
            <a href={CONTACT.youtube} target="_blank" rel="noreferrer" className="p-2.5 bg-slate-900 hover:bg-slate-800 border border-white/10 rounded-2xl text-slate-300 hover:text-rose-400 transition" title="YouTube" data-testid="contact-youtube">
              <Youtube size={16} />
            </a>
          </div>
          <p className="text-[10px] font-mono text-slate-600">© 2026 Maujiyo. All rights reserved.</p>
        </div>
      </footer>

      {/* --- MODALS --- */}

      {/* City Selector Modal */}
      {citySelectorOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="city-selector-modal">
          <div className="bg-slate-900 border border-white/10 rounded-3xl p-8 max-w-2xl w-full shadow-2xl relative">
            <button 
              onClick={() => setCitySelectorOpen(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-white"
              data-testid="close-city-modal"
            >
              <X size={20} />
            </button>
            <span className="text-xs font-mono text-cyan-400 uppercase tracking-widest">Pan-India Discovery</span>
            <h3 className="text-2xl font-black mb-2 font-crazy-aqua">Select Your City</h3>
            <p className="text-xs text-slate-400 mb-6">Choose from cities across India or let us detect your current location.</p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-96 overflow-y-auto pr-2">
              {CITIES_OF_INDIA.map((city) => (
                <button
                  key={city}
                  onClick={() => { setCurrentCity(city); setCitySelectorOpen(false); window.history.replaceState(null, '', `/?city=${encodeURIComponent(city)}`); }}
                  className={`p-3 rounded-xl border text-xs font-mono font-semibold transition text-left flex items-center justify-between ${currentCity === city ? 'bg-rose-600 border-rose-500 text-white' : 'bg-slate-800/80 border-white/10 text-slate-300 hover:border-cyan-500/50 hover:text-white'}`}
                  data-testid={`modal-city-${city.toLowerCase()}`}
                >
                  <span>{city}</span>
                  {currentCity === city && <Check size={14} />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal (Email/Password + Google Social Login) */}
      {authModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="auth-modal">
          <div className="bg-slate-900 border border-white/10 rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
            <button 
              onClick={() => { setAuthModalOpen(false); resetAuthModal(); }}
              className="absolute top-6 right-6 text-slate-400 hover:text-white"
              data-testid="close-auth-modal"
            >
              <X size={20} />
            </button>

            <div className="text-center mb-6">
              <span className="text-xs font-mono text-rose-500 uppercase tracking-widest">Join Maujiyo</span>
              <h3 className="text-2xl font-black font-crazy-candy">{authMode === 'login' ? 'Welcome Back' : 'Create Account'}</h3>
            </div>

            {/* Google Social Login Button */}
            <button 
              onClick={handleGoogleLogin}
              className="w-full mb-6 py-3 bg-white hover:bg-slate-100 text-slate-900 font-bold text-xs uppercase tracking-wider rounded-2xl shadow-lg flex items-center justify-center gap-3 transition"
              data-testid="google-login-btn"
            >
              <Globe size={16} className="text-rose-600" />
              <span>Continue with Google</span>
            </button>

            <div className="relative flex py-2 items-center mb-6">
              <div className="flex-grow border-t border-white/10"></div>
              <span className="flex-shrink mx-4 text-[10px] font-mono text-slate-500 uppercase">Or with your phone</span>
              <div className="flex-grow border-t border-white/10"></div>
            </div>

            {devOtp && (
              <div className="mb-4 bg-amber-500/10 border border-amber-500/40 rounded-xl px-4 py-2.5 text-center" data-testid="dev-otp-banner">
                <p className="text-[10px] font-mono text-amber-300 uppercase tracking-widest">Dev mode · SMS not configured yet</p>
                <p className="text-lg font-black text-amber-200 tracking-[0.35em]" data-testid="dev-otp-code">{devOtp}</p>
              </div>
            )}

            {forgotMode ? (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <p className="text-xs text-slate-400 font-mono text-center">Reset your password with an OTP sent to your number.</p>
                <div>
                  <label className="block text-xs font-mono text-slate-400 mb-1">Phone Number</label>
                  <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="98765 43210" disabled={otpSent} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500 disabled:opacity-60" data-testid="forgot-phone-input" />
                </div>
                {otpSent && (
                  <>
                    <div>
                      <label className="block text-xs font-mono text-slate-400 mb-1">OTP</label>
                      <input type="text" inputMode="numeric" required value={otp} onChange={e => setOtp(e.target.value)} placeholder="6-digit code" className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500 tracking-[0.3em] text-center" data-testid="forgot-otp-input" />
                      <p className="text-center text-[10px] font-mono text-slate-500 mt-2">
                        Didn't get it?{" "}
                        {resendIn > 0 ? <span data-testid="otp-resend-timer">Resend in {resendIn}s</span> : <button type="button" onClick={() => sendOtp(otpPurpose)} className="text-cyan-400 font-bold hover:underline" data-testid="otp-resend-btn">Resend OTP</button>}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-mono text-slate-400 mb-1">New Password</label>
                      <input type="password" required minLength={6} value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500" data-testid="forgot-new-password-input" />
                    </div>
                  </>
                )}
                <button type="submit" className="w-full py-3.5 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs uppercase tracking-wider rounded-2xl btn-neon transition" data-testid="forgot-submit-btn">
                  {otpSent ? "Reset Password" : "Send OTP"}
                </button>
                <p className="text-center text-xs text-slate-400 pt-2">
                  Remembered it? <button type="button" onClick={() => { setForgotMode(false); setOtpSent(false); setDevOtp(null); }} className="text-cyan-400 font-bold hover:underline" data-testid="forgot-back-to-login">Back to Sign In</button>
                </p>
              </form>
            ) : authMode === 'login' ? (
              loginViaOtp ? (
                <form onSubmit={handleOtpLogin} className="space-y-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-400 mb-1">Phone Number</label>
                    <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="98765 43210" disabled={otpSent} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500 disabled:opacity-60" data-testid="login-otp-phone-input" />
                  </div>
                  {otpSent && (
                    <div>
                      <label className="block text-xs font-mono text-slate-400 mb-1">OTP</label>
                      <input type="text" inputMode="numeric" required value={otp} onChange={e => setOtp(e.target.value)} placeholder="6-digit code" className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500 tracking-[0.3em] text-center" data-testid="login-otp-input" />
                      <p className="text-center text-[10px] font-mono text-slate-500 mt-2">
                        Didn't get it?{" "}
                        {resendIn > 0 ? <span data-testid="otp-resend-timer">Resend in {resendIn}s</span> : <button type="button" onClick={() => sendOtp(otpPurpose)} className="text-cyan-400 font-bold hover:underline" data-testid="otp-resend-btn">Resend OTP</button>}
                      </p>
                    </div>
                  )}
                  <button type="submit" className="w-full py-3.5 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs uppercase tracking-wider rounded-2xl btn-neon transition" data-testid="login-otp-submit-btn">
                    {otpSent ? "Verify & Sign In" : "Send OTP"}
                  </button>
                  <p className="text-center text-xs text-slate-400 pt-2">
                    <button type="button" onClick={() => { setLoginViaOtp(false); setOtpSent(false); setDevOtp(null); }} className="text-cyan-400 font-bold hover:underline" data-testid="use-password-instead">Use password instead</button>
                  </p>
                </form>
              ) : legacyEmail ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-400 mb-1">Email Address</label>
                    <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500" data-testid="login-email-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-slate-400 mb-1">Password</label>
                    <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500" data-testid="login-password-input" />
                  </div>
                  <button type="submit" className="w-full py-3.5 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs uppercase tracking-wider rounded-2xl btn-neon transition" data-testid="login-submit-btn">
                    Sign In
                  </button>
                  <p className="text-center text-xs text-slate-400 pt-2">
                    <button type="button" onClick={() => setLegacyEmail(false)} className="text-cyan-400 font-bold hover:underline" data-testid="use-phone-instead">Use phone instead</button>
                  </p>
                </form>
              ) : (
                <form onSubmit={handlePhoneLogin} className="space-y-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-400 mb-1">Phone Number</label>
                    <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="98765 43210" className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500" data-testid="login-phone-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-slate-400 mb-1">Password</label>
                    <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500" data-testid="login-password-input" />
                  </div>
                  <div className="flex items-center justify-between text-[11px] font-mono">
                    <button type="button" onClick={() => { setForgotMode(true); setOtpSent(false); setDevOtp(null); }} className="text-amber-400 hover:underline" data-testid="forgot-password-link">Forgot password?</button>
                    <button type="button" onClick={() => { setLoginViaOtp(true); setOtpSent(false); }} className="text-cyan-400 hover:underline" data-testid="login-via-otp-link">Login with OTP</button>
                  </div>
                  <button type="submit" className="w-full py-3.5 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs uppercase tracking-wider rounded-2xl btn-neon transition" data-testid="login-submit-btn">
                    Sign In
                  </button>
                  <p className="text-center text-xs text-slate-400 pt-2">
                    Don't have an account? <button type="button" onClick={() => setAuthMode('register')} className="text-cyan-400 font-bold hover:underline" data-testid="switch-to-register">Register</button>
                  </p>
                  <p className="text-center text-[10px] font-mono text-slate-500">
                    <button type="button" onClick={() => setLegacyEmail(true)} className="hover:text-slate-300 underline decoration-dotted" data-testid="use-email-instead">Use email instead</button>
                  </p>
                </form>
              )
            ) : (
              <form onSubmit={handleRegisterPhone} className="space-y-4">
                <div>
                  <label className="block text-xs font-mono text-slate-400 mb-1">Full Name</label>
                  <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500" data-testid="register-name-input" />
                </div>
                <div>
                  <label className="block text-xs font-mono text-slate-400 mb-1">Phone Number</label>
                  <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="98765 43210" disabled={otpSent} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500 disabled:opacity-60" data-testid="register-phone-input" />
                </div>
                <div>
                  <label className="block text-xs font-mono text-slate-400 mb-1">Your City</label>
                  <select value={regCity} onChange={e => setRegCity(e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500" data-testid="register-city-select">
                    {CITIES_OF_INDIA.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-mono text-slate-400 mb-2">I am a…</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[{ v: "party_animal", l: "Party Animal", e: "🎉" }, { v: "club_owner", l: "Club Owner", e: "🏢" }, { v: "party_host", l: "Party Host", e: "🎪" }].map(r => (
                      <button type="button" key={r.v} onClick={() => setSelectedRegRole(r.v)} className={`flex flex-col items-center gap-1.5 py-3 rounded-2xl border transition ${selectedRegRole === r.v ? 'border-rose-500 bg-rose-600/20 text-white shadow-lg shadow-rose-600/20' : 'border-white/10 bg-slate-800 text-slate-400 hover:border-rose-500/40'}`} data-testid={`register-role-${r.v}`}>
                        <span className="text-xl">{r.e}</span>
                        <span className="text-[10px] font-mono font-bold text-center leading-tight">{r.l}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-mono text-slate-400 mb-1">Set a Password</label>
                  <input type="password" required minLength={6} value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500" data-testid="register-password-input" />
                </div>
                {otpSent && (
                  <div>
                    <label className="block text-xs font-mono text-slate-400 mb-1">OTP sent to your phone</label>
                    <input type="text" inputMode="numeric" required value={otp} onChange={e => setOtp(e.target.value)} placeholder="6-digit code" className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500 tracking-[0.3em] text-center" data-testid="register-otp-input" />
                    <p className="text-center text-[10px] font-mono text-slate-500 mt-2">
                      Didn't get it?{" "}
                      {resendIn > 0 ? <span data-testid="otp-resend-timer">Resend in {resendIn}s</span> : <button type="button" onClick={() => sendOtp(otpPurpose)} className="text-cyan-400 font-bold hover:underline" data-testid="otp-resend-btn">Resend OTP</button>}
                    </p>
                  </div>
                )}
                <button type="submit" className="w-full py-3.5 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs uppercase tracking-wider rounded-2xl btn-neon transition" data-testid="register-submit-btn">
                  {otpSent ? "Verify & Create Account" : "Send OTP"}
                </button>
                <p className="text-center text-xs text-slate-400 pt-2">
                  Already have an account? <button type="button" onClick={() => setAuthMode('login')} className="text-cyan-400 font-bold hover:underline" data-testid="switch-to-login">Sign In</button>
                </p>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Host Event Modal */}
      {addEventOpen && user && (
        <AddEventModal 
          currentCity={currentCity} 
          onClose={() => setAddEventOpen(false)} 
          onSuccess={() => { setAddEventOpen(false); fetchEvents(currentCity, tonightOnly); }} 
        />
      )}

      {/* Story Viewer (full-screen, Instagram-style) */}
      {storyViewerId && (
        <StoryViewer shorts={shortsList} startUserId={storyViewerId} onClose={() => setStoryViewerId(null)} />
      )}
      {partyLightbox && (
        <PartyLightbox photos={partyLightbox.photos} index={partyLightbox.index} name={partyLightbox.name} onClose={() => setPartyLightbox(null)} />
      )}
      {reelsStartId && (
        <ReelsViewer
          shorts={reelsShorts || filteredShorts}
          startId={reelsStartId}
          onClose={() => { setReelsStartId(null); setReelsShorts(null); }}
          onLike={handleLikeShort}
          onOpenComments={(id) => { setReelsStartId(null); setReelsShorts(null); setCommentsShortId(id); }}
          onOpenUser={(id) => { setReelsStartId(null); setReelsShorts(null); setProfileUserId(id); }}
        />
      )}

      {/* Followers List Modal */}
      {followersOpen && user && (
        <FollowersModal userId={user.id} onClose={() => setFollowersOpen(false)} onOpenUser={(id) => { setFollowersOpen(false); setProfileUserId(id); }} />
      )}

      {/* Edit Profile Modal */}
      {editProfileOpen && user && (
        <EditProfileModal user={user} onClose={() => setEditProfileOpen(false)} onSaved={(u) => { const switchedToOwner = u.role === "club_owner" && user.role !== "club_owner"; const switchedToHost = u.role === "party_host" && user.role !== "party_host"; setUser(u); setEditProfileOpen(false); if (switchedToOwner) setActiveTab("dashboard"); if (switchedToHost) setAddEventOpen(true); }} />
      )}

      {/* User Public Profile Modal */}
      {profileUserId && (
        <UserProfileModal 
          userId={profileUserId} 
          viewer={user}
          isFollowing={myFollows.users.includes(profileUserId)}
          onClose={() => setProfileUserId(null)} 
          onNeedAuth={() => { setProfileUserId(null); setAuthModalOpen(true); }}
          onFollowChange={(now) => setMyFollows(f => ({ ...f, users: now ? [...f.users, profileUserId] : f.users.filter(id => id !== profileUserId) }))}
          onShare={handleShare}
        />
      )}

      {/* Event Guest List Modal (host/admin) */}
      {guestsEventId && (
        <GuestsModal eventId={guestsEventId} onClose={() => setGuestsEventId(null)} />
      )}

      {/* Short Comments Modal */}
      {commentsShortId && (
        <CommentsModal 
          shortId={commentsShortId} 
          user={user} 
          onClose={() => setCommentsShortId(null)} 
          onNeedAuth={() => { setCommentsShortId(null); setAuthModalOpen(true); }}
          onCountChange={(n) => setShortsList(shortsList.map(s => s.id === commentsShortId ? { ...s, comments: n } : s))}
          onOpenUser={(id) => { setCommentsShortId(null); setProfileUserId(id); }}
        />
      )}

      {/* Post Comments Modal */}
      {commentsPostId && (
        <CommentsModal
          kind="post"
          shortId={commentsPostId}
          user={user}
          onClose={() => setCommentsPostId(null)}
          onNeedAuth={() => { setCommentsPostId(null); setAuthModalOpen(true); }}
          onOpenUser={(id) => { setCommentsPostId(null); setProfileUserId(id); }}
        />
      )}

      {/* First sign-in club registration wizard for club owners */}
      {clubSetupClub && user && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4" data-testid="club-setup-wizard">
          <div className="bg-slate-900 border border-white/10 rounded-3xl max-w-2xl w-full shadow-2xl relative max-h-[88vh] overflow-y-auto p-7">
            <span className="text-xs font-mono text-amber-400 uppercase tracking-widest">Welcome, Club Owner</span>
            <h3 className="text-2xl font-black font-crazy-sunset mb-1">Set up {clubSetupClub.name}</h3>
            <p className="text-xs text-slate-400 mb-5">Add photos, pick your services and upload your menus — party animals will see all of this on your club page.</p>
            <ClubProfileEditor
              club={clubSetupClub}
              onSaved={() => { setClubSetupClub(null); setClubTick(t => t + 1); fetchClubs(currentCity); }}
              onCancel={() => setClubSetupClub(null)}
            />
          </div>
        </div>
      )}

      {/* Club Profile Modal */}
      {clubProfileId && (
        <ClubProfileModal 
          clubId={clubProfileId} 
          onClose={closeClubProfile} 
          onNeedAuth={() => { closeClubProfile(); setAuthModalOpen(true); }}
          isFollowing={myFollows.clubs.includes(clubProfileId)}
          onToggle={(now) => setMyFollows(f => ({ ...f, clubs: now ? [...f.clubs, clubProfileId] : f.clubs.filter(id => id !== clubProfileId) }))}
          onShare={handleShare}
          viewer={user}
          onPhotoOpen={(photos, i, name) => setPartyLightbox({ photos, index: i, name })}
          onBookTable={(club, offerId) => setTableBookingTarget({ club, offerId })}
          refreshKey={clubTick}
        />
      )}

      {/* Upload Short Modal */}
      {uploadModalOpen && user && (
        <UploadShortModal 
          user={user} 
          currentCity={currentCity} 
          onClose={() => setUploadModalOpen(false)} 
          onSuccess={() => { setUploadModalOpen(false); fetchShorts(currentCity); setShortsTick(t => t + 1); setActiveTab("shorts"); }} 
        />
      )}
      {newPostOpen && user && (
        <NewPostModal currentCity={currentCity} onClose={() => setNewPostOpen(false)} onSuccess={() => { setNewPostOpen(false); setPostsTick(t => t + 1); }} />
      )}
      {tableBookingTarget && (
        <TableBookingModal
          club={tableBookingTarget.club}
          event={tableBookingTarget.event}
          initialOfferId={tableBookingTarget.offerId}
          user={user}
          onClose={() => setTableBookingTarget(null)}
          onNeedAuth={() => { setTableBookingTarget(null); setAuthModalOpen(true); }}
          onBooked={() => { setClubTick(t => t + 1); fetchClubs(currentCity); fetchEvents(currentCity, tonightOnly); }}
        />
      )}


    </div>
  );
}

// Subtle neon glow that follows the cursor
function CursorGlow() {
  const ref = useRef(null);
  useEffect(() => {
    const move = (e) => {
      if (ref.current) ref.current.style.transform = `translate(${e.clientX - 220}px, ${e.clientY - 220}px)`;
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);
  return (
    <div
      ref={ref}
      className="fixed top-0 left-0 w-[440px] h-[440px] rounded-full pointer-events-none z-0 hidden md:block"
      style={{ background: "radial-gradient(circle, rgba(244,63,94,0.10) 0%, rgba(34,211,238,0.05) 40%, transparent 70%)", filter: "blur(24px)" }}
      data-testid="cursor-glow"
    />
  );
}

// Ambient club audio with track picker (bottom-right)
const AMBIENT_TRACKS = [
  { name: "Midnight Drive", url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3" },
  { name: "Neon Pulse", url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3" },
  { name: "After Hours", url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-9.mp3" }
];

function AmbientAudio() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [trackIdx, setTrackIdx] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);

  const playTrack = async (i) => {
    setTrackIdx(i);
    setPickerOpen(false);
    if (audioRef.current) {
      audioRef.current.src = AMBIENT_TRACKS[i].url;
      audioRef.current.volume = 0.3;
      try { await audioRef.current.play(); setPlaying(true); } catch (e) {}
    }
  };

  const toggle = async () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      try {
        audioRef.current.src = AMBIENT_TRACKS[trackIdx].url;
        audioRef.current.volume = 0.3;
        await audioRef.current.play();
        setPlaying(true);
      } catch (e) {}
    }
  };

  return (
    <>
      <audio ref={audioRef} loop />
      <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-2">
        {pickerOpen && (
          <div className="bg-slate-900/95 border border-white/10 rounded-2xl p-2 shadow-2xl backdrop-blur-md" data-testid="track-picker">
            {AMBIENT_TRACKS.map((t, i) => (
              <button
                key={t.name}
                onClick={() => playTrack(i)}
                className={`block w-full text-left text-[11px] font-mono px-4 py-2.5 rounded-xl transition ${i === trackIdx && playing ? 'bg-rose-600 text-white' : 'text-slate-300 hover:bg-slate-800'}`}
                data-testid={`track-${i}`}
              >
                {i === trackIdx && playing ? "❚❚ " : "▶ "}{t.name}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <button
            onClick={() => setPickerOpen(!pickerOpen)}
            className="text-[11px] font-mono font-bold px-3 py-2.5 rounded-full border bg-slate-900/90 border-white/15 text-slate-300 hover:border-cyan-500/50 hover:text-white backdrop-blur-md transition"
            data-testid="track-picker-toggle"
            title="Choose track"
          >
            ♫
          </button>
          <button
            onClick={toggle}
            className={`text-[11px] font-mono font-bold px-4 py-2.5 rounded-full border backdrop-blur-md transition shadow-lg ${playing ? 'bg-rose-600 border-rose-500 text-white shadow-rose-600/40' : 'bg-slate-900/90 border-white/15 text-slate-300 hover:border-rose-500/50 hover:text-white'}`}
            data-testid="ambient-audio-toggle"
            title="Ambient club audio"
          >
            {playing ? `❚❚ ${AMBIENT_TRACKS[trackIdx].name}` : "▶ Ambient"}
          </button>
        </div>
      </div>
    </>
  );
}

// Edit own profile (name, bio, city, photo)
function EditProfileModal({ user, onClose, onSaved }) {
  const SWITCHABLE = ["party_goer", "party_host", "club_owner"];
  const canSwitchRole = SWITCHABLE.includes(user.role);
  const [form, setForm] = useState({ name: user.name, bio: user.bio || "", city: user.city || "Mumbai", role: user.role });
  const [avatarFile, setAvatarFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name, bio: form.bio, city: form.city };
      if (canSwitchRole && form.role !== user.role) payload.role = form.role;
      if (avatarFile) {
        const fd = new FormData();
        fd.append("file", avatarFile);
        const up = await apiFetch("/upload", { method: "POST", body: fd });
        if (up.ok) payload.avatar = (await up.json()).url;
      }
      if (coverFile) {
        const fd = new FormData();
        fd.append("file", coverFile);
        const up = await apiFetch("/upload", { method: "POST", body: fd });
        if (up.ok) payload.cover = (await up.json()).url;
      }
      const res = await apiFetch("/users/me", { method: "PATCH", body: JSON.stringify(payload) });
      if (res.ok) {
        onSaved(await res.json());
      } else {
        alert("Failed to save profile");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="edit-profile-modal">
      <div className="bg-slate-900 border border-white/10 rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-white" data-testid="close-edit-profile">
          <X size={20} />
        </button>
        <span className="text-xs font-mono text-rose-500 uppercase tracking-widest">Your Identity</span>
        <h3 className="text-2xl font-black mb-4 font-crazy-aqua">Edit Profile</h3>
        <form onSubmit={submit} className="space-y-4">
          <label className="block border-2 border-dashed border-white/20 hover:border-rose-500 rounded-xl p-3 text-center cursor-pointer text-xs font-mono text-slate-400 transition" data-testid="avatar-dropzone">
            {avatarFile ? avatarFile.name : "Change profile photo (optional)"}
            <input type="file" accept="image/*" onChange={e => setAvatarFile(e.target.files[0] || null)} className="hidden" data-testid="avatar-input" />
          </label>
          <label className="block border-2 border-dashed border-white/20 hover:border-cyan-500 rounded-xl p-3 text-center cursor-pointer text-xs font-mono text-slate-400 transition" data-testid="cover-dropzone">
            {coverFile ? coverFile.name : "Add a cover banner (optional)"}
            <input type="file" accept="image/*" onChange={e => setCoverFile(e.target.files[0] || null)} className="hidden" data-testid="cover-input" />
          </label>
          <input required placeholder="Display name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="edit-name-input" />
          <textarea rows={2} placeholder="Bio (e.g. Mumbai's techno curator)" value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} className={inputCls} data-testid="edit-bio-input" />
          <select value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className={inputCls} data-testid="edit-city-select">
            {CITIES_OF_INDIA.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {canSwitchRole && (
            <div>
              <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2">Profile Type — switch anytime</p>
              <div className="grid grid-cols-3 gap-2">
                {[{ v: "party_goer", l: "Party Animal", e: "🎉", d: "Discover & book" }, { v: "party_host", l: "Party Host", e: "🎪", d: "Host parties" }, { v: "club_owner", l: "Club Owner", e: "🏢", d: "Manage venues" }].map(r => (
                  <button type="button" key={r.v} onClick={() => {
                    if (r.v === "party_goer" && user.role !== "party_goer" && !window.confirm("Switch back to Party Animal? Your venues/parties stay listed, but you lose host & owner tools.")) return;
                    setForm({ ...form, role: r.v });
                  }} className={`flex flex-col items-center gap-1 py-3 px-1 rounded-2xl border transition ${form.role === r.v ? 'border-rose-500 bg-rose-600/20 text-white shadow-lg shadow-rose-600/20' : 'border-white/10 bg-slate-800 text-slate-400 hover:border-rose-500/40'}`} data-testid={`switch-role-${r.v}`}>
                    <span className="text-xl">{r.e}</span>
                    <span className="text-[10px] font-mono font-bold">{r.l}</span>
                    <span className="text-[8px] font-mono text-slate-500">{r.d}</span>
                  </button>
                ))}
              </div>
              {form.role !== user.role && (
                <p className="text-[10px] font-mono text-amber-400 mt-2" data-testid="role-switch-note">Saving switches you to {roleLabel(form.role)} — new tools unlock instantly.</p>
              )}
            </div>
          )}
          <button type="submit" disabled={saving} className="w-full py-3.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-lg shadow-rose-600/30 transition" data-testid="save-profile-btn">
            {saving ? "Saving…" : "Save Profile"}
          </button>
        </form>
      </div>
    </div>
  );
}

// Subcomponent for Uploading Shorts (real photo/video upload)
function UploadShortModal({ user, currentCity, onClose, onSuccess }) {
  const [caption, setCaption] = useState("");
  const [clubTag, setClubTag] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const pickFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { alert("Choose a photo or video first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await apiFetch("/upload", { method: "POST", body: fd });
      if (!up.ok) throw new Error("upload failed");
      const { url, content_type } = await up.json();
      const res = await apiFetch("/shorts", {
        method: "POST",
        body: JSON.stringify({
          user_id: user.id,
          user_name: user.name,
          user_avatar: user.avatar,
          caption,
          video_url: url,
          club_tag: clubTag,
          city: currentCity,
          media_type: content_type.startsWith("image/") ? "image" : "video"
        })
      });
      if (res.ok) {
        onSuccess();
      } else {
        alert("Failed to post short");
      }
    } catch (e) {
      alert("Upload failed, please try again");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="upload-short-modal">
      <div className="bg-slate-900 border border-white/10 rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-white" data-testid="close-upload-modal">
          <X size={20} />
        </button>
        <span className="text-xs font-mono text-rose-500 uppercase tracking-widest">Share the Vibe</span>
        <h3 className="text-2xl font-black mb-4 font-crazy-sunset">Upload Party Short</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-mono text-slate-400 mb-1">Photo or Video</label>
            <label className="block w-full border-2 border-dashed border-white/20 hover:border-rose-500 rounded-2xl p-4 text-center cursor-pointer transition" data-testid="upload-file-dropzone">
              {preview ? (
                file.type.startsWith("image/") ? (
                  <img src={preview} alt="preview" className="max-h-48 mx-auto rounded-xl object-cover" />
                ) : (
                  <video src={preview} className="max-h-48 mx-auto rounded-xl" muted />
                )
              ) : (
                <span className="text-xs text-slate-400 font-mono flex flex-col items-center gap-2 py-4">
                  <Camera size={24} className="text-rose-500" />
                  Tap to choose from your device (photo or video, max 50MB)
                </span>
              )}
              <input type="file" accept="image/*,video/*" onChange={pickFile} className="hidden" data-testid="upload-file-input" />
            </label>
          </div>
          <div>
            <label className="block text-xs font-mono text-slate-400 mb-1">Caption</label>
            <textarea 
              required 
              rows={3}
              placeholder="What's happening tonight? Add hashtags..." 
              value={caption} 
              onChange={e => setCaption(e.target.value)}
              className="w-full bg-slate-800 border border-white/10 rounded-xl p-3 text-sm text-white focus:outline-none focus:border-rose-500" 
              data-testid="upload-caption-input"
            />
          </div>
          <div>
            <label className="block text-xs font-mono text-slate-400 mb-1">Club Tag (optional)</label>
            <input 
              type="text" 
              value={clubTag} 
              onChange={e => setClubTag(e.target.value)}
              placeholder="e.g. Toy Room Mumbai"
              className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500" 
              data-testid="upload-club-tag-input"
            />
          </div>
          <button 
            type="submit" 
            disabled={uploading}
            className="w-full py-3.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-lg shadow-rose-600/30 transition"
            data-testid="submit-upload-btn"
          >
            {uploading ? "Uploading…" : "Post Short Now"}
          </button>
        </form>
      </div>
    </div>
  );
}

// Club owner dashboard: add and manage own venues
// Venue booking setup editor (club owners): entry prices, tables, offer slots
function BookingSetupEditor({ club, onSaved }) {
  const [ep, setEp] = useState({ single: club.entry_prices?.single || "", stag: club.entry_prices?.stag || "", couple: club.entry_prices?.couple || "" });
  const [tables, setTables] = useState(club.tables || []);
  const [offers, setOffers] = useState(club.offers || []);
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full bg-slate-800 border border-white/10 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-rose-500";

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/clubs/${club.id}/booking-config`, {
        method: "PATCH",
        body: JSON.stringify({
          entry_prices: { single: parseFloat(ep.single) || null, stag: parseFloat(ep.stag) || null, couple: parseFloat(ep.couple) || null },
          tables: tables.filter(t => t.name && t.price).map(t => ({ name: t.name, price: parseFloat(t.price), seats: parseInt(t.seats) || 4 })),
          offers: offers.filter(o => o.title).map(o => ({ id: o.id, title: o.title, from_time: o.from_time, slots_total: parseInt(o.slots_total) || 10, slots_left: o.slots_left, cover_charge: parseFloat(o.cover_charge) || 0 }))
        })
      });
      if (res.ok) {
        onSaved && onSaved(await res.json());
        alert("Booking setup saved — guests can now book tables & entries.");
      } else {
        const err = await res.json();
        alert(err.detail || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 bg-slate-800/50 border border-white/10 rounded-2xl p-4 space-y-4" data-testid={`booking-setup-${club.id}`}>
      <div>
        <p className="text-[10px] font-mono text-cyan-400 uppercase tracking-widest mb-2">Entry Prices (₹) — leave blank to not offer</p>
        <div className="grid grid-cols-3 gap-2">
          {[["single", "Single"], ["stag", "Stag"], ["couple", "Couple"]].map(([k, l]) => (
            <div key={k}>
              <label className="text-[9px] font-mono text-slate-500">{l}</label>
              <input type="number" min="0" placeholder="0" value={ep[k]} onChange={e => setEp({ ...ep, [k]: e.target.value })} className={inputCls} data-testid={`entry-price-${k}-${club.id}`} />
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[10px] font-mono text-cyan-400 uppercase tracking-widest mb-2">Tables</p>
        {tables.map((t, i) => (
          <div key={i} className="grid grid-cols-[1fr_90px_70px_28px] gap-2 mb-2">
            <input placeholder="Table name (VIP Booth)" value={t.name} onChange={e => setTables(tables.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className={inputCls} data-testid={`table-name-${i}-${club.id}`} />
            <input type="number" min="0" placeholder="₹ Price" value={t.price} onChange={e => setTables(tables.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} className={inputCls} data-testid={`table-price-${i}-${club.id}`} />
            <input type="number" min="1" placeholder="Seats" value={t.seats} onChange={e => setTables(tables.map((x, j) => j === i ? { ...x, seats: e.target.value } : x))} className={inputCls} />
            <button type="button" onClick={() => setTables(tables.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300"><X size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={() => setTables([...tables, { name: "", price: "", seats: 4 }])} className="text-[10px] font-mono text-cyan-400 hover:underline" data-testid={`add-table-${club.id}`}>+ Add a table</button>
      </div>
      <div>
        <p className="text-[10px] font-mono text-cyan-400 uppercase tracking-widest mb-2">Offer Slots (e.g. FLAT 20% OFF)</p>
        {offers.map((o, i) => (
          <div key={i} className="grid grid-cols-[1fr_80px_60px_70px_28px] gap-2 mb-2">
            <input placeholder="Title" value={o.title} onChange={e => setOffers(offers.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} className={inputCls} data-testid={`offer-title-${i}-${club.id}`} />
            <input placeholder="10:00 PM" value={o.from_time} onChange={e => setOffers(offers.map((x, j) => j === i ? { ...x, from_time: e.target.value } : x))} className={inputCls} />
            <input type="number" min="1" placeholder="Slots" value={o.slots_total} onChange={e => setOffers(offers.map((x, j) => j === i ? { ...x, slots_total: e.target.value } : x))} className={inputCls} data-testid={`offer-slots-${i}-${club.id}`} />
            <input type="number" min="0" placeholder="Cover ₹" value={o.cover_charge} onChange={e => setOffers(offers.map((x, j) => j === i ? { ...x, cover_charge: e.target.value } : x))} className={inputCls} />
            <button type="button" onClick={() => setOffers(offers.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300"><X size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={() => setOffers([...offers, { title: "", from_time: "", slots_total: 10, cover_charge: 0 }])} className="text-[10px] font-mono text-purple-400 hover:underline" data-testid={`add-offer-${club.id}`}>+ Add an offer slot</button>
      </div>
      <button onClick={save} disabled={saving} className="w-full py-2.5 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-[10px] font-mono font-bold rounded-xl transition" data-testid={`save-booking-setup-${club.id}`}>
        {saving ? "Saving…" : "Save Booking Setup"}
      </button>
    </div>
  );
}

// Incoming table/entry bookings (club owners + party hosts)
function TableBookingsManager() {
  const [bookings, setBookings] = useState(null);
  const load = useCallback(async () => {
    const res = await apiFetch("/table-bookings/manage");
    if (res.ok) setBookings(await res.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    const res = await apiFetch(`/table-bookings/${id}/status`, { method: "POST", body: JSON.stringify({ action }) });
    if (res.ok) load(); else { const err = await res.json(); alert(err.detail || "Failed"); }
  };

  if (!bookings || bookings.length === 0) return null;
  const badge = (s) => s === "confirmed" ? "bg-emerald-600/20 text-emerald-300" : s === "declined" ? "bg-red-600/20 text-red-300" : "bg-amber-600/20 text-amber-300";
  return (
    <div className="mb-8" data-testid="table-bookings-manager">
      <p className="text-xs font-mono text-amber-400 uppercase tracking-widest mb-3">🪑 Table & Entry Bookings</p>
      <div className="space-y-3">
        {bookings.map(b => (
          <div key={b.id} className="bg-slate-800/60 border border-white/5 rounded-2xl p-4" data-testid={`manage-booking-${b.id}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-sm font-bold">{b.guest_name} <span className="text-[10px] font-mono text-slate-400">@ {b.venue_name}</span></p>
              <span className={`text-[9px] font-mono font-bold px-2 py-1 rounded-lg uppercase ${badge(b.status)}`}>{b.status}</span>
            </div>
            <p className="text-xs text-slate-400 font-mono">{b.label} • 📅 {b.date} • ₹{b.amount.toLocaleString('en-IN')} at venue{b.offer ? ` • ${b.offer}` : ""}</p>
            {b.guest_phone && <p className="text-[10px] font-mono text-slate-500 mt-0.5">📱 {b.guest_phone}</p>}
            {b.note && <p className="text-xs text-slate-300 mt-1">"{b.note}"</p>}
            {b.status === "pending" && (
              <div className="flex gap-2 mt-3">
                <button onClick={() => act(b.id, "confirm")} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-mono font-bold py-2 rounded-xl transition" data-testid={`confirm-booking-${b.id}`}>Confirm</button>
                <button onClick={() => act(b.id, "decline")} className="flex-1 bg-slate-700 hover:bg-red-600 text-white text-[10px] font-mono font-bold py-2 rounded-xl transition" data-testid={`decline-booking-${b.id}`}>Decline</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Guest's own table/entry bookings (profile tab)
function MyTableBookings() {
  const [bookings, setBookings] = useState(null);
  useEffect(() => {
    apiFetch("/table-bookings/mine").then(r => r.ok ? r.json() : []).then(d => setBookings(Array.isArray(d) ? d : [])).catch(() => setBookings([]));
  }, []);
  if (!bookings || bookings.length === 0) return null;
  const badge = (s) => s === "confirmed" ? "bg-emerald-600/20 text-emerald-300" : s === "declined" ? "bg-red-600/20 text-red-300" : "bg-amber-600/20 text-amber-300";
  return (
    <div className="mb-8" data-testid="my-table-bookings">
      <p className="text-xs font-mono text-amber-400 uppercase tracking-widest mb-3">🪑 My Table Bookings</p>
      <div className="space-y-3">
        {bookings.map(b => (
          <div key={b.id} className="bg-slate-800/40 border border-white/5 rounded-2xl p-4 flex items-center justify-between gap-2" data-testid={`my-booking-${b.id}`}>
            <div>
              <p className="text-xs font-bold">{b.venue_name}</p>
              <p className="text-[10px] font-mono text-slate-400">{b.label} • {b.date} • ₹{b.amount.toLocaleString('en-IN')} at venue{b.offer ? ` • ${b.offer}` : ""}</p>
            </div>
            <span className={`text-[9px] font-mono font-bold px-2 py-1 rounded-lg uppercase shrink-0 ${badge(b.status)}`}>{b.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const CLUB_SERVICES = [
  "Drinks", "Food", "Valet Parking", "Home Delivery", "DJ", "Free Parking",
  "Vegetarian Friendly", "Romantic Dining", "Nightlife", "Table Reservation",
  "Smoking Area", "Live Sports Screening",
];

// Photo grid with remove buttons and an add tile (used by ClubProfileEditor)
function PhotoGridEditor({ items, setter, max, testid, onAdd }) {
  return (
    <div className="grid grid-cols-4 gap-2">
      {items.map((p, i) => (
        <div key={i} className="relative group">
          <img src={p} alt="" className="aspect-square rounded-xl object-cover w-full" data-testid={`${testid}-photo-${i}`} />
          <button type="button" onClick={() => setter(items.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-black/70 text-rose-400 rounded-full p-1 opacity-0 group-hover:opacity-100 transition" data-testid={`${testid}-remove-${i}`}><X size={12} /></button>
        </div>
      ))}
      {(!max || items.length < max) && (
        <label className="aspect-square rounded-xl border-2 border-dashed border-white/20 hover:border-cyan-500 flex items-center justify-center cursor-pointer text-slate-500 hover:text-cyan-400 transition text-2xl" data-testid={`${testid}-add`}>
          +
          <input type="file" accept="image/*" multiple onChange={(e) => { onAdd(e.target.files, items, setter, max); e.target.value = ""; }} className="hidden" data-testid={`${testid}-input`} />
        </label>
      )}
    </div>
  );
}

// Reusable club profile editor: photos, services, food menu (max 4) & bar menu (max 4)
function ClubProfileEditor({ club, onSaved, onCancel }) {
  const [photos, setPhotos] = useState(club.photos || []);
  const [services, setServices] = useState(club.services || []);
  const [foodMenu, setFoodMenu] = useState(club.food_menu_photos || []);
  const [barMenu, setBarMenu] = useState(club.bar_menu_photos || []);
  const [saving, setSaving] = useState(false);

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const up = await apiFetch("/upload", { method: "POST", body: fd });
    if (!up.ok) throw new Error("upload failed");
    return (await up.json()).url;
  };

  const addFiles = async (files, current, setter, max) => {
    const list = Array.from(files || []);
    if (max && current.length + list.length > max) {
      alert(`Maximum ${max} photos allowed here`);
      return;
    }
    setSaving(true);
    try {
      const urls = [];
      for (const f of list) urls.push(await uploadOne(f));
      setter([...current, ...urls]);
    } finally {
      setSaving(false);
    }
  };

  const toggleService = (s) => setServices(services.includes(s) ? services.filter(x => x !== s) : [...services, s]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/clubs/${club.id}/profile`, {
        method: "PUT",
        body: JSON.stringify({ photos, services, food_menu_photos: foodMenu, bar_menu_photos: barMenu })
      });
      if (res.ok) {
        onSaved && onSaved(await res.json());
      } else {
        const err = await res.json();
        alert(err.detail || "Failed to save club profile");
      }
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="space-y-5" data-testid={`club-profile-editor-${club.id}`}>
      <div>
        <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-2">Club Photos (unlimited)</p>
        <PhotoGridEditor items={photos} setter={setPhotos} testid="club-photos" onAdd={addFiles} />
      </div>

      <div>
        <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-2">Services You Offer</p>
        <div className="flex flex-wrap gap-2">
          {CLUB_SERVICES.map(s => (
            <button key={s} type="button" onClick={() => toggleService(s)} className={`px-3.5 py-2 rounded-xl text-xs font-mono font-bold border transition ${services.includes(s) ? 'bg-rose-600 border-rose-500 text-white shadow-lg shadow-rose-600/30' : 'bg-slate-800 border-white/10 text-slate-400 hover:border-rose-500/50 hover:text-slate-200'}`} data-testid={`service-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              {services.includes(s) ? "✓ " : ""}{s}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-mono text-amber-400 uppercase tracking-widest mb-2">Food Menu (max 4 photos)</p>
        <PhotoGridEditor items={foodMenu} setter={setFoodMenu} max={4} testid="food-menu" onAdd={addFiles} />
      </div>

      <div>
        <p className="text-xs font-mono text-purple-400 uppercase tracking-widest mb-2">Bar Menu (max 4 photos)</p>
        <PhotoGridEditor items={barMenu} setter={setBarMenu} max={4} testid="bar-menu" onAdd={addFiles} />
      </div>

      <div className="flex gap-3">
        <button onClick={save} disabled={saving} className="flex-1 py-3 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold text-xs uppercase tracking-wider rounded-2xl transition" data-testid={`save-club-profile-${club.id}`}>
          {saving ? "Saving…" : "Save Club Profile"}
        </button>
        {onCancel && (
          <button onClick={onCancel} className="px-5 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs uppercase tracking-wider rounded-2xl transition" data-testid="cancel-club-profile">Skip for now</button>
        )}
      </div>
    </div>
  );
}

function OwnerDashboard({ user, onChanged }) {
  const emptyForm = { name: "", category: "featured", city: user.city || "Mumbai", address: "", rating: 4.5, image: "", vibe: "", description: "" };
  const [mine, setMine] = useState([]);
  const [setupOpen, setSetupOpen] = useState(null);
  const [profileOpen, setProfileOpen] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => apiFetch("/clubs/mine").then(r => r.ok ? r.json() : []).then(setMine).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let imageUrl = form.image;
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        const up = await apiFetch("/upload", { method: "POST", body: fd });
        if (up.ok) imageUrl = (await up.json()).url;
      }
      const photos = [];
      for (const f of galleryFiles) {
        const fd = new FormData();
        fd.append("file", f);
        const up = await apiFetch("/upload", { method: "POST", body: fd });
        if (up.ok) photos.push((await up.json()).url);
      }
      const res = await apiFetch("/clubs", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          rating: parseFloat(form.rating) || 4.5,
          image: imageUrl || "https://images.unsplash.com/photo-1566737236500-c8ac43014a8e?auto=format&fit=crop&w=800&q=85",
          photos
        })
      });
      if (res.ok) {
        setForm(emptyForm);
        setImageFile(null);
        setGalleryFiles([]);
        load();
        onChanged && onChanged();
      } else {
        const err = await res.json();
        alert(err.detail || "Failed to add venue");
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await apiFetch(`/clubs/${id}`, { method: "DELETE" });
    load();
    onChanged && onChanged();
  };

  const inputCls = "w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500";

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="owner-dashboard">
      <div className="mb-8">
        <span className="text-xs font-mono text-cyan-400 uppercase tracking-widest">Club Owner</span>
        <h2 className="text-3xl font-black font-crazy-aqua">My Venues</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Add venue form */}
        <form onSubmit={submit} className="bg-slate-900/80 border border-white/10 rounded-3xl p-6 space-y-4 h-fit" data-testid="add-venue-form">
          <h3 className="font-bold text-lg">Add a Venue</h3>
          <input required placeholder="Venue name" value={form.name} onChange={e => set("name", e.target.value)} className={inputCls} data-testid="venue-name-input" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.category} onChange={e => set("category", e.target.value)} className={inputCls} data-testid="venue-category-select">
              <option value="hottest">Hottest</option>
              <option value="trending">Trending</option>
              <option value="featured">Featured</option>
              <option value="night_spot">Night Spot</option>
            </select>
            <select value={form.city} onChange={e => set("city", e.target.value)} className={inputCls} data-testid="venue-city-select">
              {CITIES_OF_INDIA.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <input required placeholder="Address" value={form.address} onChange={e => set("address", e.target.value)} className={inputCls} data-testid="venue-address-input" />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" step="0.1" min="1" max="5" placeholder="Rating" value={form.rating} onChange={e => set("rating", e.target.value)} className={inputCls} data-testid="venue-rating-input" />
            <input placeholder="Vibe (e.g. Rooftop & EDM)" value={form.vibe} onChange={e => set("vibe", e.target.value)} className={inputCls} data-testid="venue-vibe-input" />
          </div>
          <textarea rows={2} placeholder="Short description" value={form.description} onChange={e => set("description", e.target.value)} className={inputCls} data-testid="venue-description-input" />
          <label className="block border-2 border-dashed border-white/20 hover:border-rose-500 rounded-xl p-3 text-center cursor-pointer text-xs font-mono text-slate-400 transition" data-testid="venue-image-dropzone">
            {imageFile ? imageFile.name : "Upload venue photo (optional)"}
            <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0] || null)} className="hidden" data-testid="venue-image-input" />
          </label>
          <label className="block border-2 border-dashed border-white/20 hover:border-cyan-500 rounded-xl p-3 text-center cursor-pointer text-xs font-mono text-slate-400 transition" data-testid="venue-gallery-dropzone">
            {galleryFiles.length > 0 ? `${galleryFiles.length} gallery photo${galleryFiles.length === 1 ? '' : 's'} selected` : "Add gallery photos (optional, multiple)"}
            <input type="file" accept="image/*" multiple onChange={e => setGalleryFiles(Array.from(e.target.files || []))} className="hidden" data-testid="venue-gallery-input" />
          </label>
          <button type="submit" disabled={saving} className="w-full py-3.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-lg shadow-rose-600/30 transition" data-testid="add-venue-submit">
            {saving ? "Saving…" : "Add Venue"}
          </button>
        </form>

        {/* My venues list */}
        <div className="space-y-4" data-testid="my-venues-list">
          {mine.length === 0 && (
            <div className="bg-slate-900/60 border border-white/10 rounded-3xl p-8 text-center text-slate-400 text-sm font-mono">
              No venues yet — add your first club to appear in city discovery.
            </div>
          )}
          {mine.map(club => (
            <div key={club.id} className="bg-slate-900/80 border border-white/10 rounded-2xl p-4" data-testid={`my-venue-${club.id}`}>
              <div className="flex items-center gap-4">
                <img src={club.image} alt={club.name} className="w-16 h-16 rounded-xl object-cover" />
                <div className="flex-1">
                  <p className="font-bold">{club.name}</p>
                  <p className="text-xs text-slate-400 font-mono">{club.city} • {club.category} • ⭐ {club.rating}</p>
                </div>
                <button onClick={() => setProfileOpen(profileOpen === club.id ? null : club.id)} className="text-xs font-mono text-amber-400 hover:text-amber-300 border border-amber-500/30 px-3 py-1.5 rounded-xl transition" data-testid={`club-profile-toggle-${club.id}`}>
                  📋 Club Profile
                </button>
                <button onClick={() => setSetupOpen(setupOpen === club.id ? null : club.id)} className="text-xs font-mono text-cyan-400 hover:text-cyan-300 border border-cyan-500/30 px-3 py-1.5 rounded-xl transition" data-testid={`booking-setup-toggle-${club.id}`}>
                  ⚙ Booking Setup
                </button>
                <button onClick={() => remove(club.id)} className="text-xs font-mono text-rose-400 hover:text-rose-300 border border-rose-500/30 px-3 py-1.5 rounded-xl transition" data-testid={`delete-venue-${club.id}`}>
                  Remove
                </button>
              </div>
              {setupOpen === club.id && (
                <BookingSetupEditor club={club} onSaved={(updated) => setMine(mine.map(c => c.id === updated.id ? updated : c))} />
              )}
              {profileOpen === club.id && (
                <div className="mt-4 border-t border-white/10 pt-4">
                  <ClubProfileEditor club={club} onSaved={(updated) => { setMine(mine.map(c => c.id === updated.id ? updated : c)); onChanged && onChanged(); }} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <TableBookingsManager />
      </div>
    </div>
  );
}

// Club section with heading + horizontal carousel of ClubCards (numbered manifesto chapters)
function ClubSection({ tag, title, clubs, emptyText, onSelect, index, crazyCls }) {
  const scrollerRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [touched, setTouched] = useState(false); // stops auto-drift permanently once the visitor interacts
  const scroll = (dir) => { setTouched(true); scrollerRef.current && scrollerRef.current.scrollBy({ left: dir * 340, behavior: "smooth" }); };
  // Slow auto-drift until the visitor hovers or touches the carousel
  useEffect(() => {
    if (!clubs || clubs.length < 3) return;
    const t = setInterval(() => {
      const el = scrollerRef.current;
      if (!el || hover || touched) return;
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 8) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        el.scrollBy({ left: 316, behavior: "smooth" });
      }
    }, 3500);
    return () => clearInterval(t);
  }, [clubs, hover, touched]);
  return (
    <motion.div
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="mb-16"
      data-testid={`club-section-${tag.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="mb-8 relative">
        <span className="absolute -top-8 -left-3 text-8xl font-black leading-none select-none text-transparent pointer-events-none" style={{ WebkitTextStroke: "1px rgba(255,255,255,0.14)" }}>{String(index).padStart(2, "0")}</span>
        <div className="relative flex items-end justify-between gap-4">
          <div>
            <h2 className={`text-2xl md:text-3xl font-black tracking-tight ${crazyCls || "font-crazy"}`}>{title}</h2>
            <div className="mt-3 h-px w-44 bg-gradient-to-r from-rose-500 via-cyan-400 to-transparent" />
          </div>
          {clubs && clubs.length > 2 && (
            <div className="flex gap-2 shrink-0">
              <button onClick={() => scroll(-1)} className="w-9 h-9 rounded-full bg-slate-900 border border-white/10 text-slate-300 hover:border-rose-500 hover:text-white transition flex items-center justify-center text-lg" data-testid={`club-scroll-prev-${index}`}>‹</button>
              <button onClick={() => scroll(1)} className="w-9 h-9 rounded-full bg-slate-900 border border-white/10 text-slate-300 hover:border-rose-500 hover:text-white transition flex items-center justify-center text-lg" data-testid={`club-scroll-next-${index}`}>›</button>
            </div>
          )}
        </div>
      </div>
      {(!clubs || clubs.length === 0) ? (
        <p className="text-xs font-mono text-slate-500">{emptyText}</p>
      ) : (
        <div ref={scrollerRef} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} onPointerDown={() => setTouched(true)} className="flex gap-6 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-3" data-testid={`club-carousel-${index}`}>
          {clubs.map((club, i) => (
            <motion.div
              key={club.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="w-[300px] shrink-0 snap-start"
            >
              <ClubCard club={club} onSelect={onSelect} />
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

function ClubCard({ club, onSelect }) {
  return (
    <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 300, damping: 22 }} onClick={() => onSelect && onSelect(club.id)} className="bg-slate-900/60 border border-white/10 rounded-3xl overflow-hidden hover:border-rose-500/50 transition-colors group cursor-pointer h-full" data-testid={`club-card-${club.id}`}>
      <div className="relative h-52 overflow-hidden">
        <img src={club.image} alt={club.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
        <span className="absolute top-4 left-4 bg-black/60 backdrop-blur-md text-cyan-400 text-xs font-mono px-3 py-1 rounded-full border border-white/10">{club.city}</span>
        <span className="absolute top-4 right-4 bg-rose-600 text-white text-xs font-mono font-bold px-2.5 py-1 rounded-xl">⭐ {club.rating}</span>
      </div>
      <div className="p-6">
        <p className="text-xs font-mono text-rose-400 uppercase tracking-widest mb-1">{club.vibe}</p>
        <h3 className="text-xl font-bold mb-2 font-crazy">{club.name}</h3>
        <p className="text-xs text-slate-400 flex items-center gap-1.5 mb-3">
          <MapPin size={14} className="text-cyan-400" /> {club.address}
        </p>
        <p className="text-xs text-slate-300 leading-relaxed">{club.description}</p>
      </div>
    </motion.div>
  );
}

// Chat center: global lounge + private DMs between any users
function ChatCenter({ user, onNeedAuth, onOpenUser, crazyCls }) {
  const [view, setView] = useState("global"); // "global" or partner user id
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [conversations, setConversations] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const activeConvo = conversations.find(c => c.partner_id === view);
  const title = view === "global" ? "Global Nightlife Lounge" : (activeConvo?.partner_name || "Private Chat");

  const load = async () => {
    const url = view === "global" ? "/chat/messages?receiver_id=global" : `/chat/dm/${view}`;
    const res = await apiFetch(url);
    if (res.ok) setMessages(await res.json());
  };

  const loadConversations = () => {
    if (!user) return;
    apiFetch("/chat/conversations").then(r => r.ok ? r.json() : []).then(d => setConversations(Array.isArray(d) ? d : [])).catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [view, user]);

  useEffect(() => { loadConversations(); }, [user, messages]);

  const openPicker = async () => {
    const res = await apiFetch("/users");
    if (res.ok) {
      const users = await res.json();
      setDirectory(users.filter(u => !user || u.id !== user.id));
      setPickerOpen(true);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    if (!user) { onNeedAuth(); return; }
    await apiFetch("/chat/messages", {
      method: "POST",
      body: JSON.stringify({ sender_id: user.id, sender_name: user.name, receiver_id: view, message: input })
    });
    setInput("");
    load();
  };

  const hideMsg = async (id) => {
    await apiFetch(`/chat/messages/${id}`, { method: "DELETE" });
    load();
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="chat-center">
      <div className="bg-slate-900/80 border border-white/10 rounded-3xl overflow-hidden shadow-2xl grid md:grid-cols-[260px_1fr] h-[75vh]">
        {/* Sidebar: conversations */}
        <div className="bg-slate-950/80 border-b md:border-b-0 md:border-r border-white/10 flex md:flex-col overflow-x-auto md:overflow-y-auto">
          <div className="p-4 border-b border-white/10 hidden md:flex items-center justify-between">
            <span className="text-xs font-mono text-cyan-400 uppercase tracking-widest">Messages</span>
            <button onClick={openPicker} className="text-[10px] font-mono bg-rose-600 hover:bg-rose-500 text-white px-2.5 py-1.5 rounded-lg transition" data-testid="new-dm-btn">
              + New
            </button>
          </div>
          <button
            onClick={() => setView("global")}
            className={`shrink-0 md:w-full text-left p-4 flex items-center gap-3 transition border-b border-white/5 ${view === "global" ? "bg-rose-600/20 border-l-2 border-l-rose-500" : "hover:bg-slate-900"}`}
            data-testid="chat-global-item"
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-rose-500 to-cyan-400 flex items-center justify-center text-xs font-black">GN</div>
            <div className="min-w-0">
              <p className="text-xs font-bold">Global Lounge</p>
              <p className="text-[10px] font-mono text-slate-500 truncate">Everyone's party room</p>
            </div>
          </button>
          {conversations.map((c) => (
            <button
              key={c.partner_id}
              onClick={() => setView(c.partner_id)}
              className={`shrink-0 md:w-full text-left p-4 flex items-center gap-3 transition border-b border-white/5 ${view === c.partner_id ? "bg-rose-600/20 border-l-2 border-l-rose-500" : "hover:bg-slate-900"}`}
              data-testid={`chat-conv-${c.partner_id}`}
            >
              <img src={c.partner_avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"} alt="" className="w-9 h-9 rounded-full object-cover" />
              <div className="min-w-0">
                <p className="text-xs font-bold truncate">{c.partner_name}</p>
                <p className="text-[10px] font-mono text-slate-500 truncate">{c.last_message}</p>
              </div>
            </button>
          ))}
          <button onClick={openPicker} className="md:hidden shrink-0 p-4 text-[10px] font-mono text-rose-400" data-testid="new-dm-btn-mobile">+ New DM</button>
        </div>

        {/* Message pane */}
        <div className="flex flex-col min-h-0">
          <div className="bg-slate-950/80 p-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <span className="text-[10px] font-mono text-cyan-400 uppercase tracking-widest">{view === "global" ? "Live Room" : "Private"}</span>
              <h3 className={`font-bold text-sm ${crazyCls || "font-crazy-candy"}`}>{title}</h3>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/20">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
            </span>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.length === 0 && (
              <p className="text-xs font-mono text-slate-500 text-center pt-10">No messages yet — say hello!</p>
            )}
            {messages.map((msg, index) => (
              <div key={msg.id || index} className={`flex flex-col ${user && msg.sender_id === user.id ? 'items-end' : 'items-start'}`} data-testid={`chat-msg-${index}`}>
                <button onClick={() => onOpenUser && onOpenUser(msg.sender_id)} className="text-[10px] font-mono text-slate-400 hover:text-cyan-300 mb-1 px-1 transition" data-testid={`chat-author-${index}`}>{msg.sender_name}</button>
                <div className={`group flex items-center gap-2 ${user && msg.sender_id === user.id ? 'flex-row-reverse' : ''}`}>
                  <div className={`p-3.5 rounded-2xl max-w-md text-sm ${user && msg.sender_id === user.id ? 'bg-rose-600 text-white rounded-tr-none' : 'bg-slate-800 text-slate-200 rounded-tl-none border border-white/10'}`}>
                    {msg.message}
                  </div>
                  {user && (user.role === "admin" || msg.sender_id === user.id) && msg.id && (
                    <button onClick={() => hideMsg(msg.id)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition" title="Remove message" data-testid={`chat-hide-${msg.id}`}>
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={send} className="p-4 bg-slate-950 border-t border-white/10 flex gap-3">
            <input
              type="text"
              placeholder={user ? (view === "global" ? "Message the lounge..." : `Message ${title}...`) : "Sign in to chat..."}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={!user}
              className="flex-1 bg-slate-900 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500 disabled:opacity-50 font-sans"
              data-testid="chat-input-field"
            />
            <button
              type="submit"
              disabled={!user}
              className="bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white px-6 py-3 rounded-2xl font-bold flex items-center gap-2 shadow-lg shadow-rose-600/30 transition"
              data-testid="chat-send-btn"
            >
              <Send size={16} /> Send
            </button>
          </form>
        </div>
      </div>

      {/* New DM user picker */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="dm-picker-modal">
          <div className="bg-slate-900 border border-white/10 rounded-3xl p-6 max-w-sm w-full shadow-2xl relative">
            <button onClick={() => setPickerOpen(false)} className="absolute top-5 right-5 text-slate-400 hover:text-white" data-testid="close-dm-picker">
              <X size={18} />
            </button>
            <h3 className="text-lg font-black mb-1">New Message</h3>
            <p className="text-xs font-mono text-slate-500 mb-4">Pick someone to chat with privately</p>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {directory.map((u) => (
                <button
                  key={u.id}
                  onClick={() => { setView(u.id); setPickerOpen(false); }}
                  className="w-full flex items-center gap-3 p-3 rounded-2xl bg-slate-800/60 hover:bg-slate-800 border border-white/5 transition text-left"
                  data-testid={`dm-user-${u.id}`}
                >
                  <img src={u.avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"} alt="" className="w-9 h-9 rounded-full object-cover" />
                  <div className="min-w-0">
                    <p className="text-xs font-bold truncate">{u.name}</p>
                    <p className="text-[10px] font-mono text-slate-500 uppercase">{u.role} • {u.city}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Host an event modal (party hosts, club owners, admin)
// Full-screen party photo lightbox (prev/next, click to zoom, Esc/arrows)
function PartyLightbox({ photos, index, name, onClose }) {
  const [idx, setIdx] = useState(index);
  const [zoomed, setZoomed] = useState(false);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const touch = useRef({ dist: 0, startScale: 1, panStart: null });
  const safeIdx = Math.min(idx, photos.length - 1);
  const resetZoom = () => { setScale(1); setPan({ x: 0, y: 0 }); setZoomed(false); };
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") { setIdx(i => (i + 1) % photos.length); resetZoom(); }
      if (e.key === "ArrowLeft") { setIdx(i => (i - 1 + photos.length) % photos.length); resetZoom(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [photos.length, onClose]);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      touch.current.dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      touch.current.startScale = scale;
      touch.current.panStart = null;
    } else if (e.touches.length === 1 && scale > 1) {
      touch.current.panStart = { x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && touch.current.dist > 0) {
      e.preventDefault();
      const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      setScale(Math.min(4, Math.max(1, touch.current.startScale * (d / touch.current.dist))));
    } else if (e.touches.length === 1 && touch.current.panStart && scale > 1) {
      e.preventDefault();
      setPan({ x: e.touches[0].clientX - touch.current.panStart.x, y: e.touches[0].clientY - touch.current.panStart.y });
    }
  };
  const onTouchEnd = () => {
    touch.current.dist = 0;
    touch.current.panStart = null;
    if (scale <= 1.05) resetZoom();
  };
  const onWheel = (e) => {
    e.preventDefault();
    const next = Math.min(4, Math.max(1, scale + (e.deltaY < 0 ? 0.25 : -0.25)));
    setScale(next);
    if (next <= 1) setPan({ x: 0, y: 0 });
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-md flex flex-col items-center justify-center p-4" onClick={onClose} data-testid="party-lightbox">
      <button onClick={onClose} className="absolute top-5 left-5 z-10 flex items-center gap-2 bg-slate-900/60 border border-white/10 text-slate-200 hover:text-white hover:border-rose-500 rounded-full pl-3 pr-4 py-2 text-xs font-mono font-bold transition" data-testid="lightbox-back">← Back</button>
      <button onClick={onClose} className="absolute top-5 right-5 text-slate-400 hover:text-white bg-slate-900/60 rounded-full p-2.5 transition" data-testid="close-lightbox"><X size={20} /></button>
      {photos.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); setIdx((safeIdx - 1 + photos.length) % photos.length); resetZoom(); }} className="absolute left-4 top-1/2 -translate-y-1/2 bg-slate-900/70 hover:bg-rose-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl transition z-10" data-testid="lightbox-prev">‹</button>
          <button onClick={(e) => { e.stopPropagation(); setIdx((safeIdx + 1) % photos.length); resetZoom(); }} className="absolute right-4 top-1/2 -translate-y-1/2 bg-slate-900/70 hover:bg-rose-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl transition z-10" data-testid="lightbox-next">›</button>
        </>
      )}
      <div
        className="max-w-4xl max-h-[82vh] overflow-hidden rounded-2xl touch-none"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onWheel={onWheel}
        data-testid="lightbox-zoom-stage"
      >
        <img
          src={photos[safeIdx]}
          alt={`${name} photo ${safeIdx + 1}`}
          onClick={() => { if (scale === 1) setZoomed(z => !z); }}
          style={{ transform: `scale(${zoomed && scale === 1 ? 1.8 : scale}) translate(${pan.x}px, ${pan.y}px)`, transition: touch.current.dist ? "none" : "transform 0.15s ease-out" }}
          className={`max-w-full max-h-[82vh] object-contain rounded-2xl select-none ${scale > 1 || zoomed ? "cursor-zoom-out" : "cursor-zoom-in"}`}
          draggable={false}
          data-testid="lightbox-image"
        />
      </div>
      <p className="mt-4 text-xs font-mono text-slate-400" data-testid="lightbox-caption">{name} · {safeIdx + 1}/{photos.length} · pinch, scroll or tap to zoom{scale > 1 ? ` (${Math.round(scale * 100)}%)` : ""}</p>
    </div>
  );
}

// Full-bleed swipeable photo backdrop for party cards (up to 3 photos)
function EventGallery({ photos, eventId, name, onOpen }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (!photos || photos.length < 2 || paused) return;
    const t = setInterval(() => setIdx(i => (i + 1) % photos.length), 5000);
    return () => clearInterval(t);
  }, [photos, paused]);
  if (!photos || photos.length === 0) return null;
  const safeIdx = Math.min(idx, photos.length - 1);
  return (
    <div className="absolute inset-0" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid={`event-gallery-${eventId}`}>
      <img src={photos[safeIdx]} alt={`${name} photo ${safeIdx + 1}`} onClick={() => onOpen && onOpen(photos, safeIdx)} className="w-full h-full object-cover cursor-zoom-in" data-testid={`event-photo-${eventId}`} />
      {photos.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); setIdx((safeIdx - 1 + photos.length) % photos.length); }} className="absolute left-2 top-14 bg-black/60 hover:bg-black/80 text-white rounded-full w-7 h-7 flex items-center justify-center transition z-10" data-testid={`event-photo-prev-${eventId}`}>‹</button>
          <button onClick={(e) => { e.stopPropagation(); setIdx((safeIdx + 1) % photos.length); }} className="absolute right-2 top-14 bg-black/60 hover:bg-black/80 text-white rounded-full w-7 h-7 flex items-center justify-center transition z-10" data-testid={`event-photo-next-${eventId}`}>›</button>
          <div className="absolute top-28 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {photos.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === safeIdx ? 'bg-white' : 'bg-white/40'}`} data-testid={`event-photo-dot-${eventId}-${i}`} />)}
          </div>
        </>
      )}
    </div>
  );
}

// Hosted-by badge with host track record (parties hosted + avg venue rating)
function HostBadge({ hostId, hostName, onOpen }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    if (!hostId) return;
    apiFetch(`/users/${hostId}/host-stats`).then(r => r.ok ? r.json() : null).then(setStats).catch(() => {});
  }, [hostId]);
  return (
    <button onClick={() => hostId && onOpen && onOpen(hostId)} className="text-left group shrink-0" data-testid={`host-badge-${hostId}`}>
      <p className="text-[10px] font-mono text-cyan-400 uppercase group-hover:text-cyan-300 transition">Hosted by {hostName}</p>
      {stats && (stats.parties_hosted > 0 || stats.avg_rating != null) && (
        <p className="text-[9px] font-mono text-slate-500 mt-0.5" data-testid={`host-stats-${hostId}`}>
          {stats.parties_hosted} {stats.parties_hosted === 1 ? "party" : "parties"} hosted{stats.avg_rating != null ? ` · ★ ${stats.avg_rating} (${stats.reviews_count} reviews)` : ""}
        </p>
      )}
    </button>
  );
}

function AddEventModal({ currentCity, onClose, onSuccess }) {
  const [form, setForm] = useState({ name: "", date: "", venue: "", price: "", description: "", ticket_price: "", capacity: "", recurring: false, lineup: "", starts_at: "", entry_single: "", entry_stag: "", entry_couple: "", tables_text: "" });
  const [photos, setPhotos] = useState([]); // up to 3 File objects
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const inputCls = "w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500";

  const pickPhotos = (files) => {
    const next = [...photos, ...files].slice(0, 3);
    if (photos.length + files.length > 3) alert("Maximum 3 photos per party.");
    setPhotos(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const photoUrls = [];
      for (const p of photos) {
        const fd = new FormData();
        fd.append("file", p);
        const up = await apiFetch("/upload", { method: "POST", body: fd });
        if (up.ok) {
          photoUrls.push((await up.json()).url);
        } else {
          alert("One photo failed to upload — continuing with the rest.");
        }
      }
      const res = await apiFetch("/events", {
        method: "POST",
        body: JSON.stringify({ ...form, city: currentCity, price: form.price || "Free entry", ticket_price: form.ticket_price ? parseFloat(form.ticket_price) : null, capacity: form.capacity ? parseInt(form.capacity) : null, starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null, photo: photoUrls[0] || null, photos: photoUrls, entry_prices: (form.entry_single || form.entry_stag || form.entry_couple) ? Object.fromEntries([["single", form.entry_single], ["stag", form.entry_stag], ["couple", form.entry_couple]].filter(([, v]) => v).map(([k, v]) => [k, parseFloat(v)])) : null, tables: form.tables_text ? form.tables_text.split("\n").map(l => l.split(":").map(x => x.trim())).filter(p => p[0] && p[1]).map(p => ({ name: p[0], price: parseFloat(p[1]), seats: parseInt(p[2]) || 4 })) : null })
      });
      if (res.ok) {
        onSuccess();
      } else {
        const err = await res.json();
        alert(err.detail || "Failed to add event");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="add-event-modal">
      <div className="bg-slate-900 border border-white/10 rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-white" data-testid="close-event-modal">
          <X size={20} />
        </button>
        <span className="text-xs font-mono text-rose-500 uppercase tracking-widest">Host the Night</span>
        <h3 className="text-2xl font-black mb-4 font-crazy-sunset">Add a Party in {currentCity}</h3>
        <form onSubmit={submit} className="space-y-4">
          <input required placeholder="Party / event name" value={form.name} onChange={e => set("name", e.target.value)} className={inputCls} data-testid="event-name-input" />
          <input required placeholder="Date & time (e.g. Tonight, 10 PM)" value={form.date} onChange={e => set("date", e.target.value)} className={inputCls} data-testid="event-date-input" />
          <input required placeholder="Venue" value={form.venue} onChange={e => set("venue", e.target.value)} className={inputCls} data-testid="event-venue-input" />
          <input placeholder="Price label (e.g. ₹999 onwards, or leave blank for free)" value={form.price} onChange={e => set("price", e.target.value)} className={inputCls} data-testid="event-price-input" />
          <div>
            <input type="number" min="0" step="1" placeholder="Ticket price in ₹ (paid entry — leave blank for free RSVP)" value={form.ticket_price} onChange={e => set("ticket_price", e.target.value)} className={inputCls} data-testid="event-ticket-price-input" />
            <p className="text-[10px] font-mono text-slate-500 mt-1">Set a number to sell tickets online; guests pay by card via Stripe.</p>
          </div>
          <input type="number" min="1" step="1" placeholder="Capacity (max guests — leave blank for unlimited)" value={form.capacity} onChange={e => set("capacity", e.target.value)} className={inputCls} data-testid="event-capacity-input" />
          <label className="flex items-center gap-2 text-xs font-mono text-slate-300" data-testid="event-recurring-label">
            <input type="checkbox" checked={form.recurring} onChange={e => set("recurring", e.target.checked)} className="accent-rose-600 w-4 h-4" data-testid="event-recurring-checkbox" />
            Repeats every week (weekly party)
          </label>
          <input placeholder="Lineup / DJs (e.g. DJ Arjun, Rhea)" value={form.lineup} onChange={e => set("lineup", e.target.value)} className={inputCls} data-testid="event-lineup-input" />
          <div className="bg-slate-800/50 border border-white/10 rounded-xl p-3 space-y-2">
            <p className="text-[10px] font-mono text-purple-400 uppercase tracking-widest">Table & Entry Booking (optional)</p>
            <div className="grid grid-cols-3 gap-2">
              <input type="number" min="0" placeholder="Single ₹" value={form.entry_single} onChange={e => set("entry_single", e.target.value)} className={inputCls} data-testid="event-entry-single" />
              <input type="number" min="0" placeholder="Stag ₹" value={form.entry_stag} onChange={e => set("entry_stag", e.target.value)} className={inputCls} data-testid="event-entry-stag" />
              <input type="number" min="0" placeholder="Couple ₹" value={form.entry_couple} onChange={e => set("entry_couple", e.target.value)} className={inputCls} data-testid="event-entry-couple" />
            </div>
            <textarea placeholder={"Tables, one per line: Name:Price:Seats\ne.g. VIP Booth:15000:6"} value={form.tables_text} onChange={e => set("tables_text", e.target.value)} rows={2} className={`${inputCls} text-xs font-mono`} data-testid="event-tables-input" />
          </div>
          <div>
            <label className="flex items-center justify-center gap-2 w-full bg-slate-800 border border-dashed border-white/20 hover:border-rose-500 rounded-xl px-4 py-3 text-xs font-mono text-slate-300 cursor-pointer transition" data-testid="event-photo-dropzone">
              <Camera size={14} className="text-rose-400" /> {photos.length > 0 ? `${photos.length}/3 photo${photos.length === 1 ? '' : 's'} selected` : "Add party posters / photos (up to 3)"}
              <input type="file" accept="image/*" multiple onChange={(e) => { pickPhotos(Array.from(e.target.files)); e.target.value = ""; }} className="hidden" data-testid="event-photo-input" />
            </label>
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative group">
                    <img src={URL.createObjectURL(p)} alt={`Party photo ${i + 1} preview`} className="h-20 w-full object-cover rounded-xl" data-testid={`event-photo-preview-${i}`} />
                    <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] transition" data-testid={`event-photo-remove-${i}`}><X size={11} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <input type="datetime-local" value={form.starts_at} onChange={e => set("starts_at", e.target.value)} className={inputCls} data-testid="event-starts-at-input" />
            <p className="text-[10px] font-mono text-slate-500 mt-1">Exact start time (optional) — shows a live countdown on the party card.</p>
          </div>
          <textarea rows={2} placeholder="Short description" value={form.description} onChange={e => set("description", e.target.value)} className={inputCls} data-testid="event-description-input" />
          <button type="submit" disabled={saving} className="w-full py-3.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-lg shadow-rose-600/30 transition" data-testid="submit-event-btn">
            {saving ? "Publishing…" : "Publish Party"}
          </button>
        </form>
      </div>
    </div>
  );
}

// Shorts segment on the main page — vertical 9:16 reel cards, tap to open full-screen reels
function MainShorts({ onOpen, crazyCls }) {
  const [shorts, setShorts] = useState(null);
  useEffect(() => {
    apiFetch("/shorts?city=All India").then(r => r.ok ? r.json() : []).then(d => setShorts(Array.isArray(d) ? d.slice(0, 12) : [])).catch(() => setShorts([]));
  }, []);
  if (!shorts || shorts.length === 0) return null;
  return (
    <div className="mb-14" data-testid="main-shorts">
      <div className="mb-6">
        <h2 className={`text-2xl md:text-3xl font-black tracking-tight ${crazyCls || "font-crazy"}`}>Party Shorts</h2>
        <span className="block mt-1 text-xs font-mono text-rose-400 tracking-widest uppercase">Swipe The Vibe · All India</span>
      </div>
      <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-3" data-testid="main-shorts-row">
        {shorts.map(s => (
          <button key={s.id} onClick={() => onOpen(s.id, shorts)} className="relative w-40 shrink-0 snap-start aspect-[9/16] rounded-2xl overflow-hidden border border-white/10 hover:border-rose-500/60 transition group text-left" data-testid={`main-short-${s.id}`}>
            {s.media_type === "image" ? (
              <img src={s.video_url} alt={s.caption} className="w-full h-full object-cover" />
            ) : (
              <video src={s.video_url} className="w-full h-full object-cover" muted loop playsInline onMouseEnter={(e) => e.target.play()} onMouseLeave={(e) => e.target.pause()} />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/20 pointer-events-none" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="bg-black/50 backdrop-blur rounded-full p-3 border border-white/20 group-hover:bg-rose-600/80 transition"><Play size={18} className="text-white fill-white" /></span>
            </div>
            <div className="absolute bottom-2.5 left-2.5 right-2.5">
              <p className="text-[10px] font-bold text-white truncate">{s.caption}</p>
              <p className="text-[9px] font-mono text-slate-300 truncate">{s.user_name} · {s.city}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// Full-screen reels mode: swipe/scroll vertically through shorts (TikTok-style)
function ReelsViewer({ shorts, startId, onClose, onLike, onOpenComments, onOpenUser }) {
  const startIdx = Math.max(0, shorts.findIndex(s => s.id === startId));
  const [idx, setIdx] = useState(startIdx);
  const clamp = (i) => Math.min(shorts.length - 1, Math.max(0, i));
  const go = (d) => setIdx(i => clamp(i + d));

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown") go(1);
      if (e.key === "ArrowUp") go(-1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [shorts.length, onClose]);

  useEffect(() => {
    const el = document.getElementById("reels-stage");
    if (!el) return;
    let startY = null;
    let lastWheel = 0;
    const ts = (e) => { startY = e.touches[0].clientY; };
    const te = (e) => {
      if (startY === null) return;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dy) > 60) go(dy < 0 ? 1 : -1);
      startY = null;
    };
    const wh = (e) => {
      const now = Date.now();
      if (Math.abs(e.deltaY) > 30 && now - lastWheel > 500) { lastWheel = now; go(e.deltaY > 0 ? 1 : -1); }
    };
    el.addEventListener("touchstart", ts, { passive: true });
    el.addEventListener("touchend", te);
    el.addEventListener("wheel", wh, { passive: true });
    return () => { el.removeEventListener("touchstart", ts); el.removeEventListener("touchend", te); el.removeEventListener("wheel", wh); };
  }, [shorts.length]);

  const short = shorts[clamp(idx)];
  if (!short) return null;
  return (
    <div className="fixed inset-0 z-[75] bg-black flex items-center justify-center" data-testid="reels-viewer">
      <button onClick={onClose} className="absolute top-5 left-5 z-20 flex items-center gap-2 bg-slate-900/60 border border-white/10 text-slate-200 hover:text-white hover:border-rose-500 rounded-full pl-3 pr-4 py-2 text-xs font-mono font-bold transition" data-testid="reels-back">← Back</button>
      <button onClick={onClose} className="absolute top-5 right-5 z-20 text-slate-400 hover:text-white bg-slate-900/60 rounded-full p-2.5 transition" data-testid="close-reels"><X size={20} /></button>
      <span className="absolute top-[4.5rem] left-6 z-20 text-xs font-mono text-slate-400" data-testid="reels-counter">{clamp(idx) + 1} / {shorts.length}</span>
      <div id="reels-stage" className="relative w-full sm:max-w-sm h-full sm:h-[92vh] sm:rounded-3xl overflow-hidden bg-slate-900 border border-white/10">
        <div key={short.id} className="absolute inset-0">
          {short.media_type === "image" ? (
            <img src={short.video_url} alt={short.caption} className="w-full h-full object-cover" />
          ) : (
            <video src={short.video_url} className="w-full h-full object-cover" autoPlay loop muted playsInline data-testid="reels-video" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/40 pointer-events-none" />
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
            <button onClick={() => onOpenUser(short.user_id)} className="flex items-center gap-2.5 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10 hover:border-cyan-500/50 transition" data-testid="reels-author">
              <img src={short.user_avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"} alt="" className="w-7 h-7 rounded-full object-cover" />
              <span className="text-xs font-bold text-white">{short.user_name}</span>
            </button>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-mono text-cyan-400 mb-1">#{short.city} Nightlife</p>
              <p className="text-sm font-medium text-slate-100">{short.caption}</p>
            </div>
            <div className="flex flex-col items-center gap-4 shrink-0">
              <button onClick={() => onLike(short.id)} className="flex flex-col items-center gap-1 bg-black/50 hover:bg-rose-600/80 backdrop-blur p-3 rounded-2xl border border-white/10 transition group" data-testid="reels-like-btn">
                <Heart size={20} className="text-rose-500 group-hover:text-white fill-rose-500 group-hover:fill-white" />
                <span className="text-[10px] font-mono font-bold text-white">{short.likes}</span>
              </button>
              <button onClick={() => onOpenComments(short.id)} className="flex flex-col items-center gap-1 bg-black/50 hover:bg-cyan-600/60 backdrop-blur p-3 rounded-2xl border border-white/10 transition" data-testid="reels-comments-btn">
                <MessageCircle size={20} className="text-cyan-400" />
                <span className="text-[10px] font-mono font-bold text-white">{short.comments}</span>
              </button>
            </div>
          </div>
        </div>
        <div className="absolute right-4 top-1/2 -translate-y-1/2 hidden sm:flex flex-col gap-2 z-10">
          <button onClick={() => go(-1)} disabled={clamp(idx) === 0} className="bg-black/60 hover:bg-rose-600 disabled:opacity-30 text-white rounded-full w-9 h-9 flex items-center justify-center transition" data-testid="reels-prev"><ChevronUp size={18} /></button>
          <button onClick={() => go(1)} disabled={clamp(idx) >= shorts.length - 1} className="bg-black/60 hover:bg-rose-600 disabled:opacity-30 text-white rounded-full w-9 h-9 flex items-center justify-center transition" data-testid="reels-next"><ChevronDown size={18} /></button>
        </div>
      </div>
    </div>
  );
}

// Full-screen story viewer (Instagram-style) for one creator's shorts
function StoryViewer({ shorts, startUserId, onClose }) {
  const userShorts = shorts.filter(s => s.user_id === startUserId);
  const [idx, setIdx] = useState(0);

  // Auto-advance every 6s like Instagram stories
  useEffect(() => {
    const t = setTimeout(() => {
      if (idx < userShorts.length - 1) setIdx(idx + 1);
      else onClose();
    }, 6000);
    return () => clearTimeout(t);
  }, [idx]);

  if (userShorts.length === 0) return null;
  const s = userShorts[Math.min(idx, userShorts.length - 1)];

  return (
    <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-4" data-testid="story-viewer">
      <button onClick={onClose} className="absolute top-5 left-5 z-10 flex items-center gap-2 bg-slate-900/60 border border-white/10 text-slate-200 hover:text-white hover:border-rose-500 rounded-full pl-3 pr-4 py-2 text-xs font-mono font-bold transition" data-testid="story-back">← Back</button>
      <button onClick={onClose} className="absolute top-5 right-5 text-slate-400 hover:text-white z-10" data-testid="close-story-viewer">
        <X size={24} />
      </button>
      <div className="relative w-full max-w-sm aspect-[9/16] max-h-[85vh] bg-slate-900 rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
        {s.media_type === "image" ? (
          <img src={s.video_url} alt={s.caption} className="w-full h-full object-cover" />
        ) : (
          <video src={s.video_url} className="w-full h-full object-cover" controls autoPlay loop muted playsInline />
        )}
        {/* Progress segments */}
        <div className="absolute top-3 left-3 right-3 flex gap-1.5">
          {userShorts.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full ${i <= idx ? 'bg-rose-500' : 'bg-white/20'}`} />
          ))}
        </div>
        <div className="absolute top-7 left-4 flex items-center gap-2.5 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10">
          <img src={s.user_avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"} alt="" className="w-7 h-7 rounded-full object-cover" />
          <span className="text-xs font-bold">{s.user_name}</span>
        </div>
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent p-5">
          <p className="text-xs font-mono text-cyan-400 mb-1">#{s.city} Nightlife {s.club_tag ? `• 📍 ${s.club_tag}` : ''}</p>
          <p className="text-sm font-medium">{s.caption}</p>
        </div>
        {userShorts.length > 1 && (
          <>
            <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 disabled:opacity-30 text-white p-2 rounded-full" data-testid="story-prev-btn">‹</button>
            <button onClick={() => setIdx(Math.min(userShorts.length - 1, idx + 1))} disabled={idx === userShorts.length - 1} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 disabled:opacity-30 text-white p-2 rounded-full" data-testid="story-next-btn">›</button>
          </>
        )}
      </div>
    </div>
  );
}

// Club profile: details, follow, upcoming parties, tagged shorts
function ClubProfileModal({ clubId, onClose, onNeedAuth, isFollowing, onToggle, onShare, viewer, onPhotoOpen, onBookTable, refreshKey = 0 }) {
  const [club, setClub] = useState(null);
  const [parties, setParties] = useState([]);
  const [clubShorts, setClubShorts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [reviewPhoto, setReviewPhoto] = useState(null);

  const loadReviews = () => apiFetch(`/clubs/${clubId}/reviews`).then(r => r.ok ? r.json() : []).then(d => setReviews(Array.isArray(d) ? d : [])).catch(() => {});

  useEffect(() => {
    apiFetch(`/clubs/${clubId}`).then(r => r.ok ? r.json() : null).then(c => {
      if (!c) return;
      setClub(c);
      apiFetch(`/events?city=${encodeURIComponent(c.city)}`).then(r => r.ok ? r.json() : []).then(d => setParties(Array.isArray(d) ? d : [])).catch(() => {});
      apiFetch(`/shorts?city=${encodeURIComponent(c.city)}`).then(r => r.ok ? r.json() : []).then(d => setClubShorts((Array.isArray(d) ? d : []).filter(s => s.club_tag === c.name))).catch(() => {});
    }).catch(() => {});
    loadReviews();
  }, [clubId, refreshKey]);

  const submitReview = async (e) => {
    e.preventDefault();
    let photoUrl = null;
    if (reviewPhoto) {
      const fd = new FormData();
      fd.append("file", reviewPhoto);
      const up = await apiFetch("/upload", { method: "POST", body: fd });
      if (up.ok) photoUrl = (await up.json()).url;
    }
    const res = await apiFetch(`/clubs/${clubId}/reviews`, { method: "POST", body: JSON.stringify({ rating: reviewRating, text: reviewText, photo: photoUrl }) });
    if (res.ok) {
      setReviewText("");
      setReviewRating(5);
      setReviewPhoto(null);
      loadReviews();
      apiFetch(`/clubs/${clubId}`).then(r => r.ok ? r.json() : null).then(c => c && setClub(c));
    } else if (res.status === 401) {
      onNeedAuth();
    }
  };

  const removeReview = async (id) => {
    await apiFetch(`/reviews/${id}`, { method: "DELETE" });
    loadReviews();
  };

  const submitReply = async (reviewId) => {
    if (!replyText.trim()) return;
    const res = await apiFetch(`/reviews/${reviewId}/reply`, { method: "POST", body: JSON.stringify({ text: replyText }) });
    if (res.ok) {
      setReplyingTo(null);
      setReplyText("");
      loadReviews();
    } else {
      const err = await res.json();
      alert(err.detail || "Failed to reply");
    }
  };

  const follow = async () => {
    const res = await apiFetch(`/clubs/${clubId}/follow`, { method: "POST" });
    if (res.ok) {
      const c = await res.json();
      setClub(c);
      onToggle && onToggle(c.is_following);
    } else if (res.status === 401) {
      onNeedAuth();
    }
  };

  if (!club) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 overflow-y-auto" data-testid="club-profile-modal">
      <div className="max-w-4xl mx-auto w-full relative pb-16 min-h-full bg-slate-950">
        <button onClick={onClose} className="fixed top-5 left-5 z-20 flex items-center gap-2 bg-black/60 border border-white/10 text-slate-200 hover:text-white hover:border-rose-500 rounded-full pl-3 pr-4 py-2 text-xs font-mono font-bold transition" data-testid="club-page-back">
          ← Back
        </button>
        <button onClick={onClose} className="fixed top-5 right-5 text-slate-400 hover:text-white z-20 bg-black/60 border border-white/10 rounded-full p-2 transition" data-testid="close-club-profile">
          <X size={20} />
        </button>
        <div className="relative h-72">
          <img src={club.image} alt={club.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
        </div>
        <div className="p-7">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <p className="text-xs font-mono text-rose-400 uppercase tracking-widest mb-1">{club.vibe}</p>
              <h3 className="text-2xl font-black font-crazy-sunset">{club.name}</h3>
              <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-2">
                <MapPin size={13} className="text-cyan-400" /> {club.address}, {club.city} • ⭐ {club.rating}
              </p>
              <a
                href={mapsUrl(`${club.name}, ${club.address}, ${club.city}`)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 mt-2.5 text-[10px] font-mono font-bold px-3.5 py-2 rounded-xl bg-emerald-600/20 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-600/40 hover:text-white transition"
                data-testid="club-directions-btn"
              >
                <MapPin size={12} /> Get Directions on Google Maps
              </a>

              {((club.offers || []).length > 0 || (club.tables || []).length > 0 || Object.keys(club.entry_prices || {}).length > 0) && (
                <div className="mt-4" data-testid="club-booking-section">
                  {["single", "stag", "couple"].some(k => (club.entry_prices || {})[k] != null) && (
                    <div className="flex flex-wrap gap-2 mb-3" data-testid="club-entry-prices">
                      {["single", "stag", "couple"].filter(k => (club.entry_prices || {})[k] != null).map(k => (
                        <span key={k} className="text-[10px] font-mono font-bold px-3 py-1.5 rounded-xl bg-cyan-600/15 border border-cyan-500/30 text-cyan-300" data-testid={`club-entry-${k}`}>
                          🎟 {k.charAt(0).toUpperCase() + k.slice(1)} Entry ₹{club.entry_prices[k]}
                        </span>
                      ))}
                    </div>
                  )}
                  {(club.tables || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3" data-testid="club-tables">
                      {club.tables.map((t, i) => (
                        <span key={i} className="text-[10px] font-mono font-bold px-3 py-1.5 rounded-xl bg-amber-600/15 border border-amber-500/30 text-amber-300" data-testid={`club-table-${i}`}>
                          🪑 {t.name} · ₹{t.price} · {t.seats} seats
                        </span>
                      ))}
                    </div>
                  )}
                  {(club.offers || []).length > 0 && (
                    <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-3">
                      {club.offers.map(o => (
                        <button key={o.id} onClick={() => onBookTable && onBookTable(club, o.id)} className="shrink-0 w-48 text-left rounded-2xl p-3.5 border border-purple-500/30 bg-gradient-to-br from-purple-600/40 to-indigo-700/25 hover:border-purple-400 transition" data-testid={`club-offer-${o.id}`}>
                          <p className="text-base font-black text-purple-100">{o.title}</p>
                          <p className="text-[9px] font-mono text-slate-300 mt-1">{o.from_time ? `From ${o.from_time} · ` : ""}{o.slots_left} slots left</p>
                          {o.cover_charge > 0 && <p className="text-[9px] font-mono text-slate-400">Cover charge ₹{o.cover_charge}</p>}
                          <p className="text-[10px] font-mono font-bold text-white mt-2">Book now →</p>
                        </button>
                      ))}
                    </div>
                  )}
                  <button onClick={() => onBookTable && onBookTable(club)} className="w-full py-3.5 bg-white text-black hover:bg-slate-200 font-bold text-xs uppercase tracking-wider rounded-2xl transition flex items-center justify-center gap-2" data-testid="book-table-btn">
                    🪑 Book a Table / Entry
                  </button>
                </div>
              )}
            </div>
            <div className="shrink-0 flex items-center gap-2">
              <button onClick={() => onShare && onShare("club", clubId)} className="p-2.5 bg-slate-800 hover:bg-emerald-600/70 border border-white/10 rounded-2xl text-emerald-400 transition" title="Copy link to this club" data-testid="share-club-btn">
                <Share2 size={16} />
              </button>
              <button onClick={follow} className={`text-xs font-mono font-bold px-4 py-2.5 rounded-2xl shadow-lg transition ${isFollowing ? 'bg-slate-700 text-cyan-300 shadow-none' : 'bg-rose-600 hover:bg-rose-500 text-white shadow-rose-600/30'}`} data-testid="follow-club-btn">
                {isFollowing ? `Following ✓ (${club.followers_count || 0})` : `+ Follow (${club.followers_count || 0})`}
              </button>
            </div>
          </div>
          <p className="text-sm text-slate-300 leading-relaxed mb-6">{club.description}</p>

          {club.services && club.services.length > 0 && (
            <div className="mb-6">
              <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-3">Services & Amenities</p>
              <div className="flex flex-wrap gap-2" data-testid="club-services">
                {club.services.map(s => (
                  <span key={s} className="px-3.5 py-2 rounded-xl text-xs font-mono font-bold bg-slate-800/80 border border-white/10 text-slate-200" data-testid={`club-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>✓ {s}</span>
                ))}
              </div>
            </div>
          )}

          {club.photos && club.photos.length > 0 && (
            <div className="mb-6">
              <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-3">Gallery</p>
              <div className="grid grid-cols-3 gap-2">
                {club.photos.map((p, i) => (
                  <img key={i} src={p} alt={`${club.name} photo ${i + 1}`} onClick={() => onPhotoOpen && onPhotoOpen(club.photos, i, club.name)} className="aspect-square rounded-xl object-cover w-full cursor-zoom-in hover:opacity-80 transition" data-testid={`club-photo-${i}`} />
                ))}
              </div>
            </div>
          )}

          {club.food_menu_photos && club.food_menu_photos.length > 0 && (
            <div className="mb-6">
              <p className="text-xs font-mono text-amber-400 uppercase tracking-widest mb-3">Food Menu</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="club-food-menu">
                {club.food_menu_photos.map((p, i) => (
                  <img key={i} src={p} alt={`Food menu ${i + 1}`} onClick={() => onPhotoOpen && onPhotoOpen(club.food_menu_photos, i, `${club.name} — Food Menu`)} className="aspect-[3/4] rounded-xl object-cover w-full cursor-zoom-in hover:opacity-80 transition" data-testid={`food-menu-photo-${i}`} />
                ))}
              </div>
            </div>
          )}

          {club.bar_menu_photos && club.bar_menu_photos.length > 0 && (
            <div className="mb-6">
              <p className="text-xs font-mono text-purple-400 uppercase tracking-widest mb-3">Bar Menu</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="club-bar-menu">
                {club.bar_menu_photos.map((p, i) => (
                  <img key={i} src={p} alt={`Bar menu ${i + 1}`} onClick={() => onPhotoOpen && onPhotoOpen(club.bar_menu_photos, i, `${club.name} — Bar Menu`)} className="aspect-[3/4] rounded-xl object-cover w-full cursor-zoom-in hover:opacity-80 transition" data-testid={`bar-menu-photo-${i}`} />
                ))}
              </div>
            </div>
          )}

          <div className="mb-6">
            <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-3">Guest Reviews ({reviews.length})</p>
            <form onSubmit={submitReview} className="mb-4 bg-slate-800/40 border border-white/5 rounded-2xl p-4" data-testid="review-form">
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button" onClick={() => setReviewRating(n)} className={`text-lg transition ${n <= reviewRating ? 'text-amber-400' : 'text-slate-600 hover:text-amber-300'}`} data-testid={`review-star-${n}`}>★</button>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="How was your night here?"
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  className="flex-1 bg-slate-900 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-rose-500"
                  data-testid="review-text-input"
                />
                <label className="shrink-0 bg-slate-900 border border-white/10 hover:border-cyan-500 rounded-xl px-3 py-2.5 text-xs text-slate-400 cursor-pointer transition" title="Attach a photo" data-testid="review-photo-dropzone">
                  {reviewPhoto ? "📷 ✓" : <Camera size={14} />}
                  <input type="file" accept="image/*" onChange={(e) => setReviewPhoto(e.target.files[0] || null)} className="hidden" data-testid="review-photo-input" />
                </label>
                <button type="submit" className="bg-rose-600 hover:bg-rose-500 text-white text-[10px] font-mono font-bold px-4 rounded-xl transition" data-testid="submit-review-btn">Post</button>
              </div>
            </form>
            {reviews.length > 0 && (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {reviews.map(r => (
                  <div key={r.id} className="bg-slate-800/60 border border-white/5 rounded-2xl p-3.5 group" data-testid={`review-${r.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold">{r.user_name}</span>
                      <span className="text-amber-400 text-xs">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                    </div>
                    {r.text && <p className="text-xs text-slate-300">{r.text}</p>}
                    {r.photo && <img src={r.photo} alt="review" className="mt-2 h-24 rounded-xl object-cover" data-testid={`review-photo-${r.id}`} />}
                    {r.reply && (
                      <div className="mt-2 ml-3 bg-slate-950/60 border-l-2 border-cyan-500/50 rounded-r-xl p-2.5" data-testid={`review-reply-${r.id}`}>
                        <p className="text-[9px] font-mono text-cyan-400 uppercase">Owner reply • {r.reply.by}</p>
                        <p className="text-xs text-slate-300">{r.reply.text}</p>
                      </div>
                    )}
                    {!r.reply && viewer && club && (viewer.role === "admin" || club.owner_id === viewer.id) && (
                      replyingTo === r.id ? (
                        <div className="flex gap-2 mt-2">
                          <input type="text" placeholder="Reply as the venue..." value={replyText} onChange={(e) => setReplyText(e.target.value)} className="flex-1 bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-500" data-testid={`reply-input-${r.id}`} />
                          <button onClick={() => submitReply(r.id)} className="bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] font-mono font-bold px-3 rounded-xl transition" data-testid={`submit-reply-${r.id}`}>Send</button>
                        </div>
                      ) : (
                        <button onClick={() => setReplyingTo(r.id)} className="text-[10px] font-mono text-cyan-400 hover:underline mt-1.5" data-testid={`reply-btn-${r.id}`}>Reply as venue</button>
                      )
                    )}
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[9px] font-mono text-slate-500">{new Date(r.created_at).toLocaleDateString()}</span>
                      <button onClick={() => removeReview(r.id)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition" data-testid={`delete-review-${r.id}`}><X size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mb-6">
            <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-3">Upcoming Parties Here</p>
            {parties.length === 0 ? (
              <p className="text-xs font-mono text-slate-500">No parties listed yet.</p>
            ) : (
              <div className="space-y-2">
                {parties.map(ev => (
                  <div key={ev.id} className="bg-slate-800/60 border border-white/5 rounded-2xl p-3.5 flex items-center justify-between" data-testid={`club-event-${ev.id}`}>
                    <div>
                      <p className="text-sm font-bold">{ev.name}</p>
                      <p className="text-[10px] font-mono text-slate-400">📅 {ev.date} • by {ev.host_name}</p>
                    </div>
                    <span className="text-[10px] font-mono text-rose-400">{ev.price}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {clubShorts.length > 0 && (
            <div>
              <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-3">Shorts From This Club</p>
              <div className="grid grid-cols-3 gap-2">
                {clubShorts.map(s => (
                  <div key={s.id} className="aspect-[9/16] rounded-xl overflow-hidden bg-black" data-testid={`club-short-${s.id}`}>
                    {s.media_type === "image" ? (
                      <img src={s.video_url} alt={s.caption} className="w-full h-full object-cover" />
                    ) : (
                      <video src={s.video_url} className="w-full h-full object-cover" muted playsInline />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Followers list modal
function FollowersModal({ userId, onClose, onOpenUser }) {
  const [followers, setFollowers] = useState([]);
  useEffect(() => {
    apiFetch(`/users/${userId}/followers`).then(r => r.ok ? r.json() : []).then(d => setFollowers(Array.isArray(d) ? d : [])).catch(() => {});
  }, [userId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="followers-modal">
      <div className="bg-slate-900 border border-white/10 rounded-3xl p-6 max-w-sm w-full shadow-2xl relative">
        <button onClick={onClose} className="absolute top-5 right-5 text-slate-400 hover:text-white" data-testid="close-followers-modal">
          <X size={18} />
        </button>
        <h3 className="text-lg font-black mb-1">Followers</h3>
        <p className="text-xs font-mono text-slate-500 mb-4">People following this profile</p>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {followers.length === 0 && <p className="text-xs font-mono text-slate-500 text-center py-6">No followers yet — share your shorts to grow your crew.</p>}
          {followers.map((u) => (
            <button key={u.id} onClick={() => onOpenUser && onOpenUser(u.id)} className="w-full flex items-center gap-3 p-3 rounded-2xl bg-slate-800/60 hover:bg-slate-800 border border-white/5 transition text-left" data-testid={`follower-${u.id}`}>
              <img src={u.avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"} alt="" className="w-9 h-9 rounded-full object-cover" />
              <div className="min-w-0">
                <p className="text-xs font-bold truncate">{u.name}</p>
                <p className="text-[10px] font-mono text-slate-500 uppercase">{u.role} • {u.city}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Short comments modal
function CommentsModal({ shortId, kind = "short", user, onClose, onNeedAuth, onCountChange, onOpenUser }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const base = kind === "post" ? `/posts/${shortId}` : `/shorts/${shortId}`;

  const load = () => apiFetch(`${base}/comments`)
    .then(r => r.ok ? r.json() : [])
    .then(d => { const arr = Array.isArray(d) ? d : []; setComments(arr); onCountChange && onCountChange(arr.length); })
    .catch(() => {});

  useEffect(() => { load(); }, [shortId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (!user) { onNeedAuth(); return; }
    const res = await apiFetch(`${base}/comments`, { method: "POST", body: JSON.stringify({ text }) });
    if (res.ok) { setText(""); load(); }
  };

  const remove = async (id) => {
    await apiFetch(`/comments/${id}`, { method: "DELETE" });
    load();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center" data-testid="comments-modal">
      <div className="bg-slate-900 border border-white/10 rounded-t-3xl sm:rounded-3xl max-w-md w-full shadow-2xl relative flex flex-col max-h-[80vh]">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-bold">Comments ({comments.length})</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-comments-modal">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {comments.length === 0 && <p className="text-xs font-mono text-slate-500 text-center py-6">No comments yet — start the conversation.</p>}
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-3 group" data-testid={`comment-${c.id}`}>
              <img src={c.user_avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <button onClick={() => onOpenUser && onOpenUser(c.user_id)} className="text-xs font-bold hover:text-cyan-300 transition" data-testid={`comment-author-${c.id}`}>{c.user_name}</button>
                <p className="text-sm text-slate-300">{c.text}</p>
              </div>
              {user && (user.role === "admin" || c.user_id === user.id) && (
                <button onClick={() => remove(c.id)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition" data-testid={`delete-comment-${c.id}`}>
                  <X size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
        <form onSubmit={submit} className="p-4 border-t border-white/10 flex gap-2">
          <input
            type="text"
            placeholder={user ? "Add a comment..." : "Sign in to comment..."}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={!user}
            className="flex-1 bg-slate-800 border border-white/10 rounded-2xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-rose-500 disabled:opacity-50"
            data-testid="comment-input"
          />
          <button type="submit" disabled={!user} className="bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white px-4 py-2.5 rounded-2xl font-bold text-xs transition" data-testid="comment-send-btn">
            Post
          </button>
        </form>
      </div>
    </div>
  );
}

// Following feed: shorts & parties from people and clubs you follow
// Home feed — newest posts from people you follow (+ yours), Instagram-style
function FeedTab({ user, onNeedAuth, onOpenUser, onOpenComments, crazyCls }) {
  const [posts, setPosts] = useState(null);
  const load = useCallback(async () => {
    const res = await apiFetch("/posts/feed");
    if (res.ok) setPosts(await res.json());
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const like = async (id) => {
    const res = await apiFetch(`/posts/${id}/like`, { method: "POST" });
    if (res.ok) load(); else if (res.status === 401) onNeedAuth();
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-4" data-testid="feed-tab">
      <div className="mb-6">
        <h2 className={`text-2xl font-black ${crazyCls || "font-crazy"}`}>Your Feed</h2>
        <span className="block mt-1 text-xs font-mono text-slate-500 uppercase tracking-widest">Posts from your crew</span>
      </div>
      {posts === null ? null : posts.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-4xl mb-3">📸</p>
          <p className="text-sm font-mono text-slate-400" data-testid="feed-empty">No posts yet — follow people and clubs to fill your feed, or share your own from your profile.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {posts.map(p => (
            <div key={p.id} className="bg-slate-900/70 border border-white/10 rounded-2xl overflow-hidden" data-testid={`feed-post-${p.id}`}>
              <button onClick={() => onOpenUser(p.user_id)} className="w-full flex items-center gap-2.5 p-3.5 text-left hover:bg-white/5 transition" data-testid={`feed-author-${p.id}`}>
                <img src={p.user_avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"} className="w-9 h-9 rounded-full object-cover border border-white/10" alt="" />
                <div>
                  <p className="text-sm font-bold flex items-center gap-1.5">{p.user_name} {p.user_verified && <span className="text-cyan-400 text-[10px]">✓</span>}</p>
                  <p className="text-[9px] font-mono text-slate-500">📍 {p.city} · {new Date(p.created_at).toLocaleDateString()}</p>
                </div>
              </button>
              <img src={p.photo} alt={p.caption || "post"} className="w-full aspect-square object-cover" data-testid={`feed-photo-${p.id}`} />
              <div className="p-3.5">
                <div className="flex items-center gap-4 mb-2">
                  <button onClick={() => like(p.id)} className="flex items-center gap-1.5" data-testid={`feed-like-${p.id}`}>
                    <Heart size={19} className={user && (p.liked_by || []).includes(user.id) ? "text-rose-500 fill-rose-500" : "text-slate-300"} />
                    <span className="text-xs font-mono font-bold text-slate-300">{p.likes}</span>
                  </button>
                  <button onClick={() => onOpenComments(p.id)} className="flex items-center gap-1.5" data-testid={`feed-comments-${p.id}`}>
                    <MessageCircle size={19} className="text-slate-300" />
                    <span className="text-xs font-mono font-bold text-slate-300">{p.comments || 0}</span>
                  </button>
                </div>
                {p.caption && <p className="text-xs text-slate-200"><span className="font-bold mr-1.5">{p.user_name}</span>{p.caption}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FollowingFeed({ user, onShare, crazyCls }) {
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    apiFetch("/feed/following").then(r => r.ok ? r.json() : { shorts: [], events: [] }).then(setFeed).catch(() => setFeed({ shorts: [], events: [] }));
  }, []);

  if (!feed) {
    return <div className="max-w-4xl mx-auto px-6 py-16 text-center text-slate-500 font-mono text-xs">Loading your crew's updates…</div>;
  }

  const empty = feed.shorts.length === 0 && feed.events.length === 0;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="following-feed">
      <div className="mb-8">
        <h2 className={`text-3xl font-black ${crazyCls || "font-crazy-sunset"}`}>Following Feed</h2>
        <div className="mt-2 h-px w-28 bg-gradient-to-r from-rose-500 to-transparent" />
        <p className="text-xs text-slate-400 mt-2">Shorts and parties from people and clubs you follow.</p>
      </div>

      {empty && (
        <div className="bg-slate-900/60 border border-white/10 rounded-3xl p-10 text-center" data-testid="following-empty">
          <Sparkles size={28} className="mx-auto text-rose-500 mb-4" />
          <p className="text-sm font-bold mb-2">Your feed is quiet</p>
          <p className="text-xs text-slate-400 font-mono">Follow creators on Shorts or clubs on the main page to fill this up.</p>
        </div>
      )}

      {feed.events.length > 0 && (
        <div className="mb-10">
          <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-4">Parties From Clubs You Follow</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {feed.events.map(ev => (
              <div key={ev.id} className="bg-slate-900/70 border border-white/10 rounded-2xl p-5" data-testid={`feed-event-${ev.id}`}>
                <h3 className="font-bold mb-1">{ev.name}</h3>
                <p className="text-xs text-slate-400 font-mono">📅 {ev.date} • 📍 {ev.venue}, {ev.city}</p>
                <p className="text-[10px] font-mono text-rose-400 mt-2">{ev.price}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {feed.shorts.length > 0 && (
        <div className="space-y-6">
          {feed.shorts.map(s => (
            <div key={s.id} className="bg-slate-900 border border-white/10 rounded-3xl overflow-hidden" data-testid={`feed-short-${s.id}`}>
              <div className="flex items-center gap-3 p-4">
                <img src={s.user_avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"} alt="" className="w-9 h-9 rounded-full object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate">{s.user_name}</p>
                  <p className="text-[10px] font-mono text-slate-500">{s.city} {s.club_tag ? `• ${s.club_tag}` : ''}</p>
                </div>
                <button onClick={() => onShare && onShare("short", s.id)} className="text-slate-400 hover:text-emerald-400 transition" title="Copy link" data-testid={`feed-share-${s.id}`}>
                  <Share2 size={15} />
                </button>
              </div>
              <div className="aspect-video bg-black">
                {s.media_type === "image" ? (
                  <img src={s.video_url} alt={s.caption} className="w-full h-full object-cover" />
                ) : (
                  <video src={s.video_url} className="w-full h-full object-cover" controls muted playsInline />
                )}
              </div>
              <div className="p-4">
                <p className="text-sm text-slate-200">{s.caption}</p>
                <p className="text-[10px] font-mono text-slate-500 mt-2">❤️ {s.likes} • 💬 {s.comments}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Guest list modal (event host or admin)
function GuestsModal({ eventId, onClose }) {
  const [guests, setGuests] = useState(null);
  useEffect(() => {
    apiFetch(`/events/${eventId}/guests`).then(r => r.ok ? r.json() : []).then(d => setGuests(Array.isArray(d) ? d : [])).catch(() => setGuests([]));
  }, [eventId]);

  const toggleCheckin = async (guestId) => {
    const res = await apiFetch(`/events/${eventId}/checkin/${guestId}`, { method: "POST" });
    if (res.ok) {
      const d = await res.json();
      setGuests(gs => gs.map(g => g.id === guestId ? { ...g, checked_in: d.checked_in } : g));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="guests-modal">
      <div className="bg-slate-900 border border-white/10 rounded-3xl p-6 max-w-sm w-full shadow-2xl relative">
        <button onClick={onClose} className="absolute top-5 right-5 text-slate-400 hover:text-white" data-testid="close-guests-modal">
          <X size={18} />
        </button>
        <h3 className="text-lg font-black mb-1">Guest List</h3>
        <p className="text-xs font-mono text-slate-500 mb-4">{guests ? `${guests.length} going` : "Loading…"}</p>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {guests && guests.length === 0 && <p className="text-xs font-mono text-slate-500 text-center py-6">No RSVPs yet.</p>}
          {(guests || []).map((u) => (
            <button key={u.id} onClick={() => toggleCheckin(u.id)} className={`w-full flex items-center gap-3 p-3 rounded-2xl border transition text-left ${u.checked_in ? 'bg-emerald-900/30 border-emerald-500/40' : 'bg-slate-800/60 border-white/5 hover:bg-slate-800'}`} data-testid={`guest-${u.id}`} title="Tap to check in / undo">
              <img src={u.avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"} alt="" className="w-9 h-9 rounded-full object-cover" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold truncate">{u.name}</p>
                <p className="text-[10px] font-mono text-slate-500 uppercase">{u.role} • {u.city}</p>
              </div>
              {u.ticket_code && (
                <span className="shrink-0 text-[10px] font-mono font-bold text-cyan-300 bg-slate-950 border border-dashed border-cyan-500/40 px-2.5 py-1.5 rounded-lg" data-testid={`guest-ticket-${u.id}`}>
                  🎟 {u.ticket_code}
                </span>
              )}
              {u.checked_in && (
                <span className="shrink-0 text-[10px] font-mono font-bold text-emerald-400" data-testid={`guest-entered-${u.id}`}>✓ Entered</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Public user profile: bio, stats, follow, their parties and shorts
function UserProfileModal({ userId, viewer, isFollowing, onClose, onNeedAuth, onFollowChange, onShare }) {
  const [profile, setProfile] = useState(null);
  const [shorts, setShorts] = useState([]);
  const [events, setEvents] = useState([]);
  const [hostStats, setHostStats] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    apiFetch(`/users/${userId}`).then(r => r.ok ? r.json() : null).then(p => setProfile(p || { id: userId, name: "Party Guest", role: "guest", city: "—", bio: "", avatar: "", followers_count: 0, following_count: 0 })).catch(() => {});
    apiFetch(`/users/${userId}/shorts`).then(r => r.ok ? r.json() : []).then(d => setShorts(Array.isArray(d) ? d : [])).catch(() => {});
    apiFetch(`/users/${userId}/events`).then(r => r.ok ? r.json() : []).then(d => setEvents(Array.isArray(d) ? d : [])).catch(() => {});
    apiFetch(`/users/${userId}/host-stats`).then(r => r.ok ? r.json() : null).then(setHostStats).catch(() => {});
  }, [userId]);

  const follow = async () => {
    const res = await apiFetch(`/users/${userId}/follow`, { method: "POST" });
    if (res.ok) {
      const updated = await res.json();
      setProfile(p => ({ ...p, followers_count: updated.followers_count }));
      onFollowChange && onFollowChange(updated.is_following);
    } else if (res.status === 401) {
      onNeedAuth();
    }
  };

  const toggleVerify = async () => {
    const res = await apiFetch(`/users/${userId}/verify`, { method: "POST" });
    if (res.ok) setProfile(await res.json());
  };

  if (!profile) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="user-profile-modal">
      <div className="bg-slate-900 border border-white/10 rounded-3xl max-w-lg w-full shadow-2xl relative max-h-[85vh] overflow-y-auto p-7">
        <button onClick={onClose} className="absolute top-5 right-5 text-slate-400 hover:text-white z-10 bg-black/50 rounded-full p-1.5" data-testid="close-user-profile">
          <X size={20} />
        </button>
        {(profile.cover || (profile.role === "artist" && (profile.genres || []).length > 0)) && (
          <div className="relative h-32 -mx-7 -mt-7 mb-6 overflow-hidden bg-gradient-to-r from-purple-700 via-rose-600 to-cyan-600" data-testid="user-profile-cover">
            {profile.cover && <img src={profile.cover} alt="" className="w-full h-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
            {profile.role === "artist" && (profile.genres || []).length > 0 && (
              <div className="absolute bottom-3 left-6 right-6 flex flex-wrap gap-1.5" data-testid="banner-genres">
                {profile.genres.map(g => (
                  <span key={g} className="text-[9px] font-mono font-bold px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-purple-400/40 text-purple-200">{g}</span>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="flex flex-col items-center text-center mb-6">
          <img src={profile.avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&q=80"} alt={profile.name} className="w-20 h-20 rounded-full object-cover border-2 border-rose-500 shadow-xl mb-3" />
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-xl font-black font-crazy-aqua">{profile.name}</h3>
            {profile.verified && (
              <span className="bg-cyan-500 rounded-full p-1" title="Verified"><Check size={11} className="text-white" /></span>
            )}
            <span className="text-[10px] font-mono uppercase bg-cyan-500/20 text-cyan-300 px-2 py-0.5 rounded-lg border border-cyan-500/30">{roleLabel(profile.role)}</span>
          </div>
          <p className="text-xs text-slate-400 font-mono">📍 {profile.city}</p>
          {profile.bio && <p className="text-xs text-slate-300 mt-2">{profile.bio}</p>}
          <div className="flex gap-6 mt-4 font-mono text-center">
            <div><p className="text-lg font-black text-rose-500">{shorts.length}</p><p className="text-[10px] text-slate-500 uppercase">Shorts</p></div>
            <div><p className="text-lg font-black text-cyan-400">{profile.followers_count || 0}</p><p className="text-[10px] text-slate-500 uppercase">Followers</p></div>
            <div><p className="text-lg font-black text-purple-400">{profile.following_count || 0}</p><p className="text-[10px] text-slate-500 uppercase">Following</p></div>
          </div>
          {hostStats && (hostStats.parties_hosted > 0 || hostStats.reviews_count > 0) && (
            <div className="mt-4 w-full bg-slate-800/50 border border-white/5 rounded-2xl p-3.5" data-testid="host-track-record">
              <p className="text-[9px] font-mono text-amber-400 uppercase tracking-widest mb-2">Track Record</p>
              <div className="flex justify-center gap-6 font-mono text-center">
                <div><p className="text-base font-black text-white">{hostStats.parties_hosted}</p><p className="text-[9px] text-slate-500 uppercase">Parties Hosted</p></div>
                {hostStats.avg_rating != null && (
                  <div><p className="text-base font-black text-amber-400">★ {hostStats.avg_rating}</p><p className="text-[9px] text-slate-500 uppercase">Avg Rating</p></div>
                )}
                {hostStats.reviews_count > 0 && (
                  <div><p className="text-base font-black text-cyan-400">{hostStats.reviews_count}</p><p className="text-[9px] text-slate-500 uppercase">Guest Reviews</p></div>
                )}
              </div>
            </div>
          )}
          {(!viewer || viewer.id !== userId) && (
            <button onClick={follow} className={`mt-4 text-xs font-mono font-bold px-6 py-2.5 rounded-2xl transition ${isFollowing ? 'bg-slate-700 text-cyan-300' : 'bg-rose-600 hover:bg-rose-500 text-white shadow-lg shadow-rose-600/30'}`} data-testid="profile-follow-btn">
              {isFollowing ? "Following ✓" : "+ Follow"}
            </button>
          )}
          {viewer && viewer.role === "admin" && viewer.id !== userId && (
            <button onClick={toggleVerify} className="mt-2 text-[10px] font-mono text-amber-400 border border-amber-500/30 px-3 py-1.5 rounded-xl hover:bg-amber-600/20 transition" data-testid="verify-user-btn">
              {profile.verified ? "Remove Verified Badge" : "Mark as Verified ✓"}
            </button>
          )}
          <button onClick={() => onShare && onShare("user", userId)} className="mt-2 text-[10px] font-mono text-emerald-400 border border-emerald-500/30 px-3 py-1.5 rounded-xl hover:bg-emerald-600/20 transition flex items-center gap-1.5" data-testid="share-profile-btn">
            <Share2 size={11} /> Share Profile
          </button>
          {profile.role === "artist" && profile.spotify && (
            <a href={profile.spotify} target="_blank" rel="noreferrer" className="mt-2 text-[10px] font-mono font-bold bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-xl shadow-lg shadow-emerald-600/30 transition inline-flex items-center gap-1.5" data-testid="profile-spotify-btn">
              ▶ Listen on Spotify
            </a>
          )}
          {profile.role === "artist" && viewer && ["club_owner", "admin"].includes(viewer.role) && viewer.id !== userId && (
            <button onClick={() => setBookingOpen(true)} className="mt-2 text-[10px] font-mono font-bold bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-xl btn-neon-purple transition" data-testid="book-dj-btn">
              🎧 Book This DJ
            </button>
          )}
          {bookingOpen && <BookDJModal artist={profile} onClose={() => setBookingOpen(false)} />}
        </div>

        {profile.role === "artist" && (profile.genres || []).length > 0 && (
          <div className="mb-6" data-testid="profile-genres">
            <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-3">🎛️ Genres</p>
            <div className="flex flex-wrap gap-2">
              {profile.genres.map(g => (
                <span key={g} className="text-[11px] font-mono px-3 py-1.5 rounded-full bg-purple-600/20 border border-purple-500/30 text-purple-300" data-testid={`profile-genre-${g.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{g}</span>
              ))}
            </div>
          </div>
        )}

        {profile.role === "artist" && (
          <div className="mb-6">
            <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-3">🎧 Mixes</p>
            {(profile.mixes || []).length === 0 ? (
              <p className="text-xs font-mono text-slate-500">No mixes yet.</p>
            ) : (
              <div className="space-y-2">
                {profile.mixes.map((m, i) => (
                  <a key={i} href={m} target="_blank" rel="noreferrer" className="block bg-slate-800/60 border border-white/5 rounded-2xl p-3 text-xs font-mono text-cyan-300 hover:border-cyan-500/50 truncate transition" data-testid={`mix-${i}`}>{m}</a>
                ))}
              </div>
            )}
          </div>
        )}

        {events.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-3">{profile.role === "artist" ? "Gig History" : "Parties They're Hosting"}</p>
            <div className="space-y-2">
              {events.map(ev => (
                <div key={ev.id} className="bg-slate-800/60 border border-white/5 rounded-2xl p-3.5" data-testid={`profile-event-${ev.id}`}>
                  <p className="text-sm font-bold">{ev.name}</p>
                  <p className="text-[10px] font-mono text-slate-400">📅 {ev.date} • 📍 {ev.venue}, {ev.city} • {ev.price}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-3">Their Shorts</p>
          {shorts.length === 0 ? (
            <p className="text-xs font-mono text-slate-500">No shorts yet.</p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {shorts.map(s => (
                <div key={s.id} className="aspect-[9/16] rounded-xl overflow-hidden bg-black" data-testid={`profile-short-${s.id}`}>
                  {s.media_type === "image" ? (
                    <img src={s.video_url} alt={s.caption} className="w-full h-full object-cover" />
                  ) : (
                    <video src={s.video_url} className="w-full h-full object-cover" muted playsInline />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6">
          <p className="text-xs font-mono text-rose-400 uppercase tracking-widest mb-3">Posts</p>
          <PostsGrid userId={profile.id} viewer={viewer} />
        </div>
      </div>
    </div>
  );
}

// Table & entry booking modal (District-style: offer slots, entry types, tables)
function TableBookingModal({ club, event, initialOfferId, user, onClose, onNeedAuth, onBooked }) {
  const venue = club || event;
  const offers = venue.offers || [];
  const ep = venue.entry_prices || {};
  const tables = venue.tables || [];
  const [offerId, setOfferId] = useState(initialOfferId || null);
  const [kind, setKind] = useState(tables.length > 0 ? "table" : "entry");
  const [entryType, setEntryType] = useState(ep.couple ? "couple" : Object.keys(ep)[0] || "single");
  const [tableOpt, setTableOpt] = useState(tables[0]?.name || "");
  const [qty, setQty] = useState(1);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const selectedOffer = offers.find(o => o.id === offerId);
  const unit = kind === "entry" ? (ep[entryType] || 0) : (tables.find(t => t.name === tableOpt)?.price || 0);
  const total = unit * qty + (selectedOffer?.cover_charge || 0);
  const entryTypes = [["single", "Single"], ["stag", "Stag"], ["couple", "Couple"]].filter(([k]) => ep[k]);

  const submit = async () => {
    if (!user) { onNeedAuth(); return; }
    if (!date) { alert("Pick a date first"); return; }
    setSaving(true);
    try {
      const res = await apiFetch("/table-bookings", {
        method: "POST",
        body: JSON.stringify({ club_id: club?.id, event_id: event?.id, kind, entry_type: kind === "entry" ? entryType : null, option: kind === "table" ? tableOpt : null, offer_id: offerId || null, qty, date, note })
      });
      if (res.ok) {
        alert("🪑 Booking requested! The venue will confirm shortly — track it on your profile.");
        onBooked && onBooked();
        onClose();
      } else {
        const err = await res.json();
        alert(err.detail || "Booking failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const pill = (active) => `flex-1 py-2.5 rounded-xl border text-[11px] font-mono font-bold transition ${active ? 'border-rose-500 bg-rose-600/20 text-white' : 'border-white/10 bg-slate-800 text-slate-400 hover:border-rose-500/40'}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-white/10 rounded-3xl p-6 w-full max-w-md relative max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="table-booking-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="close-table-booking"><X size={20} /></button>
        <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest">Book a Table</span>
        <h3 className="text-2xl font-black mb-4 font-crazy">{venue.name}</h3>

        {offers.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2">Offers for tonight</p>
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
              {offers.map(o => (
                <button key={o.id} type="button" onClick={() => setOfferId(offerId === o.id ? null : o.id)} className={`shrink-0 w-44 text-left rounded-2xl p-3 border transition ${offerId === o.id ? 'border-purple-400 bg-gradient-to-br from-purple-600/50 to-indigo-700/40' : 'border-white/10 bg-gradient-to-br from-purple-600/20 to-indigo-700/10 hover:border-purple-500/40'}`} data-testid={`offer-${o.id}`}>
                  <p className="text-sm font-black text-purple-200">{o.title}</p>
                  <p className="text-[9px] font-mono text-slate-300 mt-1">{o.from_time ? `From ${o.from_time} · ` : ""}{o.slots_left} slots left</p>
                  {o.cover_charge > 0 && <p className="text-[9px] font-mono text-slate-400">Cover charge ₹{o.cover_charge}</p>}
                </button>
              ))}
            </div>
          </div>
        )}

        {tables.length > 0 && Object.keys(ep).length > 0 && (
          <div className="flex gap-2 mb-4">
            <button type="button" onClick={() => setKind("table")} className={pill(kind === "table")} data-testid="kind-table">🪑 Table</button>
            <button type="button" onClick={() => setKind("entry")} className={pill(kind === "entry")} data-testid="kind-entry">🎟️ Entry Only</button>
          </div>
        )}

        {kind === "table" && tables.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2">Choose a table</p>
            <div className="space-y-2">
              {tables.map(t => (
                <button key={t.name} type="button" onClick={() => setTableOpt(t.name)} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition ${tableOpt === t.name ? 'border-rose-500 bg-rose-600/15' : 'border-white/10 bg-slate-800 hover:border-rose-500/40'}`} data-testid={`table-option-${t.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <span className="text-sm font-bold">{t.name} <span className="text-[10px] font-mono text-slate-400">seats {t.seats}</span></span>
                  <span className="text-xs font-mono text-amber-300">₹{Number(t.price).toLocaleString('en-IN')}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {kind === "entry" && entryTypes.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2">Entry type</p>
            <div className="flex gap-2">
              {entryTypes.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setEntryType(k)} className={pill(entryType === k)} data-testid={`entry-${k}`}>
                  {l}<br /><span className="text-amber-300">₹{Number(ep[k]).toLocaleString('en-IN')}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-4">
          <div className="flex-1">
            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2">Qty</p>
            <div className="flex items-center gap-3 bg-slate-800 border border-white/10 rounded-xl px-3 py-2">
              <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="w-7 h-7 rounded-lg bg-slate-900 text-white font-bold" data-testid="qty-minus">−</button>
              <span className="text-sm font-black flex-1 text-center" data-testid="qty-value">{qty}</span>
              <button type="button" onClick={() => setQty(Math.min(20, qty + 1))} className="w-7 h-7 rounded-lg bg-slate-900 text-white font-bold" data-testid="qty-plus">+</button>
            </div>
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2">Date</p>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-rose-500" data-testid="booking-date-input" />
          </div>
        </div>

        <input placeholder="Note for the venue (birthday, bottle service…)" value={note} onChange={e => setNote(e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500 mb-4" data-testid="booking-note-input" />

        <div className="flex items-center justify-between mb-4 bg-slate-800/60 rounded-xl px-4 py-3">
          <span className="text-[10px] font-mono text-slate-400 uppercase">Pay at venue</span>
          <span className="text-lg font-black text-amber-300" data-testid="booking-total">₹{total.toLocaleString('en-IN')}</span>
        </div>
        <button onClick={submit} disabled={saving} className="w-full py-3.5 bg-white text-black hover:bg-slate-200 disabled:opacity-50 font-bold text-xs uppercase tracking-wider rounded-2xl transition" data-testid="confirm-booking-btn">
          {saving ? "Booking…" : "Book Now"}
        </button>
      </div>
    </div>
  );
}

// Create a permanent photo post (Instagram-style feed)
function NewPostModal({ currentCity, onClose, onSuccess }) {
  const [photo, setPhoto] = useState(null);
  const [caption, setCaption] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!photo) { alert("Pick a photo first"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", photo);
      const up = await apiFetch("/upload", { method: "POST", body: fd });
      if (!up.ok) { alert("Photo upload failed"); return; }
      const url = (await up.json()).url;
      const res = await apiFetch("/posts", { method: "POST", body: JSON.stringify({ photo: url, caption, city: currentCity }) });
      if (res.ok) {
        onSuccess();
      } else {
        const err = await res.json();
        alert(err.detail || "Post failed");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-white/10 rounded-3xl p-6 w-full max-w-md relative" onClick={e => e.stopPropagation()} data-testid="new-post-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="close-new-post"><X size={20} /></button>
        <span className="text-xs font-mono text-rose-400 uppercase tracking-widest">New Post</span>
        <h3 className="text-2xl font-black mb-4 font-crazy">Post To Your Feed</h3>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="flex items-center justify-center gap-2 w-full bg-slate-800 border border-dashed border-white/20 hover:border-rose-500 rounded-xl px-4 py-3 text-xs font-mono text-slate-300 cursor-pointer transition" data-testid="post-photo-dropzone">
              <Camera size={14} className="text-rose-400" /> {photo ? photo.name : "Choose a photo"}
              <input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files[0] || null)} className="hidden" data-testid="post-photo-input" />
            </label>
            {photo && <img src={URL.createObjectURL(photo)} alt="Post preview" className="mt-2 w-full aspect-square object-cover rounded-xl" data-testid="post-photo-preview" />}
          </div>
          <textarea placeholder="Caption your moment… #vibes" value={caption} onChange={e => setCaption(e.target.value)} rows={3} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-rose-500" data-testid="post-caption-input" />
          <button type="submit" disabled={saving} className="w-full py-3.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold text-xs uppercase tracking-wider rounded-2xl btn-neon transition" data-testid="post-submit-btn">
            {saving ? "Posting…" : "Share Post"}
          </button>
        </form>
      </div>
    </div>
  );
}

// Posts grid — permanent Instagram-style profile feed (squares)
function PostsGrid({ userId, viewer, refreshKey = 0 }) {
  const [posts, setPosts] = useState(null);
  const load = useCallback(async () => {
    const res = await apiFetch(`/users/${userId}/posts`);
    if (res.ok) setPosts(await res.json());
  }, [userId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const like = async (id) => {
    if (!viewer) { alert("Sign in to like posts"); return; }
    await apiFetch(`/posts/${id}/like`, { method: "POST" });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await apiFetch(`/posts/${id}`, { method: "DELETE" });
    load();
  };

  if (!posts) return null;
  const isOwner = viewer && viewer.id === userId;
  if (posts.length === 0) {
    return isOwner ? <p className="text-xs font-mono text-slate-500" data-testid="posts-empty">No posts yet — share your first night out.</p> : <p className="text-xs font-mono text-slate-500" data-testid="posts-empty">No posts yet.</p>;
  }
  return (
    <div className="grid grid-cols-3 gap-1.5" data-testid="posts-grid">
      {posts.map(p => (
        <div key={p.id} className="relative aspect-square rounded-xl overflow-hidden bg-black group" data-testid={`post-${p.id}`}>
          <img src={p.photo} alt={p.caption || "post"} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center gap-1.5 p-2">
            <button onClick={() => like(p.id)} className="flex items-center gap-1.5 text-white text-xs font-mono font-bold" data-testid={`post-like-${p.id}`}>
              <Heart size={15} className={viewer && (p.liked_by || []).includes(viewer.id) ? "text-rose-500 fill-rose-500" : "text-white"} /> {p.likes}
            </button>
            {p.caption && <p className="text-[9px] font-mono text-slate-300 text-center leading-tight line-clamp-2">{p.caption}</p>}
            {isOwner && (
              <button onClick={() => remove(p.id)} className="text-[9px] font-mono text-red-400 hover:text-red-300 underline" data-testid={`post-delete-${p.id}`}>delete</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Navbar search: clubs, people, parties
function SearchBox({ onSelectClub, onSelectUser, onSelectEvent, mobile = false }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const tid = mobile ? "mobile" : "desktop";

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      apiFetch(`/search?q=${encodeURIComponent(q.trim())}`).then(r => r.ok ? r.json() : null).then(setResults).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const pick = (fn, id) => { setQ(""); setResults(null); fn(id); };
  const hasResults = results && (results.clubs.length || results.users.length || results.events.length);

  return (
    <div className={`relative ${mobile ? 'block w-full' : 'hidden lg:block'}`} data-testid={`search-box-${tid}`}>
      <div className="flex items-center gap-2 bg-slate-900/80 border border-white/10 px-3.5 py-2 rounded-xl focus-within:border-rose-500 transition">
        <Search size={14} className="text-slate-500" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search clubs, people, parties..."
          className={`bg-transparent text-xs font-mono text-white placeholder-slate-500 focus:outline-none ${mobile ? 'flex-1' : 'w-48'}`}
          data-testid={`search-input-${tid}`}
        />
      </div>
      {results && (
        <div className="absolute top-full mt-2 left-0 right-0 lg:right-auto lg:w-80 bg-slate-900 border border-white/10 rounded-2xl shadow-2xl p-3 z-50 max-h-96 overflow-y-auto" data-testid={`search-results-${tid}`}>
          {!hasResults && <p className="text-xs font-mono text-slate-500 text-center py-4">No matches for "{q}"</p>}
          {results.clubs.length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] font-mono text-rose-400 uppercase tracking-widest mb-2">Clubs</p>
              {results.clubs.map(c => (
                <button key={c.id} onClick={() => pick(onSelectClub, c.id)} className="w-full text-left p-2.5 rounded-xl hover:bg-slate-800 transition flex items-center justify-between" data-testid={`search-club-${c.id}`}>
                  <span className="text-xs font-bold">{c.name}</span>
                  <span className="text-[10px] font-mono text-slate-500">{c.city} • ⭐ {c.rating}</span>
                </button>
              ))}
            </div>
          )}
          {results.users.length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] font-mono text-cyan-400 uppercase tracking-widest mb-2">People</p>
              {results.users.map(u => (
                <button key={u.id} onClick={() => pick(onSelectUser, u.id)} className="w-full text-left p-2.5 rounded-xl hover:bg-slate-800 transition flex items-center gap-2.5" data-testid={`search-user-${u.id}`}>
                  <img src={u.avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"} alt="" className="w-7 h-7 rounded-full object-cover" />
                  <span className="text-xs font-bold">{u.name}</span>
                  <span className="text-[10px] font-mono text-slate-500 uppercase">{u.role}</span>
                </button>
              ))}
            </div>
          )}
          {results.events.length > 0 && (
            <div>
              <p className="text-[10px] font-mono text-emerald-400 uppercase tracking-widest mb-2">Parties</p>
              {results.events.map(ev => (
                <button key={ev.id} onClick={() => pick(onSelectEvent, ev.id)} className="w-full text-left p-2.5 rounded-xl hover:bg-slate-800 transition" data-testid={`search-event-${ev.id}`}>
                  <p className="text-xs font-bold">{ev.name}</p>
                  <p className="text-[10px] font-mono text-slate-500">{ev.date} • {ev.venue}, {ev.city}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// DJ booking offer modal (club owner -> artist)
function BookDJModal({ artist, onClose }) {
  const [form, setForm] = useState({ date: "", fee: "", message: "" });
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-purple-500";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiFetch("/bookings", {
        method: "POST",
        body: JSON.stringify({ artist_id: artist.id, date: form.date, fee: parseFloat(form.fee), message: form.message })
      });
      if (res.ok) {
        alert(`Gig offer sent to ${artist.name}!`);
        onClose();
      } else {
        const err = await res.json();
        alert(err.detail || "Failed to send offer");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="book-dj-modal">
      <div className="bg-slate-900 border border-white/10 rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-white" data-testid="close-book-dj">
          <X size={20} />
        </button>
        <span className="text-xs font-mono text-purple-400 uppercase tracking-widest">Gig Offer</span>
        <h3 className="text-2xl font-black mb-4 font-crazy-candy">Book {artist.name}</h3>
        {(artist.genres || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="book-dj-genres">
            {artist.genres.map(g => (
              <span key={g} className="text-[10px] font-mono px-2.5 py-1 rounded-full bg-purple-600/20 border border-purple-500/30 text-purple-300">{g}</span>
            ))}
          </div>
        )}
        {artist.spotify && (
          <a href={artist.spotify} target="_blank" rel="noreferrer" className="inline-block text-[10px] font-mono text-emerald-400 hover:text-emerald-300 hover:underline mb-4" data-testid="book-dj-spotify">
            ▶ Listen on Spotify before booking
          </a>
        )}
        <form onSubmit={submit} className="space-y-4">
          <input required placeholder="Date (e.g. Saturday 14 Sep, 10 PM – 2 AM)" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="dj-booking-date-input" />
          <input required type="number" min="1" step="1" placeholder="Fee in ₹" value={form.fee} onChange={e => setForm({ ...form, fee: e.target.value })} className={inputCls} data-testid="booking-fee-input" />
          <textarea rows={2} placeholder="Message (venue, set length, vibe...)" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className={inputCls} data-testid="booking-message-input" />
          <button type="submit" disabled={saving} className="w-full py-3.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-lg shadow-purple-600/30 transition" data-testid="send-booking-btn">
            {saving ? "Sending…" : "Send Gig Offer"}
          </button>
        </form>
      </div>
    </div>
  );
}

// Artist directory (main page) — genres live on artist profiles, not here
function ArtistDirectory({ onSelectArtist, crazyCls }) {
  const [artists, setArtists] = useState(null);
  useEffect(() => {
    apiFetch("/artists").then(r => r.ok ? r.json() : []).then(d => setArtists(Array.isArray(d) ? d : [])).catch(() => setArtists([]));
  }, []);
  if (!artists) return null;
  return (
    <div className="mb-14" data-testid="artist-directory">
      <div className="mb-6">
        <h2 className={`text-2xl md:text-3xl font-black tracking-tight ${crazyCls || "font-crazy"}`}>Artists &amp; DJs</h2>
        <span className="block mt-1 text-xs font-mono text-purple-400 tracking-widest uppercase">Find Your DJ · All India</span>
      </div>
      {artists.length === 0 ? (
        <p className="text-xs font-mono text-slate-500" data-testid="artists-empty">No DJs listed yet — artists, join and set up your profile!</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {artists.map(a => (
            <button key={a.id} onClick={() => onSelectArtist(a.id)} className="text-left bg-slate-900/70 border border-white/10 rounded-2xl p-4 hover:border-purple-500/50 transition" data-testid={`artist-card-${a.id}`}>
              <img src={a.avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"} alt={a.name} className="w-14 h-14 rounded-full object-cover border-2 border-purple-500/40 mb-3" />
              <p className="font-bold text-sm flex items-center gap-1.5">{a.name} {a.verified && <span className="text-cyan-400 text-[10px]" title="Verified">✓</span>}</p>
              <p className="text-[10px] font-mono text-slate-500">📍 {a.city}{a.mixes_count > 0 ? ` • 🎧 ${a.mixes_count} mix${a.mixes_count === 1 ? '' : 'es'}` : ""}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Public wall of the freshest club reviews across India (main page)
function LatestReviews({ onSelectClub, crazyCls }) {
  const [reviews, setReviews] = useState(null);
  useEffect(() => {
    apiFetch("/reviews/latest").then(r => r.ok ? r.json() : null).then(d => setReviews(Array.isArray(d) ? d : [])).catch(() => setReviews([]));
  }, []);
  if (!reviews || reviews.length === 0) return null;
  return (
    <div className="mb-14" data-testid="latest-reviews-wall">
      <div className="mb-6">
        <h2 className={`text-2xl md:text-3xl font-black tracking-tight ${crazyCls || "font-crazy"}`}>Latest Reviews</h2>
        <span className="block mt-1 text-xs font-mono text-amber-400 tracking-widest uppercase">Fresh From The Floor · All India</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {reviews.map(r => (
          <button key={r.id} onClick={() => onSelectClub(r.club_id)} className="text-left bg-slate-900/70 border border-white/10 rounded-2xl p-5 hover:border-amber-500/50 transition" data-testid={`wall-review-${r.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-amber-400 text-sm tracking-wider" data-testid={`wall-stars-${r.id}`}>{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
              <span className="text-[9px] font-mono text-slate-500">{new Date(r.created_at).toLocaleDateString()}</span>
            </div>
            {r.text && <p className="text-sm text-slate-200 mb-2">{r.text}</p>}
            {r.photo && <img src={r.photo} alt={`Review of ${r.club_name}`} className="mb-3 h-28 w-full rounded-xl object-cover" data-testid={`wall-photo-${r.id}`} />}
            <p className="text-[10px] font-mono text-slate-400 uppercase">
              <span className="text-white font-bold">{r.user_name}</span> · {r.club_name} · {r.club_city}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

// Booking requests inbox/outbox (profile tab)
function BookingRequests({ user }) {
  const [data, setData] = useState(null);

  const load = () => apiFetch("/bookings/mine").then(r => r.ok ? r.json() : null).then(d => d && setData(d)).catch(() => {});
  useEffect(() => { load(); }, []);

  const respond = async (id, accept) => {
    const res = await apiFetch(`/bookings/${id}/respond`, { method: "POST", body: JSON.stringify({ accept }) });
    if (res.ok) load();
  };

  const payAndHold = async (id) => {
    const res = await apiFetch("/payments/booking-checkout", { method: "POST", body: JSON.stringify({ booking_id: id, origin_url: window.location.origin }) });
    if (res.ok) {
      const d = await res.json();
      window.location.href = d.checkout_url;
    } else {
      const err = await res.json();
      alert(err.detail || "Could not start payment");
    }
  };

  const release = async (id) => {
    const res = await apiFetch(`/bookings/${id}/release`, { method: "POST" });
    if (res.ok) {
      load();
    } else {
      const err = await res.json();
      alert(err.detail || "Release failed");
    }
  };

  if (!data || (data.received.length === 0 && data.sent.length === 0)) return null;

  const badge = (s) => s === "accepted" ? "bg-emerald-600/20 text-emerald-300" : s === "funded" ? "bg-cyan-600/20 text-cyan-300" : s === "released" ? "bg-emerald-600/30 text-emerald-200" : s === "declined" ? "bg-red-600/20 text-red-300" : "bg-amber-600/20 text-amber-300";
  const badgeLabel = (s) => s === "funded" ? "💰 Funded · Held" : s === "released" ? "✅ Released" : s;

  return (
    <div className="mb-8" data-testid="booking-requests">
      <p className="text-xs font-mono text-purple-400 uppercase tracking-widest mb-4">🎧 Gig Offers</p>
      {data.received.length > 0 && (
        <div className="space-y-2 mb-4">
          {data.received.map(b => (
            <div key={b.id} className="bg-slate-800/60 border border-white/5 rounded-2xl p-4" data-testid={`booking-${b.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-sm font-bold">{b.owner_name}</p>
                <span className={`text-[9px] font-mono font-bold px-2 py-1 rounded-lg uppercase ${badge(b.status)}`}>{badgeLabel(b.status)}</span>
              </div>
              <p className="text-xs text-slate-400 font-mono">📅 {b.date} • 💰 ₹{b.fee.toLocaleString('en-IN')}</p>
              {b.message && <p className="text-xs text-slate-300 mt-1">{b.message}</p>}
              {b.status === "pending" && (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => respond(b.id, true)} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-mono font-bold py-2 rounded-xl transition" data-testid={`accept-booking-${b.id}`}>Accept</button>
                  <button onClick={() => respond(b.id, false)} className="flex-1 bg-slate-800 hover:bg-red-600/60 text-slate-300 text-[10px] font-mono font-bold py-2 rounded-xl transition" data-testid={`decline-booking-${b.id}`}>Decline</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {data.sent.length > 0 && (
        <div className="space-y-2">
          {data.sent.map(b => (
            <div key={b.id} className="bg-slate-800/40 border border-white/5 rounded-2xl p-4" data-testid={`sent-booking-${b.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-bold">Offer sent • 📅 {b.date} • ₹{b.fee.toLocaleString('en-IN')}</p>
                </div>
                <span className={`text-[9px] font-mono font-bold px-2 py-1 rounded-lg uppercase ${badge(b.status)}`}>{badgeLabel(b.status)}</span>
              </div>
              {b.status === "accepted" && (
                <button onClick={() => payAndHold(b.id)} className="mt-3 w-full bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] font-mono font-bold py-2.5 rounded-xl transition shadow-lg shadow-cyan-600/20" data-testid={`pay-hold-booking-${b.id}`}>
                  💳 Pay &amp; Hold ₹{b.fee.toLocaleString('en-IN')} (escrow)
                </button>
              )}
              {b.status === "funded" && (
                <button onClick={() => release(b.id)} className="mt-3 w-full bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-mono font-bold py-2.5 rounded-xl transition shadow-lg shadow-emerald-600/20" data-testid={`release-booking-${b.id}`}>
                  ✅ Release Payment to {b.artist_name || "Artist"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Live countdown to party start
function Countdown({ startsAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const target = new Date(startsAt).getTime();
  if (isNaN(target)) return null;
  const diff = target - now;
  if (diff <= 0) {
    return <span className="text-[10px] font-mono font-bold text-red-300 bg-red-600/20 px-2 py-1 rounded-lg animate-pulse" data-testid="countdown-live">🔴 Live now</span>;
  }
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const label = d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m ${s}s`;
  return <span className="text-[10px] font-mono font-bold text-emerald-300 bg-emerald-600/20 px-2 py-1 rounded-lg" data-testid="countdown-timer">⏳ {label}</span>;
}

// Link a phone number to an existing account (enables OTP login) — profile tab
function LinkPhone({ user, onSaved }) {
  const [ph, setPh] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [dev, setDev] = useState(null);
  const [busy, setBusy] = useState(false);

  if (user.phone) {
    return (
      <div className="mb-8 flex items-center gap-2 text-xs font-mono text-emerald-300 bg-emerald-600/10 border border-emerald-500/20 rounded-2xl px-4 py-3" data-testid="linked-phone">
        📱 <span className="font-bold">{user.phone}</span> <span className="text-emerald-500">✓ linked — OTP login enabled</span>
      </div>
    );
  }

  const send = async () => {
    if (!ph.trim()) { alert("Enter your phone number"); return; }
    setBusy(true);
    try {
      const res = await apiFetch("/auth/otp/send", { method: "POST", body: JSON.stringify({ phone: ph, purpose: "register" }) });
      const d = await res.json();
      if (!res.ok) { alert(d.detail || "Could not send OTP"); return; }
      setDev(d.dev_otp || null);
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const res = await apiFetch("/users/me/link-phone", { method: "POST", body: JSON.stringify({ phone: ph, otp: code }) });
      if (res.ok) {
        onSaved && onSaved(await res.json());
      } else {
        const err = await res.json();
        alert(err.detail || "Verification failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-8 bg-slate-800/40 border border-white/10 rounded-2xl p-4" data-testid="link-phone">
      <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-1">📱 Link Your Phone</p>
      <p className="text-[10px] font-mono text-slate-500 mb-3">Add your number to enable OTP login and password reset.</p>
      {dev && <p className="text-[10px] font-mono text-amber-300 mb-2" data-testid="link-dev-otp">DEV OTP: <span className="font-black tracking-[0.3em]">{dev}</span></p>}
      <div className="flex gap-2">
        <input type="tel" value={ph} onChange={(e) => setPh(e.target.value)} placeholder="98765 43210" disabled={sent} className="flex-1 bg-slate-800 border border-white/10 rounded-xl px-4 py-2.5 text-xs font-mono text-white focus:outline-none focus:border-cyan-500 disabled:opacity-60" data-testid="link-phone-input" />
        {!sent ? (
          <button onClick={send} disabled={busy} className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-[10px] font-mono font-bold px-4 rounded-xl transition" data-testid="link-phone-send">Send OTP</button>
        ) : (
          <>
            <input type="text" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="OTP" className="w-24 bg-slate-800 border border-white/10 rounded-xl px-3 py-2.5 text-xs font-mono text-white text-center focus:outline-none focus:border-cyan-500" data-testid="link-phone-otp-input" />
            <button onClick={verify} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-[10px] font-mono font-bold px-4 rounded-xl transition" data-testid="link-phone-verify">Verify</button>
          </>
        )}
      </div>
    </div>
  );
}

// Artist's genre picker (profile tab) — multi-select chips from the canonical list
function ArtistGenres({ user, onSaved }) {
  const [genres, setGenres] = useState(user.genres || []);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify([...genres].sort()) !== JSON.stringify([...(user.genres || [])].sort());

  const toggle = (g) => setGenres(gs => gs.includes(g) ? gs.filter(x => x !== g) : [...gs, g]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiFetch("/users/me", { method: "PATCH", body: JSON.stringify({ genres }) });
      if (res.ok) {
        const u = await res.json();
        onSaved && onSaved(u);
      } else {
        const err = await res.json();
        alert(err.detail || "Failed to save genres");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-8" data-testid="artist-genres">
      <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-1">🎛️ Genres I Play</p>
      <p className="text-[10px] font-mono text-slate-500 mb-3">Tap to select — club owners see these before booking you.</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {DJ_GENRES.map(g => {
          const active = genres.includes(g);
          return (
            <button
              key={g}
              type="button"
              onClick={() => toggle(g)}
              className={`text-[11px] font-mono px-3 py-1.5 rounded-full border transition ${active ? 'bg-purple-600 border-purple-500 text-white shadow-lg shadow-purple-600/30' : 'bg-slate-900 border-white/10 text-slate-300 hover:border-purple-500/50'}`}
              data-testid={`genre-chip-${g.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {g}
            </button>
          );
        })}
      </div>
      {dirty && (
        <button onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-[10px] font-mono font-bold px-5 py-2.5 rounded-xl transition" data-testid="save-genres-btn">
          {saving ? "Saving…" : `Save Genres (${genres.length})`}
        </button>
      )}
    </div>
  );
}

// Artist's Spotify link (profile tab)
function ArtistSpotify({ user, onSaved }) {
  const [url, setUrl] = useState(user.spotify || "");
  const [saving, setSaving] = useState(false);
  const dirty = url.trim() !== (user.spotify || "");

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiFetch("/users/me", { method: "PATCH", body: JSON.stringify({ spotify: url.trim() }) });
      if (res.ok) {
        onSaved && onSaved(await res.json());
      } else {
        const err = await res.json();
        alert(err.detail || "Failed to save Spotify link");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-8" data-testid="artist-spotify">
      <p className="text-xs font-mono text-emerald-400 uppercase tracking-widest mb-1">🎵 Spotify</p>
      <p className="text-[10px] font-mono text-slate-500 mb-3">Link your Spotify artist page — fans and club owners can listen before booking you.</p>
      <div className="flex gap-2">
        <input
          type="url"
          placeholder="https://open.spotify.com/artist/..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="flex-1 bg-slate-800 border border-white/10 rounded-xl px-4 py-2.5 text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
          data-testid="spotify-url-input"
        />
        {dirty && (
          <button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-[10px] font-mono font-bold px-5 rounded-xl transition" data-testid="save-spotify-btn">
            {saving ? "Saving…" : "Save"}
          </button>
        )}
      </div>
    </div>
  );
}

// Artist's own mixes manager (profile tab)
function ArtistMixes({ user }) {
  const [mixes, setMixes] = useState(user.mixes || []);
  const [url, setUrl] = useState("");

  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    const res = await apiFetch("/users/me/mixes", { method: "POST", body: JSON.stringify({ url: url.trim() }) });
    if (res.ok) {
      const d = await res.json();
      setMixes(d.mixes || []);
      setUrl("");
    } else {
      const err = await res.json();
      alert(err.detail || "Failed to add mix");
    }
  };

  return (
    <div className="mb-8" data-testid="artist-mixes">
      <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-4">🎧 My Mixes</p>
      <form onSubmit={add} className="flex gap-2 mb-3">
        <input
          type="url"
          required
          placeholder="Paste a SoundCloud / YouTube mix link..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="flex-1 bg-slate-800 border border-white/10 rounded-xl px-4 py-2.5 text-xs font-mono text-white focus:outline-none focus:border-rose-500"
          data-testid="mix-url-input"
        />
        <button type="submit" className="bg-rose-600 hover:bg-rose-500 text-white text-xs font-mono font-bold px-4 py-2.5 rounded-xl transition" data-testid="add-mix-btn">
          Add
        </button>
      </form>
      {mixes.length > 0 && (
        <div className="space-y-2">
          {mixes.map((m, i) => (
            <a key={i} href={m} target="_blank" rel="noreferrer" className="block bg-slate-800/60 border border-white/5 rounded-2xl p-3 text-xs font-mono text-cyan-300 hover:border-cyan-500/50 truncate transition" data-testid={`my-mix-${i}`}>{m}</a>
          ))}
        </div>
      )}
    </div>
  );
}

// My tickets with door codes (profile tab)
function MyTickets() {
  const [tickets, setTickets] = useState(null);
  useEffect(() => {
    apiFetch("/tickets/mine").then(r => r.ok ? r.json() : []).then(d => setTickets(Array.isArray(d) ? d : [])).catch(() => setTickets([]));
  }, []);
  if (!tickets || tickets.length === 0) return null;

  const refund = async (id) => {
    if (!confirm("Cancel this ticket? Your spot opens up for the waitlist and a refund is recorded.")) return;
    const res = await apiFetch(`/tickets/${id}/refund`, { method: "POST" });
    if (res.ok) {
      setTickets(ts => ts.map(t => t.id === id ? { ...t, status: "refunded" } : t));
    } else {
      const err = await res.json();
      alert(err.detail || "Refund failed");
    }
  };

  return (
    <div className="mb-8" data-testid="my-tickets">
      <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-4">My Tickets</p>
      <div className="space-y-3">
        {tickets.map(t => (
          <div key={t.id} className="bg-slate-800/60 border border-white/10 rounded-2xl p-4 flex items-center justify-between gap-4" data-testid={`ticket-${t.id}`}>
            {t.event && ((t.event.photos && t.event.photos[0]) || t.event.photo) && (
              <img src={(t.event.photos && t.event.photos[0]) || t.event.photo} alt={`${t.event.name} poster`} className="w-16 h-16 rounded-xl object-cover shrink-0" data-testid={`ticket-photo-${t.id}`} />
            )}
            <div className="min-w-0">
              <p className="text-sm font-bold truncate">{t.event ? t.event.name : "Party"}</p>
              <p className="text-[10px] font-mono text-slate-400">{t.event ? `📅 ${t.event.date} • 📍 ${t.event.venue}, ${t.event.city}` : ""}</p>
              <p className="text-[10px] font-mono text-rose-400 mt-1">₹{t.amount} paid</p>
            </div>
            <div className="text-center shrink-0 bg-slate-950 border border-dashed border-cyan-500/40 rounded-xl px-4 py-3">
              {t.status === "refunded" ? (
                <span className="text-[10px] font-mono font-bold text-slate-500" data-testid={`ticket-refunded-${t.id}`}>REFUNDED</span>
              ) : (
                <>
                  <p className="text-[9px] font-mono text-slate-500 uppercase">Door Code</p>
                  <p className="text-sm font-mono font-bold text-cyan-300 tracking-widest" data-testid={`ticket-code-${t.id}`}>{t.code || "—"}</p>
                  <button onClick={() => refund(t.id)} className="block mx-auto text-[9px] font-mono text-rose-400 hover:text-rose-300 underline mt-1" data-testid={`refund-btn-${t.id}`}>Cancel & Refund</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Host earnings from ticket sales (profile tab)
function HostEarnings() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    apiFetch("/payouts/mine").then(r => r.ok ? r.json() : []).then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([]));
  }, []);
  if (!rows || rows.length === 0) return null;
  const total = rows.reduce((s, r) => s + r.gross, 0);

  return (
    <div className="mb-8" data-testid="host-earnings">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest">Ticket Earnings</p>
        <span className="text-sm font-mono font-bold text-emerald-400" data-testid="earnings-total">₹{total.toLocaleString('en-IN')} total</span>
      </div>
      <div className="space-y-2">
        {rows.map(r => (
          <div key={r.event_id} className="bg-slate-800/60 border border-white/5 rounded-2xl p-4 flex items-center justify-between gap-3" data-testid={`earning-${r.event_id}`}>
            <div className="min-w-0">
              <p className="text-sm font-bold truncate">{r.name}</p>
              <p className="text-[10px] font-mono text-slate-500">{r.date} • {r.venue}, {r.city}</p>
            </div>
            <div className="text-right shrink-0 font-mono">
              <p className="text-xs text-slate-400">{r.tickets_sold} sold{r.ticket_price ? ` × ₹${r.ticket_price}` : ""}</p>
              <p className="text-sm font-bold text-emerald-400">₹{r.gross.toLocaleString('en-IN')}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Notifications feed (profile tab): likes + party reminders
function NotificationsFeed() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    apiFetch("/notifications/list").then(r => r.ok ? r.json() : []).then(d => setItems(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  if (items.length === 0) return null;

  return (
    <div className="mb-8" data-testid="notifications-feed">
      <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-4">Notifications</p>
      <div className="space-y-2">
        {items.map(n => (
          <div key={n.id} className="bg-slate-800/60 border border-white/5 rounded-2xl p-3.5 text-xs" data-testid={`notif-${n.id}`}>
            {n.type === 'like' ? `❤️ ${n.from_name} liked your short` : `🔔 ${n.text}`}
            <span className="block text-[9px] font-mono text-slate-500 mt-1">{new Date(n.created_at).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Host analytics: views, RSVPs, check-ins, ticket sales per party
function HostAnalytics() {
  const [rows, setRows] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  useEffect(() => {
    apiFetch("/analytics/host").then(r => r.ok ? r.json() : []).then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([]));
  }, []);
  if (!rows || rows.length === 0) return null;

  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const res = await apiFetch("/digest/weekly", { method: "POST" });
      if (res.ok) {
        const d = await res.json();
        if (d.sent) {
          alert("Weekly digest emailed to you!");
        } else {
          const total = d.summary.reduce((s, r) => s + r.gross, 0);
          const lines = d.summary.map(r => `${r.name}: ${r.views} views, ${r.going} going, ₹${r.gross}`).join("\n");
          alert(`Email isn't configured yet — here's your digest:\n\n${lines}\n\nTotal earned: ₹${total}`);
        }
      }
    } finally {
      setDigestBusy(false);
    }
  };

  return (
    <div className="mb-8" data-testid="host-analytics">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-mono text-cyan-400 uppercase tracking-widest">Party Analytics</p>
        <button onClick={sendDigest} disabled={digestBusy} className="text-[10px] font-mono font-bold px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-emerald-600 text-emerald-300 hover:text-white disabled:opacity-50 transition" data-testid="weekly-digest-btn">
          {digestBusy ? "Sending…" : "📊 Email Weekly Digest"}
        </button>
      </div>
      <div className="space-y-3">
        {rows.map(r => (
          <div key={r.event_id} className="bg-slate-800/60 border border-white/5 rounded-2xl p-4" data-testid={`analytics-${r.event_id}`}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="text-sm font-bold truncate">{r.name}</p>
              <p className="text-[10px] font-mono text-slate-500 shrink-0">{r.date} • {r.city}</p>
            </div>
            <div className="grid grid-cols-5 gap-2 text-center font-mono">
              <div><p className="text-sm font-bold text-slate-200">{r.views}</p><p className="text-[9px] text-slate-500 uppercase">Views</p></div>
              <div><p className="text-sm font-bold text-cyan-400">{r.going}</p><p className="text-[9px] text-slate-500 uppercase">Going</p></div>
              <div><p className="text-sm font-bold text-emerald-400">{r.entered}</p><p className="text-[9px] text-slate-500 uppercase">Entered</p></div>
              <div><p className="text-sm font-bold text-rose-400">{r.tickets_sold}</p><p className="text-[9px] text-slate-500 uppercase">Sold</p></div>
              <div><p className="text-sm font-bold text-emerald-300">₹{r.gross.toLocaleString('en-IN')}</p><p className="text-[9px] text-slate-500 uppercase">Earned</p></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
